import * as i0 from '@angular/core';
import { ViewContainerRef, Component, ViewChild, Input, NgModule, EventEmitter, Output, HostBinding, ViewChildren, ChangeDetectionStrategy, InjectionToken, Inject } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BehaviorSubject, Subject, merge, fromEventPattern, empty, combineLatest } from 'rxjs';
import { takeUntil, filter, debounceTime, take, switchMap, distinctUntilChanged, map } from 'rxjs/operators';
import { watchResize } from '@slidebean/hydra/resize-observer';
import * as i2$1 from '@slidebean/ui/text';
import { TextModule } from '@slidebean/ui/text';
import * as i3 from '@slidebean/ui/image';
import { ImageModule } from '@slidebean/ui/image';
import * as i1$5 from '@slidebean/pusher';
import { PusherModule } from '@slidebean/pusher';
import * as i3$1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import 'ric';
import { newId } from '@slidebean/sdk';
import isEqual from 'fast-deep-equal';
import { isElementDataEmpty, getElementSampleData, getSlideOptionValue, getElementOptionValue } from '@slidebean/sdk/presentation';
import { ColorSet, Color } from '@slidebean/color';
import * as i2 from '@slidebean/noun-project-icon';
import { NounProjectIconModule } from '@slidebean/noun-project-icon';
import * as i1$1 from '@slidebean/chart';
import { ChartComponent, ChartModule } from '@slidebean/chart';
import Prism from 'prismjs';
import * as i1$2 from '@angular/platform-browser';
import * as i1$3 from '@slidebean/video';
import { VideoPlayerComponent, VideoModule } from '@slidebean/video';
import * as i1$4 from '@slidebean/font-loader';
import screenfull from 'screenfull';
import * as i4 from '@slidebean/kappa';
import { TooltipModule } from '@slidebean/kappa';
import 'hammerjs';
import 'hammer-timejs';
import cssx from 'cssx';
import * as i1$6 from '@slidebean/slide-elements/plugins/render';

class HostComponent {
    constructor(resolver, changeDetector) {
        this.resolver = resolver;
        this.changeDetector = changeDetector;
    }
    ngOnInit() {
        this.reload();
    }
    ngOnChanges(changes) {
        if (this.componentRef) {
            this.wireHostedComponent(this.componentRef);
            if (typeof this.componentRef.instance['ngOnChanges'] === 'function') {
                this.componentRef.instance.ngOnChanges(changes);
            }
        }
    }
    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }
    reload() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
        this.host.clear();
        const hostedComponentType = this.getHostedComponentType();
        if (hostedComponentType) {
            const hostedComponent = this.resolver.resolveComponentFactory(hostedComponentType);
            this.componentRef = this.host.createComponent(hostedComponent);
            this.wireHostedComponent(this.componentRef);
        }
        this.changeDetector.detectChanges();
    }
    getHostedComponentType() {
        return null;
    }
    /* eslint-disable */
    wireHostedComponent(componentRef) { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: HostComponent, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: HostComponent, selector: "sb-host", viewQueries: [{ propertyName: "host", first: true, predicate: ["host"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: '<ng-container #host></ng-container>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: HostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-host',
                    template: '<ng-container #host></ng-container>',
                }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.ChangeDetectorRef }], propDecorators: { host: [{
                type: ViewChild,
                args: ['host', { static: true, read: ViewContainerRef }]
            }] } });

function findScrollContainer(element) {
    if (!element) {
        return null;
    }
    const style = window.getComputedStyle(element);
    if (style.overflow === 'auto' || style.overflowY === 'auto') {
        return element;
    }
    return findScrollContainer(element.parentElement);
}

class LazyLoadComponent {
    constructor(zone, changeDetector, element) {
        this.zone = zone;
        this.changeDetector = changeDetector;
        this.element = element;
        this.threshold = 50; // px
        this.enabled = true;
        this.isVisible$ = new BehaviorSubject(false);
        this.destroy$ = new Subject();
    }
    ngOnInit() {
        if (!this.enabled) {
            this.isVisible$.next(true);
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    ngAfterViewInit() {
        if (!this.enabled) {
            return;
        }
        this.zone.runOutsideAngular(() => {
            const container = findScrollContainer(this.element.nativeElement);
            if (container) {
                if (!this.turnVisibleIfInsideViewport(container)) {
                    window.requestIdleCallback(() => this.turnVisibleIfInsideViewport(container));
                    merge(fromEventPattern(handler => container.addEventListener('scroll', handler), handler => container.removeEventListener('scroll', handler)), watchResize(container))
                        .pipe(takeUntil(this.isVisible$.pipe(filter(v => !!v))), takeUntil(this.destroy$))
                        .subscribe(() => this.turnVisibleIfInsideViewport(container));
                }
            }
            else {
                this.turnVisible();
            }
        });
    }
    turnVisible() {
        this.zone.run(() => {
            this.isVisible$.next(true);
            this.changeDetector.detectChanges();
        });
    }
    turnVisibleIfInsideViewport(container) {
        const isVisible = this.isVisibleWithinContainer(container);
        if (isVisible) {
            this.turnVisible();
        }
        return isVisible;
    }
    isVisibleWithinContainer(container) {
        const rect = this.element.nativeElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        return !(rect.bottom + this.threshold < containerRect.top ||
            containerRect.bottom < rect.top - this.threshold ||
            rect.right + this.threshold < containerRect.left ||
            containerRect.right < rect.left - this.threshold);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: LazyLoadComponent, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: LazyLoadComponent, selector: "sb-lazy-load", inputs: { threshold: "threshold", enabled: "enabled" }, ngImport: i0, template: `
    <ng-container *ngIf="isVisible$ | async">
      <ng-content></ng-content>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: LazyLoadComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-lazy-load',
                    template: `
    <ng-container *ngIf="isVisible$ | async">
      <ng-content></ng-content>
    </ng-container>
  `,
                }]
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }], propDecorators: { threshold: [{
                type: Input
            }], enabled: [{
                type: Input
            }] } });

const DIRECTIVES = [HostComponent, LazyLoadComponent];
class RenderCommonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.11", ngImport: i0, type: RenderCommonModule, declarations: [HostComponent, LazyLoadComponent], imports: [CommonModule], exports: [HostComponent, LazyLoadComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderCommonModule, providers: [provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES,
                    exports: [...DIRECTIVES],
                    imports: [CommonModule],
                    providers: [provideHttpClient(withInterceptorsFromDi())],
                }]
        }] });

function didChange(change) {
    return (change &&
        !change.isFirstChange() &&
        change.previousValue !== change.currentValue);
}

const isBodyElement = (slideElement) => !/cover|heading|background|footer|video/.test(slideElement.type);
const hasBackgroundImage = (slide) => slide.elements.some(el => el.type === 'background' && !!el.data.image);
const hasBackgroundIcon = (slide) => slide.elements.some(el => el.type === 'background' && !!el.data.icon);
const hasVideo = (slide) => slide.elements.some(el => el.type === 'video');

class SlideElementComponent {
    constructor() {
        this.isActive = false;
        this.hiRes = false;
        this.editMode = false;
        this.onActivationEnded = new EventEmitter();
        this.reloadStyles$ = new Subject();
        this.destroy$ = new Subject();
        this.textSize$ = new BehaviorSubject(1);
        this.textAlign$ = new BehaviorSubject('');
    }
    get elementId() {
        return this.element ? this.element.id : void 0;
    }
    get data() {
        if (this.element) {
            return this.editMode && isElementDataEmpty(this.element)
                ? getElementSampleData(this.element.type)
                : this.element.data;
        }
        return {};
    }
    ngOnChanges(changes) {
        if (didChange(changes['slideElements']) ||
            didChange(changes['slideOptions']) ||
            didChange(changes['element']) ||
            didChange(changes['elementData']) ||
            didChange(changes['elementOptions']) ||
            didChange(changes['colorSet']) ||
            didChange(changes['headingFont'])) {
            this.reloadStyles$.next();
        }
        if (didChange(changes['isActive'])) {
            if (this.isActive) {
                this.activate();
            }
            else {
                this.deactivate();
            }
        }
    }
    ngOnInit() {
        this.reloadStyles$
            .pipe(debounceTime(10))
            .subscribe(() => this.reloadStyles());
        // Trigger initial load
        this.reloadStyles();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    /* eslint-disable */
    activate() { }
    deactivate() { }
    /* eslint-enable */
    reloadStyles() {
        this.reloadFontSize();
        this.reloadTextAlign();
    }
    reloadFontSize() {
        let size = 1;
        size += getSlideOptionValue(this.slide)('textSize') / 5;
        size += getElementOptionValue(this.element)('textSize') / 5;
        this.textSize$.next(size);
    }
    reloadTextAlign() {
        const textAlign = getElementOptionValue(this.element)('textAlign');
        if (/left|center|right/.test(textAlign)) {
            this.textAlign$.next(`text-${textAlign}`);
        }
        else {
            this.textAlign$.next('');
        }
    }
    getSlideOptionValue(option) {
        return getSlideOptionValue(this.slide)(option);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideElementComponent, selector: "sb-slide-element", inputs: { slide: "slide", slideElements: "slideElements", slideOptions: "slideOptions", element: "element", elementData: "elementData", elementOptions: "elementOptions", colorSet: "colorSet", textFont: "textFont", headingFont: "headingFont", isActive: "isActive", hiRes: "hiRes", editMode: "editMode" }, outputs: { onActivationEnded: "onActivationEnded" }, host: { properties: { "attr.data-sb-slide-element-id": "this.elementId" } }, usesOnChanges: true, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-slide-element',
                    template: '',
                }]
        }], propDecorators: { slide: [{
                type: Input
            }], slideElements: [{
                type: Input
            }], slideOptions: [{
                type: Input
            }], element: [{
                type: Input
            }], elementData: [{
                type: Input
            }], elementOptions: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], isActive: [{
                type: Input
            }], hiRes: [{
                type: Input
            }], editMode: [{
                type: Input
            }], onActivationEnded: [{
                type: Output
            }], elementId: [{
                type: HostBinding,
                args: ['attr.data-sb-slide-element-id']
            }] } });

class BaseBackgroundComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.backgroundIconStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const textColor = set.textColor;
        this.backgroundIconStyles$.next({
            'color': textColor,
            'stroke': textColor,
            'fill': textColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseBackgroundComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseBackgroundComponent, selector: "sb-base-background", usesInheritance: true, ngImport: i0, template: "<div\n  *ngIf=\"data.image\"\n  class=\"background-image\"\n  [class.background-size-contain]=\"!getSlideOptionValue('cropBackground')\"\n  [style.background-image]=\"'url(' + data.image.url + ')'\"\n  [sbPreloadImage]=\"data.image.url\"\n></div>\n\n<div\n  *ngIf=\"data.image\"\n  class=\"background-image-fader\"\n  [style.opacity]=\"getSlideOptionValue('backgroundDim') || 0\"\n  data-slide-decoration\n></div>\n\n<div *ngIf=\"data.icon\" class=\"background-icon\">\n  <i\n    *ngIf=\"data.icon.source === 'FontAwesome'\"\n    class=\"fa {{ data.icon.id }}\"\n  ></i>\n  <sb-noun-project-icon\n    *ngIf=\"data.icon.source === 'NounProject'\"\n    [iconId]=\"data.icon.id\"\n    [iconUrl]=\"data.icon.url\"\n    [color]=\"(backgroundIconStyles$ | async)['color']\"\n    [ngStyle]=\"backgroundIconStyles$ | async\"\n  ></sb-noun-project-icon>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseBackgroundComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-background', template: "<div\n  *ngIf=\"data.image\"\n  class=\"background-image\"\n  [class.background-size-contain]=\"!getSlideOptionValue('cropBackground')\"\n  [style.background-image]=\"'url(' + data.image.url + ')'\"\n  [sbPreloadImage]=\"data.image.url\"\n></div>\n\n<div\n  *ngIf=\"data.image\"\n  class=\"background-image-fader\"\n  [style.opacity]=\"getSlideOptionValue('backgroundDim') || 0\"\n  data-slide-decoration\n></div>\n\n<div *ngIf=\"data.icon\" class=\"background-icon\">\n  <i\n    *ngIf=\"data.icon.source === 'FontAwesome'\"\n    class=\"fa {{ data.icon.id }}\"\n  ></i>\n  <sb-noun-project-icon\n    *ngIf=\"data.icon.source === 'NounProject'\"\n    [iconId]=\"data.icon.id\"\n    [iconUrl]=\"data.icon.url\"\n    [color]=\"(backgroundIconStyles$ | async)['color']\"\n    [ngStyle]=\"backgroundIconStyles$ | async\"\n  ></sb-noun-project-icon>\n</div>\n" }]
        }] });

class BaseCoverComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.titleStyles$ = new BehaviorSubject({});
        this.subtitleStyles$ = new BehaviorSubject({});
        this.authorStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        this.titleStyles$.next({
            'font-family': this.headingFont ? this.headingFont.fontFamily : null,
        });
        this.subtitleStyles$.next({});
        this.authorStyles$.next({});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseCoverComponent, selector: "sb-base-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class BaseHeadingComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.headingStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        this.headingStyles$.next({
            'font-family': this.headingFont ? this.headingFont.fontFamily : null,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseHeadingComponent, selector: "sb-base-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n" }]
        }] });

class BaseParagraphComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.paragraphContainerStyles$ = new BehaviorSubject({});
    }
    ngOnInit() {
        super.ngOnInit();
        if (this.element) {
            if (Array.isArray(this.data.bullets)) {
                this.data.paragraph = this.convertBulletsToHTML(this.data.bullets, this.data.mode === '123' || this.data.mode === 'abc');
            }
        }
    }
    convertBulletsToHTML(bullets, ordered) {
        let str = '';
        bullets.forEach(bullet => {
            if (Array.isArray(bullet)) {
                str += this.convertBulletsToHTML(bullet, ordered);
            }
            else {
                str += `<li>${bullet}</li>`;
            }
        });
        const wrapperTag = ordered ? 'ol' : 'ul';
        return `<${wrapperTag}>${str}</${wrapperTag}>`;
    }
    reloadStyles() {
        super.reloadStyles();
        this.paragraphContainerStyles$.next({});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseParagraphComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseParagraphComponent, selector: "sb-base-paragraph", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <div\n      class=\"paragraph-container\"\n      [ngStyle]=\"paragraphContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"data.paragraph | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseParagraphComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-paragraph', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <div\n      class=\"paragraph-container\"\n      [ngStyle]=\"paragraphContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"data.paragraph | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class BaseBulletsComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.bulletsHtml$ = new BehaviorSubject('');
        this.bulletIndicatorStyles$ = new BehaviorSubject({});
        this.bulletsContainerStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        this.reloadBulletsContainerStyles();
        this.reloadBulletIndicatorStyles();
        this.reloadBullets();
    }
    reloadBulletsContainerStyles() {
        this.bulletsContainerStyles$.next({});
    }
    reloadBulletIndicatorStyles() {
        const set = new ColorSet(this.colorSet);
        const color = ColorSet.getMostReadableColor(set.backColor)([
            this.colorSet['back2'],
            set.highlightColor,
        ]);
        this.bulletIndicatorStyles$.next({ color });
    }
    reloadBullets() {
        const mainList = this.createList(this.data.bullets);
        this.bulletsHtml$.next(mainList);
    }
    createList(bullets) {
        const ul = $('<ul></ul>');
        let itemIndex = 0;
        if (bullets && bullets.length) {
            bullets.forEach(bullet => {
                if (Array.isArray(bullet)) {
                    ul.append(this.createList(bullet));
                }
                else if (typeof bullet === 'string') {
                    ul.append(this.createListItem(bullet, itemIndex++));
                }
            });
        }
        return ul.prop('ourterHTML');
    }
    createListItem(bullet, index) {
        const li = $('<li></li>');
        const indicator = $('<span class="bullet-indicator"></span>');
        indicator.append(this.createBulletIndicator(index));
        indicator.css(this.bulletIndicatorStyles$.getValue());
        li.append(indicator);
        li.append($('<span></span>').html(bullet));
        return li;
    }
    createBulletIndicator(index) {
        if (this.data.mode === '123') {
            return this.createNumberedBulletIndicator(index);
        }
        if (this.data.mode === 'abc') {
            return this.createLetteredBulletIndicator(index);
        }
        return this.createFigureBulletIndicator(index);
    }
    createNumberedBulletIndicator(index) {
        return $(`<span><strong>${index + 1}.</strong></span>`);
    }
    createLetteredBulletIndicator(index) {
        return $(`<span><strong>${String.fromCharCode(index + 'a'.charCodeAt(0))}.</strong></span>`);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<i class="fas fa-circle"></i>');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseBulletsComponent, selector: "sb-base-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class BaseChartComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.chartSizeRatio$ = new BehaviorSubject(0);
    }
    reloadStyles() {
        super.reloadStyles();
        this.reloadChartSizeRatio();
    }
    reloadChartSizeRatio() {
        const CHART_MAX_SIZE = 4;
        const CHART_MIN_SIZE = -4;
        let size = getSlideOptionValue(this.slide)('chartSize') || 0;
        size += getElementOptionValue(this.element)('size') || 0;
        size = Math.max(CHART_MIN_SIZE, Math.min(CHART_MAX_SIZE, size));
        // Convert to [0.0 - 1.0]
        size = (size - CHART_MIN_SIZE) / (CHART_MAX_SIZE - CHART_MIN_SIZE);
        this.chartSizeRatio$.next(size);
    }
    activate() {
        if (this.chart) {
            this.chart.render(true);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseChartComponent, selector: "sb-base-chart", viewQueries: [{ propertyName: "chart", first: true, predicate: ChartComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-base-chart',
                    template: '',
                }]
        }], propDecorators: { chart: [{
                type: ViewChild,
                args: [ChartComponent, { static: false }]
            }] } });

class BaseBarChartComponent extends BaseChartComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseBarChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseBarChartComponent, selector: "sb-base-bar-chart", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <sb-chart\n      [type]=\"data.mode === 'horizontal' ? 'bar' : 'column'\"\n      [stacked]=\"!!data.stacked\"\n      [data]=\"elementData\"\n      [legendPosition]=\"data.legendPosition\"\n      [scaleMin]=\"data.scaleMin\"\n      [scaleMax]=\"data.scaleMax\"\n      [scaleStep]=\"data.scaleStep\"\n      [displayGridLines]=\"data.displayGridLines\"\n      [displayDataLabels]=\"!!data.displayValues\"\n      [colorSet]=\"colorSet\"\n      [font]=\"textFont\"\n      [sizeRatio]=\"chartSizeRatio$ | async\"\n    ></sb-chart>\n  </div>\n</span>\n", dependencies: [{ kind: "component", type: i1$1.ChartComponent, selector: "sb-chart", inputs: ["type", "data", "colorSet", "font", "stacked", "widthToHeightRatio", "sizeRatio", "legendPosition", "displayDataLabels", "scaleMin", "scaleMax", "scaleStep", "displayGridLines", "displayTooltips", "tooltipFormatter", "allowPointerEvents", "allowDecimalsXAxis", "allowDecimalsYAxis", "pieSize", "pieInnerSize", "pieDisplayValuesInDataLabels"], outputs: ["onSeriesMouseOver"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseBarChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-bar-chart', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <sb-chart\n      [type]=\"data.mode === 'horizontal' ? 'bar' : 'column'\"\n      [stacked]=\"!!data.stacked\"\n      [data]=\"elementData\"\n      [legendPosition]=\"data.legendPosition\"\n      [scaleMin]=\"data.scaleMin\"\n      [scaleMax]=\"data.scaleMax\"\n      [scaleStep]=\"data.scaleStep\"\n      [displayGridLines]=\"data.displayGridLines\"\n      [displayDataLabels]=\"!!data.displayValues\"\n      [colorSet]=\"colorSet\"\n      [font]=\"textFont\"\n      [sizeRatio]=\"chartSizeRatio$ | async\"\n    ></sb-chart>\n  </div>\n</span>\n" }]
        }] });

class BaseLineChartComponent extends BaseChartComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseLineChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseLineChartComponent, selector: "sb-base-line-chart", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <sb-chart\n      [type]=\"data.mode\"\n      [stacked]=\"!!data.stacked\"\n      [data]=\"elementData\"\n      [legendPosition]=\"data.legendPosition\"\n      [scaleMin]=\"data.scaleMin\"\n      [scaleMax]=\"data.scaleMax\"\n      [scaleStep]=\"data.scaleStep\"\n      [displayGridLines]=\"data.displayGridLines\"\n      [displayDataLabels]=\"!!data.displayValues\"\n      [colorSet]=\"colorSet\"\n      [font]=\"textFont\"\n      [sizeRatio]=\"chartSizeRatio$ | async\"\n    ></sb-chart>\n  </div>\n</span>\n", dependencies: [{ kind: "component", type: i1$1.ChartComponent, selector: "sb-chart", inputs: ["type", "data", "colorSet", "font", "stacked", "widthToHeightRatio", "sizeRatio", "legendPosition", "displayDataLabels", "scaleMin", "scaleMax", "scaleStep", "displayGridLines", "displayTooltips", "tooltipFormatter", "allowPointerEvents", "allowDecimalsXAxis", "allowDecimalsYAxis", "pieSize", "pieInnerSize", "pieDisplayValuesInDataLabels"], outputs: ["onSeriesMouseOver"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseLineChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-line-chart', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <sb-chart\n      [type]=\"data.mode\"\n      [stacked]=\"!!data.stacked\"\n      [data]=\"elementData\"\n      [legendPosition]=\"data.legendPosition\"\n      [scaleMin]=\"data.scaleMin\"\n      [scaleMax]=\"data.scaleMax\"\n      [scaleStep]=\"data.scaleStep\"\n      [displayGridLines]=\"data.displayGridLines\"\n      [displayDataLabels]=\"!!data.displayValues\"\n      [colorSet]=\"colorSet\"\n      [font]=\"textFont\"\n      [sizeRatio]=\"chartSizeRatio$ | async\"\n    ></sb-chart>\n  </div>\n</span>\n" }]
        }] });

class BasePieChartComponent extends BaseChartComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BasePieChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BasePieChartComponent, selector: "sb-base-pie-chart", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <sb-chart\n      [type]=\"data.mode\"\n      [data]=\"elementData\"\n      [legendPosition]=\"data.legendPosition\"\n      [displayDataLabels]=\"\n        !data.legendPosition || data.legendPosition === 'off'\n      \"\n      [colorSet]=\"colorSet\"\n      [font]=\"textFont\"\n      [pieDisplayValuesInDataLabels]=\"!!data.displayValues\"\n      [sizeRatio]=\"chartSizeRatio$ | async\"\n    ></sb-chart>\n  </div>\n</span>\n", dependencies: [{ kind: "component", type: i1$1.ChartComponent, selector: "sb-chart", inputs: ["type", "data", "colorSet", "font", "stacked", "widthToHeightRatio", "sizeRatio", "legendPosition", "displayDataLabels", "scaleMin", "scaleMax", "scaleStep", "displayGridLines", "displayTooltips", "tooltipFormatter", "allowPointerEvents", "allowDecimalsXAxis", "allowDecimalsYAxis", "pieSize", "pieInnerSize", "pieDisplayValuesInDataLabels"], outputs: ["onSeriesMouseOver"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BasePieChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-pie-chart', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <sb-chart\n      [type]=\"data.mode\"\n      [data]=\"elementData\"\n      [legendPosition]=\"data.legendPosition\"\n      [displayDataLabels]=\"\n        !data.legendPosition || data.legendPosition === 'off'\n      \"\n      [colorSet]=\"colorSet\"\n      [font]=\"textFont\"\n      [pieDisplayValuesInDataLabels]=\"!!data.displayValues\"\n      [sizeRatio]=\"chartSizeRatio$ | async\"\n    ></sb-chart>\n  </div>\n</span>\n" }]
        }] });

class BaseCodeComponent extends SlideElementComponent {
    get languageCode() {
        return this.data.code || 'javascript';
    }
    constructor(domSanitizer) {
        super();
        this.domSanitizer = domSanitizer;
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        this.render();
    }
    render() {
        const html = Prism.highlight(this.data.code || '', Prism.languages[this.data.language] || Prism.languages.javascript, this.data.language);
        this.codeHtml = this.domSanitizer.bypassSecurityTrustHtml(html);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseCodeComponent, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseCodeComponent, selector: "sb-base-code", usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <pre\n      class=\"language-{{ languageCode }}\"\n    ><code class=\"language-{{ languageCode }}\" [innerHTML]=\"codeHtml\"></code></pre>\n  </div>\n</span>\n", styles: ["code[class*=language-],pre[class*=language-]{color:#000;background:none;text-shadow:0 1px white;font-family:Consolas,Monaco,Andale Mono,Ubuntu Mono,monospace;text-align:left;white-space:pre;word-spacing:normal;word-break:normal;word-wrap:normal;line-height:1.5;-moz-tab-size:4;-o-tab-size:4;tab-size:4;-webkit-hyphens:none;-moz-hyphens:none;-ms-hyphens:none;hyphens:none}pre[class*=language-]::-moz-selection,pre[class*=language-] ::-moz-selection,code[class*=language-]::-moz-selection,code[class*=language-] ::-moz-selection{text-shadow:none;background:#b3d4fc}pre[class*=language-]::selection,pre[class*=language-] ::selection,code[class*=language-]::selection,code[class*=language-] ::selection{text-shadow:none;background:#b3d4fc}@media print{code[class*=language-],pre[class*=language-]{text-shadow:none}}pre[class*=language-]{padding:1em;margin:.5em 0;overflow:auto}:not(pre)>code[class*=language-],pre[class*=language-]{background:#f5f2f0}:not(pre)>code[class*=language-]{padding:.1em;border-radius:.3em;white-space:normal}:host ::ng-deep * .token.comment,:host ::ng-deep * .token.prolog,:host ::ng-deep * .token.doctype,:host ::ng-deep * .token.cdata{color:#708090}:host ::ng-deep * .token.punctuation{color:#999}:host ::ng-deep * .namespace{opacity:.7}:host ::ng-deep * .token.property,:host ::ng-deep * .token.tag,:host ::ng-deep * .token.boolean,:host ::ng-deep * .token.number,:host ::ng-deep * .token.constant,:host ::ng-deep * .token.symbol,:host ::ng-deep * .token.deleted{color:#905}:host ::ng-deep * .token.selector,:host ::ng-deep * .token.attr-name,:host ::ng-deep * .token.string,:host ::ng-deep * .token.char,:host ::ng-deep * .token.builtin,:host ::ng-deep * .token.inserted{color:#690}:host ::ng-deep * .token.operator,:host ::ng-deep * .token.entity,:host ::ng-deep * .token.url,:host ::ng-deep * .language-css .token.string,:host ::ng-deep * .style .token.string{color:#a67f59;background:#ffffff80}:host ::ng-deep * .token.atrule,:host ::ng-deep * .token.attr-value,:host ::ng-deep * .token.keyword{color:#07a}:host ::ng-deep * .token.function{color:#dd4a68}:host ::ng-deep * .token.regex,:host ::ng-deep * .token.important,:host ::ng-deep * .token.variable{color:#e90}:host ::ng-deep * .token.important,:host ::ng-deep * .token.bold{font-weight:700}:host ::ng-deep * .token.italic{font-style:italic}:host ::ng-deep * .token.entity{cursor:help}\n"], dependencies: [{ kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseCodeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-code', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <pre\n      class=\"language-{{ languageCode }}\"\n    ><code class=\"language-{{ languageCode }}\" [innerHTML]=\"codeHtml\"></code></pre>\n  </div>\n</span>\n", styles: ["code[class*=language-],pre[class*=language-]{color:#000;background:none;text-shadow:0 1px white;font-family:Consolas,Monaco,Andale Mono,Ubuntu Mono,monospace;text-align:left;white-space:pre;word-spacing:normal;word-break:normal;word-wrap:normal;line-height:1.5;-moz-tab-size:4;-o-tab-size:4;tab-size:4;-webkit-hyphens:none;-moz-hyphens:none;-ms-hyphens:none;hyphens:none}pre[class*=language-]::-moz-selection,pre[class*=language-] ::-moz-selection,code[class*=language-]::-moz-selection,code[class*=language-] ::-moz-selection{text-shadow:none;background:#b3d4fc}pre[class*=language-]::selection,pre[class*=language-] ::selection,code[class*=language-]::selection,code[class*=language-] ::selection{text-shadow:none;background:#b3d4fc}@media print{code[class*=language-],pre[class*=language-]{text-shadow:none}}pre[class*=language-]{padding:1em;margin:.5em 0;overflow:auto}:not(pre)>code[class*=language-],pre[class*=language-]{background:#f5f2f0}:not(pre)>code[class*=language-]{padding:.1em;border-radius:.3em;white-space:normal}:host ::ng-deep * .token.comment,:host ::ng-deep * .token.prolog,:host ::ng-deep * .token.doctype,:host ::ng-deep * .token.cdata{color:#708090}:host ::ng-deep * .token.punctuation{color:#999}:host ::ng-deep * .namespace{opacity:.7}:host ::ng-deep * .token.property,:host ::ng-deep * .token.tag,:host ::ng-deep * .token.boolean,:host ::ng-deep * .token.number,:host ::ng-deep * .token.constant,:host ::ng-deep * .token.symbol,:host ::ng-deep * .token.deleted{color:#905}:host ::ng-deep * .token.selector,:host ::ng-deep * .token.attr-name,:host ::ng-deep * .token.string,:host ::ng-deep * .token.char,:host ::ng-deep * .token.builtin,:host ::ng-deep * .token.inserted{color:#690}:host ::ng-deep * .token.operator,:host ::ng-deep * .token.entity,:host ::ng-deep * .token.url,:host ::ng-deep * .language-css .token.string,:host ::ng-deep * .style .token.string{color:#a67f59;background:#ffffff80}:host ::ng-deep * .token.atrule,:host ::ng-deep * .token.attr-value,:host ::ng-deep * .token.keyword{color:#07a}:host ::ng-deep * .token.function{color:#dd4a68}:host ::ng-deep * .token.regex,:host ::ng-deep * .token.important,:host ::ng-deep * .token.variable{color:#e90}:host ::ng-deep * .token.important,:host ::ng-deep * .token.bold{font-weight:700}:host ::ng-deep * .token.italic{font-style:italic}:host ::ng-deep * .token.entity{cursor:help}\n"] }]
        }], ctorParameters: () => [{ type: i1$2.DomSanitizer }] });

class BaseFooterComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.hasBackgroundImage$ = new BehaviorSubject(false);
    }
    reloadStyles() {
        super.reloadStyles();
        this.hasBackgroundImage$.next(hasBackgroundImage(this.slide));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseFooterComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseFooterComponent, selector: "sb-base-footer", usesInheritance: true, ngImport: i0, template: "<div *ngIf=\"hasBackgroundImage$ | async\" class=\"footer-fader\"></div>\n<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"footer-content {{ textAlign$ | async }}\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div\n      class=\"small\"\n      [innerHTML]=\"data.footer | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseFooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-footer', template: "<div *ngIf=\"hasBackgroundImage$ | async\" class=\"footer-fader\"></div>\n<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"footer-content {{ textAlign$ | async }}\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div\n      class=\"small\"\n      [innerHTML]=\"data.footer | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n" }]
        }] });

class BaseImageComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.figureSize$ = new BehaviorSubject(1);
        this.imageClasses$ = new BehaviorSubject({});
        this.imageStyles$ = new BehaviorSubject({});
        this.iconStyles$ = new BehaviorSubject({});
        this.svgIconStyles$ = new BehaviorSubject({});
        this.captionStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        this.reloadFigureSize();
        this.reloadImageClasses();
        this.reloadImageStyles();
        this.reloadIconStyles();
        this.reloadCaptionStyles();
    }
    reloadFigureSize() {
        let size = 1;
        // XXX: Translate old image sizes
        if (this.data.image && this.slide.updatedAt < 1453241666342) {
            // Jan 19th
            size = 0.83 + 0.12 * getSlideOptionValue(this.slide)('figureSize');
        }
        else if (this.data.icon &&
            this.slide.updatedAt < 1453241666342) {
            // Jan 19th
            size += getSlideOptionValue(this.slide)('figureSize') / 3;
            size /= 3;
        }
        else {
            size += getSlideOptionValue(this.slide)('figureSize') / 3;
            size += getElementOptionValue(this.element)('size') / 3;
        }
        this.figureSize$.next(size);
        /*case 'logo':
          size += (getSlideOptionValue(this.slide)('logoSize') / 3);
          break;*/
    }
    reloadImageClasses() {
        const classes = {};
        const slideCropOption = getSlideOptionValue(this.slide)('imageCrop');
        const elementCropOption = getElementOptionValue(this.element)('imageCrop');
        const cropOption = elementCropOption !== 'slide-default'
            ? elementCropOption
            : slideCropOption;
        if (!/default/.test(cropOption)) {
            classes[`image-${cropOption}`] = true;
        }
        this.imageClasses$.next(classes);
    }
    reloadImageStyles() {
        this.imageStyles$.next({});
    }
    reloadIconStyles() {
        const set = new ColorSet(this.colorSet);
        const highlightColor = set.highlightColor;
        this.iconStyles$.next({
            color: highlightColor,
            stroke: highlightColor,
            fill: highlightColor,
        });
        this.svgIconStyles$.next({});
    }
    reloadCaptionStyles() {
        this.captionStyles$.next({});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseImageComponent, selector: "sb-base-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class BaseNumberComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.figureSize$ = new BehaviorSubject(1);
        this.numberStyles$ = new BehaviorSubject({});
        this.captionStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        this.reloadFigureSize();
        const set = new ColorSet(this.colorSet);
        this.numberStyles$.next({
            color: set.highlightColor,
        });
        this.captionStyles$.next({});
    }
    reloadFigureSize() {
        let size = 1;
        size += getSlideOptionValue(this.slide)('figureSize') / 3;
        size += getElementOptionValue(this.element)('size') / 3;
        this.figureSize$.next(size);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseNumberComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseNumberComponent, selector: "sb-base-number", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-number', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class BaseQuoteComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.symbolStyles$ = new BehaviorSubject({});
        this.authorStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.symbolStyles$.next({
            color: set.highlightColor,
        });
        this.authorStyles$.next({});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseQuoteComponent, selector: "sb-base-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&laquo;</span\n      >\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&raquo;</span\n      >\n    </h4>\n    <span\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></span>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&laquo;</span\n      >\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&raquo;</span\n      >\n    </h4>\n    <span\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></span>\n  </div>\n</span>\n" }]
        }] });

class BaseTableComponent extends SlideElementComponent {
    constructor() {
        super(...arguments);
        this.tableStyles$ = new BehaviorSubject({});
        this.regularCellStyles$ = new BehaviorSubject({});
        this.highlightedCellStyles$ = new BehaviorSubject({});
        this.isFirstRowHighlighted$ = new BehaviorSubject(false);
        this.isFirstColHighlighted$ = new BehaviorSubject(false);
    }
    reloadStyles() {
        super.reloadStyles();
        this.tableStyles$.next({});
        const set = new ColorSet(this.colorSet);
        const textColor = set.textColor;
        const backColor = set.backColor;
        const highlightColor = set.highlightColor;
        const cellBorderColor = new Color(backColor).darkenOrLighten(15).val();
        this.regularCellStyles$.next({
            'border-color': cellBorderColor,
        });
        this.highlightedCellStyles$.next({
            'border-color': cellBorderColor,
            'background-color': highlightColor,
            'color': ColorSet.getMostReadableColor(highlightColor)([
                backColor,
                textColor,
            ]),
        });
        const firstRowOption = getElementOptionValue(this.element)('tableFirstRow');
        this.isFirstRowHighlighted$.next(firstRowOption === true ||
            (typeof firstRowOption === 'undefined' && this.data.firstRow === true)); // tslint:disable-line max-line-length
        const firstColOption = getElementOptionValue(this.element)('tableFirstColumn');
        this.isFirstColHighlighted$.next(firstColOption === true ||
            (typeof firstColOption === 'undefined' &&
                this.data.firstColumn === true)); // tslint:disable-line max-line-length
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseTableComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseTableComponent, selector: "sb-base-table", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h2\n      *ngIf=\"data.title && data.title.length\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h2>\n    <table [ngStyle]=\"tableStyles$ | async\">\n      <tr *ngFor=\"let row of data.rows; let rowIndex = index\">\n        <td\n          *ngFor=\"let cell of row; let cellIndex = index\"\n          [ngStyle]=\"\n            (rowIndex === 0 && (isFirstRowHighlighted$ | async)) ||\n            (cellIndex === 0 && (isFirstColHighlighted$ | async))\n              ? (highlightedCellStyles$ | async)\n              : (regularCellStyles$ | async)\n          \"\n          data-slide-element-export-text\n        >\n          <span [innerHTML]=\"cell | sbSanitizeHtml | sbTrustHtml\"></span>\n        </td>\n      </tr>\n    </table>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-table', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h2\n      *ngIf=\"data.title && data.title.length\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h2>\n    <table [ngStyle]=\"tableStyles$ | async\">\n      <tr *ngFor=\"let row of data.rows; let rowIndex = index\">\n        <td\n          *ngFor=\"let cell of row; let cellIndex = index\"\n          [ngStyle]=\"\n            (rowIndex === 0 && (isFirstRowHighlighted$ | async)) ||\n            (cellIndex === 0 && (isFirstColHighlighted$ | async))\n              ? (highlightedCellStyles$ | async)\n              : (regularCellStyles$ | async)\n          \"\n          data-slide-element-export-text\n        >\n          <span [innerHTML]=\"cell | sbSanitizeHtml | sbTrustHtml\"></span>\n        </td>\n      </tr>\n    </table>\n  </div>\n</span>\n" }]
        }] });

class BaseVideoComponent extends SlideElementComponent {
    activate() {
        if (this.videoPlayer) {
            this.videoPlayer.play();
        }
    }
    deactivate() {
        if (this.videoPlayer) {
            this.videoPlayer.pause();
        }
    }
    onVideoEnded() {
        this.onActivationEnded.emit(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseVideoComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BaseVideoComponent, selector: "sb-base-video", viewQueries: [{ propertyName: "videoPlayer", first: true, predicate: VideoPlayerComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: "<sb-video-player\n  [videoId]=\"data.video?.id\"\n  [videoSource]=\"data.video?.source\"\n  [videoPosition]=\"data.video?.position\"\n  (onVideoEnded)=\"onVideoEnded()\"\n></sb-video-player>\n", dependencies: [{ kind: "component", type: i1$3.VideoPlayerComponent, selector: "sb-video-player", inputs: ["videoId", "videoSource", "videoPosition", "showControls"], outputs: ["onVideoEnded"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BaseVideoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-base-video', template: "<sb-video-player\n  [videoId]=\"data.video?.id\"\n  [videoSource]=\"data.video?.source\"\n  [videoPosition]=\"data.video?.position\"\n  (onVideoEnded)=\"onVideoEnded()\"\n></sb-video-player>\n" }]
        }], propDecorators: { videoPlayer: [{
                type: ViewChild,
                args: [VideoPlayerComponent, { static: false }]
            }] } });

class AuroraCoverComponent extends BaseCoverComponent {
    constructor() {
        super(...arguments);
        this.astroEnabled$ = new BehaviorSubject(true);
        this.astroStyles$ = new BehaviorSubject({});
        this.astroTextStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const highlightColor = set.highlightColor;
        const foreColor = ColorSet.getMostReadableColor(highlightColor)([
            set.textColor,
            set.backColor,
        ]);
        const astroStyles = {};
        const astroTextStyles = {};
        const astroEnabled = this.slideElements.filter(isBodyElement).length === 0 &&
            ((this.data.title && this.data.title.length) ||
                (this.data.subtitle && this.data.subtitle.length) ||
                (this.data.author && this.data.author.length)); // tslint:disable-line max-line-length
        if (astroEnabled) {
            astroStyles['background-color'] = highlightColor;
            astroStyles['color'] = foreColor;
            if (hasBackgroundImage(this.slide) || hasBackgroundIcon(this.slide)) {
                astroStyles['opacity'] = 0.5;
            }
            astroTextStyles['color'] = foreColor;
        }
        this.astroEnabled$.next(astroEnabled);
        this.astroStyles$.next(astroStyles);
        this.astroTextStyles$.next(astroTextStyles);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: AuroraCoverComponent, selector: "sb-aurora-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"titleStyles$ | async\"\n      data-slide-element-export-text\n    ></h1>\n  </div>\n</span>\n\n<div\n  *ngIf=\"astroEnabled$ | async\"\n  class=\"astro\"\n  [ngStyle]=\"astroStyles$ | async\"\n  data-slide-decoration\n></div>\n<div\n  class=\"astro-text\"\n  [ngStyle]=\"astroTextStyles$ | async\"\n  data-slide-decoration\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-aurora-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"titleStyles$ | async\"\n      data-slide-element-export-text\n    ></h1>\n  </div>\n</span>\n\n<div\n  *ngIf=\"astroEnabled$ | async\"\n  class=\"astro\"\n  [ngStyle]=\"astroStyles$ | async\"\n  data-slide-decoration\n></div>\n<div\n  class=\"astro-text\"\n  [ngStyle]=\"astroTextStyles$ | async\"\n  data-slide-decoration\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</div>\n" }]
        }] });

class AuroraHeadingComponent extends BaseHeadingComponent {
    constructor() {
        super(...arguments);
        this.astroEnabled$ = new BehaviorSubject(true);
        this.astroStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.headingStyles$.next(Object.assign({}, this.headingStyles$.getValue(), {
            color: set.textColor,
        }));
        const astroStyles = {};
        const astroEnabled = this.slide.elements.length === 1;
        if (astroEnabled) {
            astroStyles['background-color'] = set.highlightColor;
        }
        this.astroStyles$.next(astroStyles);
        this.astroEnabled$.next(astroEnabled);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: AuroraHeadingComponent, selector: "sb-aurora-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n<div\n  *ngIf=\"astroEnabled$ | async\"\n  class=\"astro\"\n  [ngStyle]=\"astroStyles$ | async\"\n  data-slide-decoration\n></div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-aurora-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n<div\n  *ngIf=\"astroEnabled$ | async\"\n  class=\"astro\"\n  [ngStyle]=\"astroStyles$ | async\"\n  data-slide-decoration\n></div>\n" }]
        }] });

class AuroraBulletsComponent extends BaseBulletsComponent {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<i class="far fa-chevron-circle-right"></i>');
    }
    reloadBulletIndicatorStyles() {
        const set = new ColorSet(this.colorSet);
        const color = ColorSet.getMostReadableColor(set.backColor)([
            this.colorSet['back2'],
            set.highlightColor,
        ]);
        this.bulletIndicatorStyles$.next({ color });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: AuroraBulletsComponent, selector: "sb-aurora-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-aurora-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class AuroraImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-circle': true,
            });
        }
    }
    reloadIconStyles() {
        super.reloadIconStyles();
        const set = new ColorSet(this.colorSet);
        const highlightColor = set.highlightColor;
        const foreColor = ColorSet.getMostReadableColor(highlightColor)([
            set.textColor,
            set.backColor,
        ]);
        this.iconStyles$.next(Object.assign({}, this.iconStyles$.getValue(), {
            'background-color': highlightColor,
            'color': foreColor,
            'stroke': foreColor,
            'fill': foreColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: AuroraImageComponent, selector: "sb-aurora-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-aurora-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class AuroraNumberComponent extends BaseNumberComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const highlightColor = set.highlightColor;
        this.numberStyles$.next({
            'background-color': highlightColor,
            'color': ColorSet.getMostReadableColor(highlightColor)([
                set.textColor,
                set.backColor,
            ]),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraNumberComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: AuroraNumberComponent, selector: "sb-aurora-number", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-aurora-number', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class AuroraQuoteComponent extends BaseQuoteComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const highlightColor = set.highlightColor;
        this.symbolStyles$.next({
            'background-color': highlightColor,
            'color': ColorSet.getMostReadableColor(highlightColor)([
                set.textColor,
                set.backColor,
            ]),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: AuroraQuoteComponent, selector: "sb-aurora-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"icon-circle\" [ngStyle]=\"symbolStyles$ | async\">\n      <i class=\"far fa-quote-left\"></i>\n    </div>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AuroraQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-aurora-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"icon-circle\" [ngStyle]=\"symbolStyles$ | async\">\n      <i class=\"far fa-quote-left\"></i>\n    </div>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n" }]
        }] });

class BlueprintBulletsComponent extends BaseBulletsComponent {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        const html = `
      <span class="bullet-triangle">
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" xml:space="preserve">
          <g opacity="0.75">
            <path d="M54.3,4L4,54.3V4H54.3 M64,0H0v64L64,0L64,0z"/>
          </g>
        </svg>
      </span>
    `;
        return $(html);
    }
    reloadBulletIndicatorStyles() {
        super.reloadBulletIndicatorStyles();
        const set = new ColorSet(this.colorSet);
        const color = ColorSet.getMostReadableColor(set.backColor)([
            this.colorSet['back2'],
            set.highlightColor,
        ]);
        this.bulletIndicatorStyles$.next({
            color: color,
            fill: color,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BlueprintBulletsComponent, selector: "sb-blueprint-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-blueprint-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class BlueprintImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-square': true,
            });
        }
    }
    reloadImageStyles() {
        super.reloadImageStyles();
        const set = new ColorSet(this.colorSet);
        const foreColor = set.textColor;
        this.imageStyles$.next(Object.assign({}, this.imageStyles$.getValue(), {
            'color': foreColor,
            'fill': foreColor,
        }));
    }
    reloadIconStyles() {
        super.reloadIconStyles();
        const set = new ColorSet(this.colorSet);
        const foreColor = set.textColor;
        this.iconStyles$.next(Object.assign({}, this.iconStyles$.getValue(), {
            'color': foreColor,
            'fill': foreColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BlueprintImageComponent, selector: "sb-blueprint-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-blueprint-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class BlueprintNumberComponent extends BaseNumberComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const color = set.textColor;
        this.numberStyles$.next({
            color: color,
            fill: color,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintNumberComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BlueprintNumberComponent, selector: "sb-blueprint-number", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-blueprint-number', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class BlueprintQuoteComponent extends BaseQuoteComponent {
    constructor() {
        super(...arguments);
        this.lineColor$ = new BehaviorSubject('');
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.lineColor$.next(set.textColor);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BlueprintQuoteComponent, selector: "sb-blueprint-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"author-block\">\n      <h5\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></h5>\n      <div\n        class=\"author-block-line\"\n        [style.background-color]=\"lineColor$ | async\"\n        data-slide-decoration\n      ></div>\n    </div>\n    <p\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n    <div class=\"quote-symbol\">\"</div>\n  </div>\n</span>\n", dependencies: [{ kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-blueprint-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"author-block\">\n      <h5\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></h5>\n      <div\n        class=\"author-block-line\"\n        [style.background-color]=\"lineColor$ | async\"\n        data-slide-decoration\n      ></div>\n    </div>\n    <p\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n    <div class=\"quote-symbol\">\"</div>\n  </div>\n</span>\n" }]
        }] });

class BoulevardCoverComponent extends BaseCoverComponent {
    constructor() {
        super(...arguments);
        this.barStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.barStyles$.next({
            'background-color': set.highlightColor,
            'opacity': hasBackgroundImage(this.slide) || hasBackgroundIcon(this.slide)
                ? 0.5
                : 1.0,
        });
        this.authorStyles$.next({
            color: set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BoulevardCoverComponent, selector: "sb-boulevard-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    *ngIf=\"data.title && data.title.length\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div class=\"bar\" [ngStyle]=\"barStyles$ | async\" data-slide-decoration></div>\n  </div>\n\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-boulevard-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    *ngIf=\"data.title && data.title.length\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div class=\"bar\" [ngStyle]=\"barStyles$ | async\" data-slide-decoration></div>\n  </div>\n\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class BoulevardHeadingComponent extends BaseHeadingComponent {
    constructor() {
        super(...arguments);
        this.barColor$ = new BehaviorSubject('');
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.barColor$.next(this.colorSet['back2'] ?? set.highlightColor);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BoulevardHeadingComponent, selector: "sb-boulevard-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"heading-container {{ textAlign$ | async }}\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div\n      class=\"bar bar-left\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <div\n      class=\"bar bar-right\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-boulevard-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"heading-container {{ textAlign$ | async }}\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div\n      class=\"bar bar-left\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <div\n      class=\"bar bar-right\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n" }]
        }] });

class BoulevardBulletsComponent extends BaseBulletsComponent {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<i class="fas fa-square"></i>');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BoulevardBulletsComponent, selector: "sb-boulevard-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-boulevard-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class BoulevardImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-contain': true,
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BoulevardImageComponent, selector: "sb-boulevard-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-boulevard-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class BoulevardQuoteComponent extends BaseQuoteComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.authorStyles$.next({
            'color': set.backColor,
            'background-color': set.textColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BoulevardQuoteComponent, selector: "sb-boulevard-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\">&ldquo;</span>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div *ngIf=\"data.author && data.author.length\" class=\"author\">\n      <span\n        [ngStyle]=\"authorStyles$ | async\"\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BoulevardQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-boulevard-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\">&ldquo;</span>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div *ngIf=\"data.author && data.author.length\" class=\"author\">\n      <span\n        [ngStyle]=\"authorStyles$ | async\"\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

function longShadow(colorCode, distance = 10, upsideDown = false) {
    let start = 1;
    let end = distance;
    if (distance < 0) {
        start = distance;
        end = -1;
    }
    const values = [];
    for (let i = start; i <= end; i++) {
        values.push(`${i}px ${Math.abs(i) * (upsideDown === true ? -1 : 1)}px 0px ${colorCode}`);
    }
    return values.join(', ');
}
function longShadowFilter(colorCode, distance = 10, upsideDown = false) {
    let start = 1;
    let end = distance;
    if (distance < 0) {
        start = distance;
        end = -1;
    }
    const values = [];
    for (let i = start; i <= end; i++) {
        values.push(`drop-shadow(${i}px ${Math.abs(i) * (upsideDown === true ? -1 : 1)}px 0px ${colorCode})`);
    }
    return values.join(' ');
}

class FiveOclockCoverComponent extends BaseCoverComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const shadowColor = ColorSet.getMostReadableColor(set.textColor)([
            this.colorSet['back2'],
            new Color(set.backColor).darkenOrLighten(),
            set.highlightColor,
        ]);
        this.titleStyles$.next(Object.assign({}, this.titleStyles$.getValue(), {
            'text-shadow': longShadow(shadowColor, 15),
        }));
        this.subtitleStyles$.next(Object.assign({}, this.subtitleStyles$.getValue(), {
            'color': set.highlightColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockCoverComponent, selector: "sb-fiveoclock-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-fiveoclock-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class FiveOclockHeadingComponent extends BaseHeadingComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const shadowColor = ColorSet.getMostReadableColor(set.textColor)([
            this.colorSet['back2'],
            new Color(set.backColor).darkenOrLighten(),
            set.highlightColor,
        ]);
        this.headingStyles$.next(Object.assign({}, this.headingStyles$.getValue(), {
            'text-shadow': longShadow(shadowColor, 10),
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockHeadingComponent, selector: "sb-fiveoclock-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-fiveoclock-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n" }]
        }] });

class FiveOclockParagraphComponent extends BaseParagraphComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const backColor = new Color(this.colorSet['back2'] ?? set.highlightColor);
        const textColor = ColorSet.getMostReadableColor(backColor)([
            set.textColor,
            set.backColor,
            set.highlightColor,
        ]);
        this.paragraphContainerStyles$.next({
            'color': textColor,
            'background-color': backColor.val(),
            'box-shadow': longShadow(backColor.darkenOrLighten(10).val(), 10),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockParagraphComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockParagraphComponent, selector: "sb-five-oclock-paragraph", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <div\n      class=\"paragraph-container\"\n      [ngStyle]=\"paragraphContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"data.paragraph | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockParagraphComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-five-oclock-paragraph', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <div\n      class=\"paragraph-container\"\n      [ngStyle]=\"paragraphContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"data.paragraph | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class FiveOclockBulletsComponent extends BaseBulletsComponent {
    reloadBulletsContainerStyles() {
        super.reloadBulletsContainerStyles();
        const set = new ColorSet(this.colorSet);
        const backColor = this.colorSet['back2'] ?? set.highlightColor;
        const textColor = ColorSet.getMostReadableColor(backColor)([
            set.backColor,
            set.textColor,
            set.highlightColor,
        ]);
        this.bulletsContainerStyles$.next({
            'color': textColor,
            'background-color': backColor,
            'box-shadow': longShadow(new Color(backColor).darkenOrLighten(10).val(), 5),
        });
    }
    reloadBulletIndicatorStyles() {
        super.reloadBulletIndicatorStyles();
        const set = new ColorSet(this.colorSet);
        const textColor = ColorSet.getMostReadableColor(this.colorSet['back2'] ?? set.highlightColor)([set.backColor, set.textColor, set.highlightColor]);
        this.bulletIndicatorStyles$.next({
            color: textColor,
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<i class="far fa-dot-circle"></i>');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockBulletsComponent, selector: "sb-fiveoclock-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-fiveoclock-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class FiveOclockImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-contain': true,
            });
        }
    }
    reloadIconStyles() {
        super.reloadIconStyles();
        const iconStyles = {};
        const svgStyles = {};
        const set = new ColorSet(this.colorSet);
        const backColor = new Color(this.colorSet['back2'] ?? set.highlightColor);
        const textColor = ColorSet.getMostReadableColor(backColor)([
            set.textColor,
            set.backColor,
            set.highlightColor,
        ]);
        iconStyles['color'] = iconStyles['stroke'] = iconStyles['fill'] = textColor;
        iconStyles['background-color'] = backColor.val();
        if (this.data.icon && this.data.icon.source === 'FontAwesome') {
            iconStyles['text-shadow'] = longShadow(backColor.darkenOrLighten(10).val(), 300);
        }
        else if (this.data.icon && this.data.icon.source === 'NounProject') {
            // XXX: Apply drop shadow only in Chrome. This is probably NOT the best way to do this.
            if (/Chrome/.test(navigator.userAgent) &&
                /Google Inc/.test(navigator.vendor)) {
                svgStyles['-webkit-filter'] = svgStyles['filter'] = longShadowFilter(backColor.darkenOrLighten(10).val(), 300);
            }
        }
        this.iconStyles$.next(iconStyles);
        this.svgIconStyles$.next(svgStyles);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockImageComponent, selector: "sb-five-oclock-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-five-oclock-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class FiveOclockNumberComponent extends BaseNumberComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const backColor = this.colorSet['back2'] ?? set.highlightColor;
        const textColor = ColorSet.getMostReadableColor(backColor)([
            set.textColor,
            set.backColor,
            set.highlightColor,
        ]);
        this.numberStyles$.next({
            'color': textColor,
            'stroke': textColor,
            'fill': textColor,
            'background-color': backColor,
            'text-shadow': longShadow(new Color(backColor).darkenOrLighten(10).val(), 300),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockNumberComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockNumberComponent, selector: "sb-five-oclock-number", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-five-oclock-number', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class FiveOclockQuoteComponent extends BaseQuoteComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const backColor = new Color(this.colorSet['back2'] ?? set.highlightColor);
        const textColor = ColorSet.getMostReadableColor(backColor)([
            set.textColor,
            set.backColor,
            set.highlightColor,
        ]);
        this.symbolStyles$.next({
            'color': textColor,
            'background-color': backColor.val(),
            'text-shadow': longShadow(backColor.darkenOrLighten(10).val(), 300),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockQuoteComponent, selector: "sb-five-oclock-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"icon-circle\" [ngStyle]=\"symbolStyles$ | async\">\n      <i class=\"far fa-quote-left\"></i>\n    </div>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-five-oclock-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"icon-circle\" [ngStyle]=\"symbolStyles$ | async\">\n      <i class=\"far fa-quote-left\"></i>\n    </div>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n" }]
        }] });

class FiveOclockTableComponent extends BaseTableComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.tableStyles$.next({
            'box-shadow': longShadow(new Color(set.backColor).darkenOrLighten(5).val(), 10),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockTableComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: FiveOclockTableComponent, selector: "sb-five-oclock-table", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h2\n      *ngIf=\"data.title && data.title.length\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h2>\n    <table [ngStyle]=\"tableStyles$ | async\">\n      <tr *ngFor=\"let row of data.rows; let rowIndex = index\">\n        <td\n          *ngFor=\"let cell of row; let cellIndex = index\"\n          [ngStyle]=\"\n            (rowIndex === 0 && (isFirstRowHighlighted$ | async)) ||\n            (cellIndex === 0 && (isFirstColHighlighted$ | async))\n              ? (highlightedCellStyles$ | async)\n              : (regularCellStyles$ | async)\n          \"\n          data-slide-element-export-text\n        >\n          <span [innerHTML]=\"cell | sbSanitizeHtml | sbTrustHtml\"></span>\n        </td>\n      </tr>\n    </table>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: FiveOclockTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-five-oclock-table', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h2\n      *ngIf=\"data.title && data.title.length\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h2>\n    <table [ngStyle]=\"tableStyles$ | async\">\n      <tr *ngFor=\"let row of data.rows; let rowIndex = index\">\n        <td\n          *ngFor=\"let cell of row; let cellIndex = index\"\n          [ngStyle]=\"\n            (rowIndex === 0 && (isFirstRowHighlighted$ | async)) ||\n            (cellIndex === 0 && (isFirstColHighlighted$ | async))\n              ? (highlightedCellStyles$ | async)\n              : (regularCellStyles$ | async)\n          \"\n          data-slide-element-export-text\n        >\n          <span [innerHTML]=\"cell | sbSanitizeHtml | sbTrustHtml\"></span>\n        </td>\n      </tr>\n    </table>\n  </div>\n</span>\n" }]
        }] });

class PolygonCoverComponent extends BaseCoverComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.titleStyles$.next(Object.assign({}, this.titleStyles$.getValue(), {
            'color': set.highlightColor,
        }));
        this.subtitleStyles$.next(Object.assign({}, this.subtitleStyles$.getValue(), {
            'border-color': this.colorSet['back2'] ??
                new Color(set.backColor).darkenOrLighten().val(),
        }));
        this.authorStyles$.next(Object.assign({}, this.authorStyles$.getValue(), {
            'border-color': set.highlightColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonCoverComponent, selector: "sb-polygon-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class PolygonHeadingComponent extends BaseHeadingComponent {
    constructor() {
        super(...arguments);
        this.boxStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.boxStyles$.next({
            'background-color': set.highlightColor,
            'border-color': this.colorSet['back2'] ??
                new Color(set.backColor).darkenOrLighten(20).val(),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonHeadingComponent, selector: "sb-polygon-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n  <div\n    *ngIf=\"slide.elements.length === 1\"\n    class=\"box\"\n    [ngStyle]=\"boxStyles$ | async\"\n    data-slide-decoration\n  ></div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n  <div\n    *ngIf=\"slide.elements.length === 1\"\n    class=\"box\"\n    [ngStyle]=\"boxStyles$ | async\"\n    data-slide-decoration\n  ></div>\n</span>\n" }]
        }] });

class PolygonParagraphComponent extends BaseParagraphComponent {
    constructor() {
        super(...arguments);
        this.boxStyles$ = new BehaviorSubject({});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonParagraphComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonParagraphComponent, selector: "sb-polygon-paragraph", usesInheritance: true, ngImport: i0, template: "<div\n  class=\"box {{ textAlign$ | async }}\"\n  [ngStyle]=\"boxStyles$ | async\"\n  data-slide-decoration\n>\n  <span class=\"slide-element__sizer--text\">\n    <div\n      [style.font-size.em]=\"textSize$ | async\"\n      [innerHTML]=\"data.paragraph | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </span>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonParagraphComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-paragraph', template: "<div\n  class=\"box {{ textAlign$ | async }}\"\n  [ngStyle]=\"boxStyles$ | async\"\n  data-slide-decoration\n>\n  <span class=\"slide-element__sizer--text\">\n    <div\n      [style.font-size.em]=\"textSize$ | async\"\n      [innerHTML]=\"data.paragraph | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </span>\n</div>\n" }]
        }] });

class PolygonBulletsComponent extends BaseBulletsComponent {
    reloadBulletIndicatorStyles() {
        const set = new ColorSet(this.colorSet);
        const color = ColorSet.getMostReadableColor(set.backColor)([
            this.colorSet['back2'],
            set.highlightColor,
        ]);
        if (this.data.mode === '123' || this.data.mode === 'abc') {
            this.bulletIndicatorStyles$.next({
                color,
            });
        }
        else {
            this.bulletIndicatorStyles$.next({
                'background-color': color,
            });
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<span class="bullet-point"></span>');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonBulletsComponent, selector: "sb-polygon-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class PolygonImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-square': true,
            });
        }
    }
    reloadIconStyles() {
        super.reloadIconStyles();
        const set = new ColorSet(this.colorSet);
        const backColor = this.colorSet['back2'] ?? set.highlightColor;
        const foreColor = ColorSet.getMostReadableColor(backColor)([
            set.textColor,
            set.backColor,
            set.highlightColor,
        ]);
        this.iconStyles$.next({
            'background-color': backColor,
            'color': foreColor,
            'stroke': foreColor,
            'fill': foreColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonImageComponent, selector: "sb-polygon-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class PolygonNumberComponent extends BaseNumberComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const backColor = this.colorSet['back2'] ?? set.highlightColor;
        const foreColor = ColorSet.getMostReadableColor(backColor)([
            set.textColor,
            set.backColor,
            set.highlightColor,
        ]);
        this.numberStyles$.next({
            'background-color': backColor,
            'color': foreColor,
            'stroke': foreColor,
            'fill': foreColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonNumberComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonNumberComponent, selector: "sb-polygon-number", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-number', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class PolygonQuoteComponent extends BaseQuoteComponent {
    constructor() {
        super(...arguments);
        this.boxStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.boxStyles$.next({
            'background-color': set.highlightColor,
            'color': ColorSet.getMostReadableColor(set.highlightColor)([
                set.textColor,
                set.backColor,
            ]),
        });
        this.symbolStyles$.next({
            color: set.backColor,
        });
        this.authorStyles$.next({
            color: set.backColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonQuoteComponent, selector: "sb-polygon-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"box\" [ngStyle]=\"boxStyles$ | async\" data-slide-decoration>\n      <i\n        class=\"far fa-quote-left quote-symbol\"\n        [ngStyle]=\"symbolStyles$ | async\"\n      ></i>\n      <div class=\"quote\">\n        <h4\n          [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></h4>\n      </div>\n      <div\n        *ngIf=\"data.author && data.author.length\"\n        class=\"author\"\n        [ngStyle]=\"authorStyles$ | async\"\n      >\n        <span\n          [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></span>\n      </div>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"box\" [ngStyle]=\"boxStyles$ | async\" data-slide-decoration>\n      <i\n        class=\"far fa-quote-left quote-symbol\"\n        [ngStyle]=\"symbolStyles$ | async\"\n      ></i>\n      <div class=\"quote\">\n        <h4\n          [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></h4>\n      </div>\n      <div\n        *ngIf=\"data.author && data.author.length\"\n        class=\"author\"\n        [ngStyle]=\"authorStyles$ | async\"\n      >\n        <span\n          [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></span>\n      </div>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class RaptureCoverComponent extends BaseCoverComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.titleStyles$.next(Object.assign({}, this.titleStyles$.getValue(), {
            color: set.highlightColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: RaptureCoverComponent, selector: "sb-rapture-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-rapture-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class RaptureImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-circle': true,
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: RaptureImageComponent, selector: "sb-rapture-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-rapture-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class RaptureQuoteComponent extends BaseQuoteComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.symbolStyles$.next({
            color: this.colorSet['back2'] ?? set.highlightColor,
        });
        this.authorStyles$.next({
            color: set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: RaptureQuoteComponent, selector: "sb-rapture-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&laquo;</span\n      >\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&raquo;</span\n      >\n    </h4>\n    <span\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></span>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-rapture-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&laquo;</span\n      >\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&raquo;</span\n      >\n    </h4>\n    <span\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></span>\n  </div>\n</span>\n" }]
        }] });

class ScalaCoverComponent extends BaseCoverComponent {
    constructor() {
        super(...arguments);
        this.lineColor$ = new BehaviorSubject('');
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.titleStyles$.next(Object.assign({}, this.titleStyles$.getValue(), {
            color: set.textColor,
        }));
        this.lineColor$.next(set.highlightColor);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: ScalaCoverComponent, selector: "sb-scala-cover", usesInheritance: true, ngImport: i0, template: "<div\n  *ngIf=\"\n    data.title && data.title.length && data.subtitle && data.subtitle.length\n  \"\n  class=\"bar bar-top\"\n  [style.background-color]=\"lineColor$ | async\"\n  data-slide-decoration\n></div>\n<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n<div\n  *ngIf=\"\n    data.title && data.title.length && data.subtitle && data.subtitle.length\n  \"\n  class=\"bar bar-bottom\"\n  [style.background-color]=\"lineColor$ | async\"\n  data-slide-decoration\n></div>\n\n<span class=\"slide-element__sizer--text\">\n  <p\n    *ngIf=\"data.author && data.author.length\"\n    [style.font-size.em]=\"textSize$ | async\"\n    [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n    data-slide-element-export-text\n  ></p>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-scala-cover', template: "<div\n  *ngIf=\"\n    data.title && data.title.length && data.subtitle && data.subtitle.length\n  \"\n  class=\"bar bar-top\"\n  [style.background-color]=\"lineColor$ | async\"\n  data-slide-decoration\n></div>\n<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n<div\n  *ngIf=\"\n    data.title && data.title.length && data.subtitle && data.subtitle.length\n  \"\n  class=\"bar bar-bottom\"\n  [style.background-color]=\"lineColor$ | async\"\n  data-slide-decoration\n></div>\n\n<span class=\"slide-element__sizer--text\">\n  <p\n    *ngIf=\"data.author && data.author.length\"\n    [style.font-size.em]=\"textSize$ | async\"\n    [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n    data-slide-element-export-text\n  ></p>\n</span>\n" }]
        }] });

class ScalaHeadingComponent extends BaseHeadingComponent {
    constructor() {
        super(...arguments);
        this.lineColor$ = new BehaviorSubject('');
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.headingStyles$.next(Object.assign({}, this.headingStyles$.getValue(), {
            color: set.textColor,
        }));
        this.lineColor$.next(set.highlightColor);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: ScalaHeadingComponent, selector: "sb-scala-heading", usesInheritance: true, ngImport: i0, template: "<div class=\"{{ textAlign$ | async }}\">\n  <div class=\"heading-wrapper\">\n    <div\n      class=\"bar\"\n      [style.background-color]=\"lineColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <span class=\"slide-element__sizer--text\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <h2\n          [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n          [ngStyle]=\"headingStyles$ | async\"\n          data-slide-element-export-text\n        ></h2>\n        <h5\n          *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n          [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n          [ngStyle]=\"headingStyles$ | async\"\n          data-slide-element-export-text\n        ></h5>\n      </div>\n    </span>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-scala-heading', template: "<div class=\"{{ textAlign$ | async }}\">\n  <div class=\"heading-wrapper\">\n    <div\n      class=\"bar\"\n      [style.background-color]=\"lineColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <span class=\"slide-element__sizer--text\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <h2\n          [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n          [ngStyle]=\"headingStyles$ | async\"\n          data-slide-element-export-text\n        ></h2>\n        <h5\n          *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n          [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n          [ngStyle]=\"headingStyles$ | async\"\n          data-slide-element-export-text\n        ></h5>\n      </div>\n    </span>\n  </div>\n</div>\n" }]
        }] });

class ScalaBulletsComponent extends BaseBulletsComponent {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<span>&#8211;</span>');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: ScalaBulletsComponent, selector: "sb-scala-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-scala-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class ScalaFooterComponent extends BaseFooterComponent {
    constructor() {
        super(...arguments);
        this.lineColor$ = new BehaviorSubject('');
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.lineColor$.next(set.highlightColor);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaFooterComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: ScalaFooterComponent, selector: "sb-scala-footer", usesInheritance: true, ngImport: i0, template: "<div\n  *ngIf=\"hasBackgroundImage$ | async\"\n  class=\"footer-fader\"\n  data-slide-decoration\n></div>\n<div class=\"{{ textAlign$ | async }}\">\n  <div class=\"footer-wrapper\">\n    <div\n      class=\"bar\"\n      [style.background-color]=\"lineColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <span class=\"slide-element__sizer--text\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <div\n          class=\"small\"\n          [innerHTML]=\"data.footer | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></div>\n      </div>\n    </span>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaFooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-scala-footer', template: "<div\n  *ngIf=\"hasBackgroundImage$ | async\"\n  class=\"footer-fader\"\n  data-slide-decoration\n></div>\n<div class=\"{{ textAlign$ | async }}\">\n  <div class=\"footer-wrapper\">\n    <div\n      class=\"bar\"\n      [style.background-color]=\"lineColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <span class=\"slide-element__sizer--text\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <div\n          class=\"small\"\n          [innerHTML]=\"data.footer | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></div>\n      </div>\n    </span>\n  </div>\n</div>\n" }]
        }] });

class ScalaImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-contain': true,
            });
        }
    }
    reloadIconStyles() {
        super.reloadIconStyles();
        const set = new ColorSet(this.colorSet);
        this.iconStyles$.next({
            'border-color': set.highlightColor,
            'color': set.textColor,
            'stroke': set.textColor,
            'fill': set.textColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: ScalaImageComponent, selector: "sb-scala-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      *ngIf=\"data.icon\"\n      class=\"icon-wrapper\"\n      [class.bordered]=\"data.caption && data.caption.length\"\n      [ngStyle]=\"iconStyles$ | async\"\n      data-slide-decoration\n    >\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <sb-noun-project-icon\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        class=\"icon\"\n        [iconId]=\"data.icon.id\"\n        [iconUrl]=\"data.icon.url\"\n        [color]=\"(iconStyles$ | async)['color']\"\n      ></sb-noun-project-icon>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-scala-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      *ngIf=\"data.icon\"\n      class=\"icon-wrapper\"\n      [class.bordered]=\"data.caption && data.caption.length\"\n      [ngStyle]=\"iconStyles$ | async\"\n      data-slide-decoration\n    >\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <sb-noun-project-icon\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        class=\"icon\"\n        [iconId]=\"data.icon.id\"\n        [iconUrl]=\"data.icon.url\"\n        [color]=\"(iconStyles$ | async)['color']\"\n      ></sb-noun-project-icon>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class ScalaNumberComponent extends BaseNumberComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.numberStyles$.next({
            'color': set.textColor,
            'border-color': set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaNumberComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: ScalaNumberComponent, selector: "sb-scala-number", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [class.bordered]=\"data.caption && data.caption.length\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 #number data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-scala-number', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [class.bordered]=\"data.caption && data.caption.length\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 #number data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class ScalaQuoteComponent extends BaseQuoteComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.authorStyles$.next({
            'border-color': set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: ScalaQuoteComponent, selector: "sb-scala-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol quote-left\">&ldquo;</span>\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol quote-right\">&rdquo;</span>\n    </h4>\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      class=\"author\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: ScalaQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-scala-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol quote-left\">&ldquo;</span>\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol quote-right\">&rdquo;</span>\n    </h4>\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      class=\"author\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n" }]
        }] });

class StairwayCoverComponent extends BaseCoverComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.titleStyles$.next(Object.assign({}, this.titleStyles$.getValue(), {
            'border-color': set.highlightColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: StairwayCoverComponent, selector: "sb-stairway-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-stairway-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class StairwayHeadingComponent extends BaseHeadingComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.headingStyles$.next(Object.assign({}, this.headingStyles$.getValue(), {
            'border-color': set.highlightColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: StairwayHeadingComponent, selector: "sb-stairway-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-stairway-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    [style.font-size.em]=\"textSize$ | async\"\n    class=\"{{ textAlign$ | async }}\"\n  >\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n" }]
        }] });

class StairwayBulletsComponent extends BaseBulletsComponent {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<span class="bullet-box"></span>');
    }
    reloadBulletIndicatorStyles() {
        super.reloadBulletIndicatorStyles();
        const set = new ColorSet(this.colorSet);
        const color = ColorSet.getMostReadableColor(set.backColor)([
            this.colorSet['back2'],
            set.highlightColor,
        ]);
        this.bulletIndicatorStyles$.next({
            color,
            'border-color': color,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: StairwayBulletsComponent, selector: "sb-stairway-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-stairway-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class StairwayImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-square': true,
            });
        }
    }
    reloadIconStyles() {
        super.reloadIconStyles();
        const set = new ColorSet(this.colorSet);
        const foreColor = set.textColor;
        this.iconStyles$.next({
            color: foreColor,
            stroke: foreColor,
            fill: foreColor,
        });
    }
    reloadCaptionStyles() {
        super.reloadCaptionStyles();
        const set = new ColorSet(this.colorSet);
        this.captionStyles$.next({
            'border-color': set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: StairwayImageComponent, selector: "sb-stairway-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-stairway-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class StairwayNumberComponent extends BaseNumberComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.numberStyles$.next({
            color: set.textColor,
        });
        this.captionStyles$.next({
            'border-color': set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayNumberComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: StairwayNumberComponent, selector: "sb-stairway-number", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayNumberComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-stairway-number', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div\n      class=\"number-wrapper\"\n      [ngStyle]=\"numberStyles$ | async\"\n      data-slide-decoration\n    >\n      <div class=\"number\">\n        <h1 data-slide-element-export-text>{{ data.rawNumber }}</h1>\n        <h5\n          *ngIf=\"data.unit && data.unit.length\"\n          data-slide-element-export-text\n          >{{ data.unit }}</h5\n        >\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class StairwayQuoteComponent extends BaseQuoteComponent {
    constructor() {
        super(...arguments);
        this.boxStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const highlightColor = set.highlightColor;
        this.boxStyles$.next({
            'background-color': highlightColor,
            'color': ColorSet.getMostReadableColor(highlightColor)([
                set.textColor,
                set.backColor,
            ]),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: StairwayQuoteComponent, selector: "sb-stairway-quote", usesInheritance: true, ngImport: i0, template: "<div class=\"box\">\n  <div\n    class=\"indicator\"\n    [style.border-right-color]=\"(boxStyles$ | async)['background-color']\"\n  ></div>\n  <div class=\"box-content\" [ngStyle]=\"boxStyles$ | async\">\n    <span class=\"slide-element__sizer--text\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <h4\n          [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></h4>\n      </div>\n    </span>\n  </div>\n</div>\n<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      class=\"author\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: StairwayQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-stairway-quote', template: "<div class=\"box\">\n  <div\n    class=\"indicator\"\n    [style.border-right-color]=\"(boxStyles$ | async)['background-color']\"\n  ></div>\n  <div class=\"box-content\" [ngStyle]=\"boxStyles$ | async\">\n    <span class=\"slide-element__sizer--text\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <h4\n          [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></h4>\n      </div>\n    </span>\n  </div>\n</div>\n<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      class=\"author\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n" }]
        }] });

class EYCoverComponent extends BaseCoverComponent {
    constructor() {
        super(...arguments);
        this.figureStyles$ = new BehaviorSubject({});
        this.coverLogoClasses$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const backColor = new Color(set.backColor);
        this.figureStyles$.next({
            stroke: set.highlightColor,
            fill: backColor.darkenOrLighten().alpha(0.3).val(),
        });
        this.titleStyles$.next(Object.assign({}, this.titleStyles$.getValue(), {
            color: set.textColor,
        }));
        this.authorStyles$.next(Object.assign({}, this.authorStyles$.getValue(), {
            color: set.highlightColor,
        }));
        const useLogoLight = backColor.brightness() < 160;
        this.coverLogoClasses$.next({
            'cover-logo-light': useLogoLight,
            'cover-logo-dark': !useLogoLight,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: EYCoverComponent, selector: "sb-ey-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div class=\"cover-container\">\n    <svg\n      version=\"1.1\"\n      xmlns=\"http://www.w3.org/2000/svg\"\n      viewBox=\"0 0 210 155\"\n      xml:space=\"preserve\"\n      [ngStyle]=\"figureStyles$ | async\"\n      data-slide-decoration\n    >\n      <g transform=\"translate(15,15)\" data-slide-decoration>\n        <polygon points=\"0 25, 0 125, 180 125, 180 0\" data-slide-decoration />\n      </g>\n    </svg>\n    <div [style.font-size.em]=\"textSize$ | async\">\n      <h1\n        *ngIf=\"data.title && data.title.length\"\n        [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n        [ngStyle]=\"titleStyles$ | async\"\n        data-slide-element-export-text\n      ></h1>\n      <h5\n        *ngIf=\"data.subtitle && data.subtitle.length\"\n        [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></h5>\n      <p\n        *ngIf=\"data.author && data.author.length\"\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        [ngStyle]=\"authorStyles$ | async\"\n        data-slide-element-export-text\n      ></p>\n    </div>\n  </div>\n</span>\n\n<div\n  class=\"cover-logo\"\n  [ngClass]=\"coverLogoClasses$ | async\"\n  data-slide-decoration\n></div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-ey-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div class=\"cover-container\">\n    <svg\n      version=\"1.1\"\n      xmlns=\"http://www.w3.org/2000/svg\"\n      viewBox=\"0 0 210 155\"\n      xml:space=\"preserve\"\n      [ngStyle]=\"figureStyles$ | async\"\n      data-slide-decoration\n    >\n      <g transform=\"translate(15,15)\" data-slide-decoration>\n        <polygon points=\"0 25, 0 125, 180 125, 180 0\" data-slide-decoration />\n      </g>\n    </svg>\n    <div [style.font-size.em]=\"textSize$ | async\">\n      <h1\n        *ngIf=\"data.title && data.title.length\"\n        [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n        [ngStyle]=\"titleStyles$ | async\"\n        data-slide-element-export-text\n      ></h1>\n      <h5\n        *ngIf=\"data.subtitle && data.subtitle.length\"\n        [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></h5>\n      <p\n        *ngIf=\"data.author && data.author.length\"\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        [ngStyle]=\"authorStyles$ | async\"\n        data-slide-element-export-text\n      ></p>\n    </div>\n  </div>\n</span>\n\n<div\n  class=\"cover-logo\"\n  [ngClass]=\"coverLogoClasses$ | async\"\n  data-slide-decoration\n></div>\n" }]
        }] });

class EYHeadingComponent extends BaseHeadingComponent {
    constructor() {
        super(...arguments);
        this.headingWithFigure$ = new BehaviorSubject(false);
        this.figureStyles$ = new BehaviorSubject({});
        this.arrowStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.headingStyles$.next(Object.assign({}, this.headingStyles$.getValue(), {
            color: set.textColor,
        }));
        this.headingWithFigure$.next(this.slideElements.filter(isBodyElement).length === 0);
        this.figureStyles$.next({
            stroke: set.highlightColor,
            fill: new Color(set.backColor).darkenOrLighten().alpha(0.3).val(),
        });
        this.arrowStyles$.next({
            color: set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: EYHeadingComponent, selector: "sb-ey-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"heading-container {{ textAlign$ | async }}\"\n    [class.heading-with-figure]=\"headingWithFigure$ | async\"\n  >\n    <svg\n      version=\"1.1\"\n      xmlns=\"http://www.w3.org/2000/svg\"\n      viewBox=\"0 0 210 155\"\n      xml:space=\"preserve\"\n      [ngStyle]=\"figureStyles$ | async\"\n      data-slide-decoration\n    >\n      <g transform=\"translate(15,15)\" data-slide-decoration>\n        <polygon points=\"0 25, 0 125, 180 125, 180 0\" data-slide-decoration />\n      </g>\n    </svg>\n    <div [style.font-size.em]=\"textSize$ | async\">\n      <h2>\n        <i\n          class=\"far fa-chevron-right heading-arrow-right\"\n          [ngStyle]=\"arrowStyles$ | async\"\n          data-slide-decoration\n        ></i>\n        <span\n          [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n          [ngStyle]=\"headingStyles$ | async\"\n          data-slide-element-export-text\n        ></span>\n        <i\n          class=\"far fa-chevron-left heading-arrow-left\"\n          [ngStyle]=\"arrowStyles$ | async\"\n          data-slide-decoration\n        ></i>\n      </h2>\n      <h5\n        *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n        [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n        [ngStyle]=\"headingStyles$ | async\"\n        data-slide-element-export-text\n      ></h5>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-ey-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"heading-container {{ textAlign$ | async }}\"\n    [class.heading-with-figure]=\"headingWithFigure$ | async\"\n  >\n    <svg\n      version=\"1.1\"\n      xmlns=\"http://www.w3.org/2000/svg\"\n      viewBox=\"0 0 210 155\"\n      xml:space=\"preserve\"\n      [ngStyle]=\"figureStyles$ | async\"\n      data-slide-decoration\n    >\n      <g transform=\"translate(15,15)\" data-slide-decoration>\n        <polygon points=\"0 25, 0 125, 180 125, 180 0\" data-slide-decoration />\n      </g>\n    </svg>\n    <div [style.font-size.em]=\"textSize$ | async\">\n      <h2>\n        <i\n          class=\"far fa-chevron-right heading-arrow-right\"\n          [ngStyle]=\"arrowStyles$ | async\"\n          data-slide-decoration\n        ></i>\n        <span\n          [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n          [ngStyle]=\"headingStyles$ | async\"\n          data-slide-element-export-text\n        ></span>\n        <i\n          class=\"far fa-chevron-left heading-arrow-left\"\n          [ngStyle]=\"arrowStyles$ | async\"\n          data-slide-decoration\n        ></i>\n      </h2>\n      <h5\n        *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n        [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n        [ngStyle]=\"headingStyles$ | async\"\n        data-slide-element-export-text\n      ></h5>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class EYBulletsComponent extends BaseBulletsComponent {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<i class="fas fa-circle"></i>');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: EYBulletsComponent, selector: "sb-ey-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-ey-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class EYImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-contain': true,
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: EYImageComponent, selector: "sb-ey-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-ey-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class EYQuoteComponent extends BaseQuoteComponent {
    constructor() {
        super(...arguments);
        this.boxStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        const highlightColor = set.highlightColor;
        this.boxStyles$.next({
            'background-color': highlightColor,
            'color': ColorSet.getMostReadableColor(highlightColor)([
                set.textColor,
                set.backColor,
            ]),
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: EYQuoteComponent, selector: "sb-ey-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div class=\"box\">\n    <div\n      class=\"indicator\"\n      [style.border-right-color]=\"(boxStyles$ | async)['background-color']\"\n    ></div>\n    <div class=\"box-content\" [ngStyle]=\"boxStyles$ | async\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <h4\n          [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></h4>\n      </div>\n    </div>\n  </div>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      class=\"author\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EYQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-ey-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div class=\"box\">\n    <div\n      class=\"indicator\"\n      [style.border-right-color]=\"(boxStyles$ | async)['background-color']\"\n    ></div>\n    <div class=\"box-content\" [ngStyle]=\"boxStyles$ | async\">\n      <div [style.font-size.em]=\"textSize$ | async\">\n        <h4\n          [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n          data-slide-element-export-text\n        ></h4>\n      </div>\n    </div>\n  </div>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      *ngIf=\"data.author && data.author.length\"\n      class=\"author\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></div>\n  </div>\n</span>\n" }]
        }] });

class GusCoverComponent extends BaseCoverComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.titleStyles$.next(Object.assign({}, this.titleStyles$.getValue(), {
            color: set.highlightColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: GusCoverComponent, selector: "sb-gus-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-gus-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class GusImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-circle': true,
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: GusImageComponent, selector: "sb-gus-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-gus-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class GusQuoteComponent extends BaseQuoteComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.symbolStyles$.next({
            color: this.colorSet['back2'] ?? set.highlightColor,
        });
        this.authorStyles$.next({
            color: set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: GusQuoteComponent, selector: "sb-gus-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&laquo;</span\n      >\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&raquo;</span\n      >\n    </h4>\n    <span\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></span>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-gus-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h4>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&laquo;</span\n      >\n      <span\n        [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n      <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\"\n        >&raquo;</span\n      >\n    </h4>\n    <span\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></span>\n  </div>\n</span>\n" }]
        }] });

class UrbanCollectiveCoverComponent extends BaseCoverComponent {
    constructor() {
        super(...arguments);
        this.barStyles$ = new BehaviorSubject({});
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.barStyles$.next({
            'background-color': set.highlightColor,
            'opacity': hasBackgroundImage(this.slide) || hasBackgroundIcon(this.slide)
                ? 0.5
                : 1.0,
        });
        this.authorStyles$.next({
            color: set.highlightColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveCoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: UrbanCollectiveCoverComponent, selector: "sb-urban-collective-cover", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"cover-logo\" data-slide-decoration></div>\n  </div>\n\n  <div\n    *ngIf=\"\n      data.title &&\n      data.title.length &&\n      data.subtitle &&\n      data.subtitle.length &&\n      data.author &&\n      data.author.length\n    \"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div class=\"bar\" [ngStyle]=\"barStyles$ | async\" data-slide-decoration></div>\n  </div>\n\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-urban-collective-cover', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div class=\"cover-logo\" data-slide-decoration></div>\n  </div>\n\n  <div\n    *ngIf=\"\n      data.title &&\n      data.title.length &&\n      data.subtitle &&\n      data.subtitle.length &&\n      data.author &&\n      data.author.length\n    \"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div class=\"bar\" [ngStyle]=\"barStyles$ | async\" data-slide-decoration></div>\n  </div>\n\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <h1\n      *ngIf=\"data.title && data.title.length\"\n      [ngStyle]=\"titleStyles$ | async\"\n      [innerHTML]=\"data.title | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h1>\n    <h5\n      *ngIf=\"data.subtitle && data.subtitle.length\"\n      [ngStyle]=\"subtitleStyles$ | async\"\n      [innerHTML]=\"data.subtitle | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h5>\n    <p\n      *ngIf=\"data.author && data.author.length\"\n      [ngStyle]=\"authorStyles$ | async\"\n      [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class UrbanCollectiveHeadingComponent extends BaseHeadingComponent {
    constructor() {
        super(...arguments);
        this.barColor$ = new BehaviorSubject('');
    }
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.barColor$.next(this.colorSet['back2'] ?? set.highlightColor);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveHeadingComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: UrbanCollectiveHeadingComponent, selector: "sb-urban-collective-heading", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"heading-container {{ textAlign$ | async }}\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div\n      class=\"bar bar-left\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <div\n      class=\"bar bar-right\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.StripHtmlPipe, name: "sbStripHtml" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveHeadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-urban-collective-heading', template: "<span class=\"slide-element__sizer--text\">\n  <div\n    class=\"heading-container {{ textAlign$ | async }}\"\n    [style.font-size.em]=\"textSize$ | async\"\n  >\n    <div\n      class=\"bar bar-left\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <div\n      class=\"bar bar-right\"\n      [style.background-color]=\"barColor$ | async\"\n      data-slide-decoration\n    ></div>\n    <h2\n      [innerHTML]=\"data.heading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h2>\n    <h5\n      *ngIf=\"data.subheading && (data.subheading | sbStripHtml).length\"\n      [innerHTML]=\"data.subheading | sbSanitizeHtml | sbTrustHtml\"\n      [ngStyle]=\"headingStyles$ | async\"\n      data-slide-element-export-text\n    ></h5>\n  </div>\n</span>\n" }]
        }] });

class UrbanCollectiveBulletsComponent extends BaseBulletsComponent {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFigureBulletIndicator(index) {
        return $('<i class="far fa-square"></i>');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveBulletsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: UrbanCollectiveBulletsComponent, selector: "sb-urban-collective-bullets", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveBulletsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-urban-collective-bullets', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <div\n      class=\"bullets-container\"\n      [ngStyle]=\"bulletsContainerStyles$ | async\"\n      data-slide-decoration\n    >\n      <span\n        [innerHTML]=\"bulletsHtml$ | async | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

class UrbanCollectiveImageComponent extends BaseImageComponent {
    reloadImageClasses() {
        super.reloadImageClasses();
        if (Object.keys(this.imageClasses$.getValue()).length === 0) {
            this.imageClasses$.next({
                'image-contain': true,
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveImageComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: UrbanCollectiveImageComponent, selector: "sb-urban-collective-image", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i2.NounProjectIconComponent, selector: "sb-noun-project-icon", inputs: ["iconId", "iconUrl", "color"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveImageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-urban-collective-image', template: "<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <figure\n      *ngIf=\"data.image\"\n      class=\"image\"\n      [ngClass]=\"imageClasses$ | async\"\n      [style.background-image]=\"'url(' + data.image.url + ')'\"\n      [sbPreloadImage]=\"data.image.url\"\n    >\n      <img src=\"{{ data.image.url }}\" />\n    </figure>\n  </div>\n</span>\n\n<span class=\"slide-element__sizer--figure\">\n  <div [style.font-size.em]=\"figureSize$ | async\">\n    <div *ngIf=\"data.icon\" class=\"icon-wrapper\" [ngStyle]=\"iconStyles$ | async\">\n      <i\n        *ngIf=\"data.icon.source == 'FontAwesome'\"\n        class=\"icon fa {{ data.icon.id }}\"\n      ></i>\n      <div\n        *ngIf=\"data.icon.source === 'NounProject'\"\n        [ngStyle]=\"svgIconStyles$ | async\"\n      >\n        <sb-noun-project-icon\n          class=\"icon\"\n          [iconId]=\"data.icon.id\"\n          [iconUrl]=\"data.icon.url\"\n          [color]=\"(iconStyles$ | async)['color']\"\n        ></sb-noun-project-icon>\n      </div>\n    </div>\n  </div>\n</span>\n\n<span\n  *ngIf=\"data.caption && data.caption.length\"\n  class=\"slide-element__sizer--text\"\n>\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <p\n      class=\"caption\"\n      [ngStyle]=\"captionStyles$ | async\"\n      [innerHTML]=\"data.caption | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></p>\n  </div>\n</span>\n" }]
        }] });

class UrbanCollectiveQuoteComponent extends BaseQuoteComponent {
    reloadStyles() {
        super.reloadStyles();
        const set = new ColorSet(this.colorSet);
        this.authorStyles$.next({
            'color': set.backColor,
            'background-color': set.textColor,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveQuoteComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: UrbanCollectiveQuoteComponent, selector: "sb-Uurban-collective-quote", usesInheritance: true, ngImport: i0, template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\">&ldquo;</span>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div *ngIf=\"data.author && data.author.length\" class=\"author\">\n      <span\n        [ngStyle]=\"authorStyles$ | async\"\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i2$1.SanitizeHtmlPipe, name: "sbSanitizeHtml" }, { kind: "pipe", type: i2$1.TrustHtmlPipe, name: "sbTrustHtml" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: UrbanCollectiveQuoteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-Uurban-collective-quote', template: "<span class=\"slide-element__sizer--text\">\n  <div [style.font-size.em]=\"textSize$ | async\">\n    <span class=\"quote-symbol\" [ngStyle]=\"symbolStyles$ | async\">&ldquo;</span>\n    <h4\n      [innerHTML]=\"data.quote | sbSanitizeHtml | sbTrustHtml\"\n      data-slide-element-export-text\n    ></h4>\n    <div *ngIf=\"data.author && data.author.length\" class=\"author\">\n      <span\n        [ngStyle]=\"authorStyles$ | async\"\n        [innerHTML]=\"data.author | sbSanitizeHtml | sbTrustHtml\"\n        data-slide-element-export-text\n      ></span>\n    </div>\n  </div>\n</span>\n" }]
        }] });

var SlideElementTypes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    AuroraBulletsComponent: AuroraBulletsComponent,
    AuroraCoverComponent: AuroraCoverComponent,
    AuroraHeadingComponent: AuroraHeadingComponent,
    AuroraImageComponent: AuroraImageComponent,
    AuroraNumberComponent: AuroraNumberComponent,
    AuroraQuoteComponent: AuroraQuoteComponent,
    BaseBackgroundComponent: BaseBackgroundComponent,
    BaseBarChartComponent: BaseBarChartComponent,
    BaseBulletsComponent: BaseBulletsComponent,
    BaseChartComponent: BaseChartComponent,
    BaseCodeComponent: BaseCodeComponent,
    BaseCoverComponent: BaseCoverComponent,
    BaseFooterComponent: BaseFooterComponent,
    BaseHeadingComponent: BaseHeadingComponent,
    BaseImageComponent: BaseImageComponent,
    BaseLineChartComponent: BaseLineChartComponent,
    BaseNumberComponent: BaseNumberComponent,
    BaseParagraphComponent: BaseParagraphComponent,
    BasePieChartComponent: BasePieChartComponent,
    BaseQuoteComponent: BaseQuoteComponent,
    BaseTableComponent: BaseTableComponent,
    BaseVideoComponent: BaseVideoComponent,
    BlueprintBulletsComponent: BlueprintBulletsComponent,
    BlueprintImageComponent: BlueprintImageComponent,
    BlueprintNumberComponent: BlueprintNumberComponent,
    BlueprintQuoteComponent: BlueprintQuoteComponent,
    BoulevardBulletsComponent: BoulevardBulletsComponent,
    BoulevardCoverComponent: BoulevardCoverComponent,
    BoulevardHeadingComponent: BoulevardHeadingComponent,
    BoulevardImageComponent: BoulevardImageComponent,
    BoulevardQuoteComponent: BoulevardQuoteComponent,
    EYBulletsComponent: EYBulletsComponent,
    EYCoverComponent: EYCoverComponent,
    EYHeadingComponent: EYHeadingComponent,
    EYImageComponent: EYImageComponent,
    EYQuoteComponent: EYQuoteComponent,
    FiveOclockBulletsComponent: FiveOclockBulletsComponent,
    FiveOclockCoverComponent: FiveOclockCoverComponent,
    FiveOclockHeadingComponent: FiveOclockHeadingComponent,
    FiveOclockImageComponent: FiveOclockImageComponent,
    FiveOclockNumberComponent: FiveOclockNumberComponent,
    FiveOclockParagraphComponent: FiveOclockParagraphComponent,
    FiveOclockQuoteComponent: FiveOclockQuoteComponent,
    FiveOclockTableComponent: FiveOclockTableComponent,
    GusCoverComponent: GusCoverComponent,
    GusImageComponent: GusImageComponent,
    GusQuoteComponent: GusQuoteComponent,
    PolygonBulletsComponent: PolygonBulletsComponent,
    PolygonCoverComponent: PolygonCoverComponent,
    PolygonHeadingComponent: PolygonHeadingComponent,
    PolygonImageComponent: PolygonImageComponent,
    PolygonNumberComponent: PolygonNumberComponent,
    PolygonParagraphComponent: PolygonParagraphComponent,
    PolygonQuoteComponent: PolygonQuoteComponent,
    RaptureCoverComponent: RaptureCoverComponent,
    RaptureImageComponent: RaptureImageComponent,
    RaptureQuoteComponent: RaptureQuoteComponent,
    ScalaBulletsComponent: ScalaBulletsComponent,
    ScalaCoverComponent: ScalaCoverComponent,
    ScalaFooterComponent: ScalaFooterComponent,
    ScalaHeadingComponent: ScalaHeadingComponent,
    ScalaImageComponent: ScalaImageComponent,
    ScalaNumberComponent: ScalaNumberComponent,
    ScalaQuoteComponent: ScalaQuoteComponent,
    StairwayBulletsComponent: StairwayBulletsComponent,
    StairwayCoverComponent: StairwayCoverComponent,
    StairwayHeadingComponent: StairwayHeadingComponent,
    StairwayImageComponent: StairwayImageComponent,
    StairwayNumberComponent: StairwayNumberComponent,
    StairwayQuoteComponent: StairwayQuoteComponent,
    UrbanCollectiveBulletsComponent: UrbanCollectiveBulletsComponent,
    UrbanCollectiveCoverComponent: UrbanCollectiveCoverComponent,
    UrbanCollectiveHeadingComponent: UrbanCollectiveHeadingComponent,
    UrbanCollectiveImageComponent: UrbanCollectiveImageComponent,
    UrbanCollectiveQuoteComponent: UrbanCollectiveQuoteComponent
});

let SlideElementHostComponent$1 = class SlideElementHostComponent extends HostComponent {
    constructor(resolver, changeDetector, domSanitizer) {
        super(resolver, changeDetector);
        this.domSanitizer = domSanitizer;
        this.isActive = false;
        this.hiRes = false;
        this.editMode = false;
        this.onActivationEnded = new EventEmitter();
        this.destroy$ = new Subject();
        this.reloadPosition$ = new Subject();
    }
    getHostedComponentType() {
        const elementName = {
            'background': 'Background',
            'cover': 'Cover',
            'heading': 'Heading',
            'footer': 'Footer',
            'paragraph': 'Paragraph',
            'bullets': 'Paragraph',
            'chart-bar': 'BarChart',
            'chart-line': 'LineChart',
            'chart-pie': 'PieChart',
            'code': 'Code',
            'image': 'Image',
            'number': 'Number',
            'quote': 'Quote',
            'table': 'Table',
            'video': 'Video',
        }[this.element.type];
        const layoutName = this.layoutId
            ? {
                'aurora': 'Aurora',
                'blueprint': 'Blueprint',
                'boulevard': 'Boulevard',
                'fiveoclock': 'FiveOclock',
                'polygon': 'Polygon',
                'rapture': 'Rapture',
                'scala': 'Scala',
                'stairway': 'Stairway',
                'ey': 'EY',
                'gus': 'Gus',
                'urban-collective': 'UrbanCollective',
            }[this.layoutId]
            : null;
        const typeName = layoutName ? `${layoutName}${elementName}Component` : null;
        const defaultTypeName = `Base${elementName}Component`;
        return ((typeName && SlideElementTypes[typeName]) ||
            SlideElementTypes[defaultTypeName]);
    }
    wireHostedComponent(componentRef) {
        const component = componentRef
            .instance;
        component.slide = this.slide;
        component.slideElements = this.slideElements;
        component.slideOptions = this.slideOptions;
        component.element = this.element;
        component.elementData = this.elementData;
        component.elementOptions = this.elementOptions;
        component.colorSet = this.colorSet;
        component.textFont = this.textFont;
        component.headingFont = this.headingFont;
        component.isActive = this.isActive;
        component.hiRes = this.hiRes;
        component.editMode = this.editMode;
        if (this.onActivationEndedSubscription) {
            this.onActivationEndedSubscription.unsubscribe();
        }
        this.onActivationEndedSubscription = component.onActivationEnded.subscribe(event => {
            this.onActivationEnded.emit(event);
        });
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        if (didChange(changes['elementOptions'])) {
            this.reloadPosition$.next();
        }
    }
    ngOnInit() {
        super.ngOnInit();
        this.reloadPosition$
            .pipe(takeUntil(this.destroy$))
            .subscribe(this.reloadPosition.bind(this));
        this.reloadPosition();
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this.destroy$.next();
        this.destroy$.complete();
    }
    reloadPosition() {
        let xRatio = 0;
        let yRatio = 0;
        if (this.element) {
            xRatio = getElementOptionValue(this.element)('xRatio');
            yRatio = getElementOptionValue(this.element)('yRatio');
        }
        if (xRatio !== 0 || yRatio !== 0) {
            this.elementTransform = this.domSanitizer.bypassSecurityTrustStyle(`translate(${xRatio}%,${yRatio}%)`);
        }
        else {
            this.elementTransform = null;
        }
    }
    activate() {
        if (this.componentRef) {
            this.componentRef.instance.activate();
        }
    }
    deactivate() {
        if (this.componentRef) {
            this.componentRef.instance.deactivate();
        }
    }
    isElementDataEmpty() {
        return isElementDataEmpty(this.element);
    }
    // Deprecated. Editor stuff
    tempElementTransform() {
        return this.element['transform'];
    }
    tempElementTransformDuration() {
        return this.element['transformDuration'] || 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementHostComponent, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ChangeDetectorRef }, { token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideElementHostComponent, selector: "sb-slide-element-host", inputs: { slide: "slide", slideElements: "slideElements", slideOptions: "slideOptions", element: "element", elementData: "elementData", elementOptions: "elementOptions", layoutId: "layoutId", colorSet: "colorSet", textFont: "textFont", headingFont: "headingFont", isActive: "isActive", hiRes: "hiRes", editMode: "editMode" }, outputs: { onActivationEnded: "onActivationEnded" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"slide-element-transformer\" [style.transform]=\"elementTransform\">\n  <div\n    class=\"slide-element-editor-transformer\"\n    [style.transform]=\"tempElementTransform()\"\n    [style.transition]=\"\n      tempElementTransformDuration()\n        ? 'transform ' + tempElementTransformDuration() + 'ms'\n        : null\n    \"\n  >\n    <div\n      class=\"slide-element-wrapper\"\n      [class.slide-element-sample]=\"isElementDataEmpty()\"\n    >\n      <span #host></span>\n    </div>\n  </div>\n</div>\n" }); }
};
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementHostComponent$1, decorators: [{
            type: Component,
            args: [{ selector: 'sb-slide-element-host', template: "<div class=\"slide-element-transformer\" [style.transform]=\"elementTransform\">\n  <div\n    class=\"slide-element-editor-transformer\"\n    [style.transform]=\"tempElementTransform()\"\n    [style.transition]=\"\n      tempElementTransformDuration()\n        ? 'transform ' + tempElementTransformDuration() + 'ms'\n        : null\n    \"\n  >\n    <div\n      class=\"slide-element-wrapper\"\n      [class.slide-element-sample]=\"isElementDataEmpty()\"\n    >\n      <span #host></span>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.ChangeDetectorRef }, { type: i1$2.DomSanitizer }], propDecorators: { slide: [{
                type: Input
            }], slideElements: [{
                type: Input
            }], slideOptions: [{
                type: Input
            }], element: [{
                type: Input
            }], elementData: [{
                type: Input
            }], elementOptions: [{
                type: Input
            }], layoutId: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], isActive: [{
                type: Input
            }], hiRes: [{
                type: Input
            }], editMode: [{
                type: Input
            }], onActivationEnded: [{
                type: Output
            }] } });

function getElementGroups(elementCount, layoutType = 'a') {
    const data = {
        '1': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 1,
                    elementStyles: [
                        {
                            width: '100%',
                            chartSize: 40,
                        },
                    ],
                },
            ],
        },
        '2': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 2,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 90,
                            figureSize: 90,
                            chartSize: 60,
                        },
                        {
                            width: '50%',
                            textSize: 90,
                            figureSize: 90,
                            chartSize: 60,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 2,
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 90,
                            figureSize: 50,
                            chartSize: 20,
                        },
                        {
                            width: '100%',
                            textSize: 90,
                            figureSize: 50,
                            chartSize: 20,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 2,
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '65%',
                            textSize: 90,
                            figureSize: 100,
                            chartSize: 50,
                        },
                        {
                            width: '35%',
                            textSize: 90,
                            figureSize: 70,
                            chartSize: 60,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 2,
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '35%',
                            textSize: 90,
                            figureSize: 70,
                            chartSize: 60,
                        },
                        {
                            width: '65%',
                            textSize: 90,
                            figureSize: 100,
                            chartSize: 50,
                        },
                    ],
                },
            ],
        },
        '3': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 3,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '33%',
                            textSize: 80,
                            figureSize: 80,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 80,
                            figureSize: 80,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 80,
                            figureSize: 80,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 3,
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 30,
                            chartSize: 10,
                        },
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 30,
                            chartSize: 10,
                        },
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 30,
                            chartSize: 10,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 3,
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 60,
                            chartSize: 15,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 1,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
                {
                    startIndex: 1,
                    endIndex: 3,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 50,
                            chartSize: 30,
                        },
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 50,
                            chartSize: 30,
                        },
                    ],
                },
            ],
            'e': [
                {
                    startIndex: 0,
                    endIndex: 2,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 50,
                            chartSize: 30,
                        },
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 50,
                            chartSize: 30,
                        },
                    ],
                },
                {
                    startIndex: 2,
                    endIndex: 3,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
            ],
        },
        '4': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 4,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 75,
                            figureSize: 75,
                            chartSize: 80,
                        },
                        {
                            width: '50%',
                            textSize: 75,
                            figureSize: 75,
                            chartSize: 80,
                        },
                        {
                            width: '50%',
                            textSize: 75,
                            figureSize: 75,
                            chartSize: 80,
                        },
                        {
                            width: '50%',
                            textSize: 75,
                            figureSize: 75,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 4,
                    elementStyles: [
                        {
                            width: '25%',
                            textSize: 75,
                            figureSize: 50,
                            chartSize: 70,
                        },
                        {
                            width: '25%',
                            textSize: 75,
                            figureSize: 50,
                            chartSize: 70,
                        },
                        {
                            width: '25%',
                            textSize: 75,
                            figureSize: 50,
                            chartSize: 70,
                        },
                        {
                            width: '25%',
                            textSize: 75,
                            figureSize: 50,
                            chartSize: 70,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 4,
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 90,
                            figureSize: 50,
                            chartSize: 15,
                        },
                        {
                            width: '33%',
                            textSize: 75,
                            figureSize: 35,
                            chartSize: 50,
                        },
                        {
                            width: '33%',
                            textSize: 75,
                            figureSize: 35,
                            chartSize: 50,
                        },
                        {
                            width: '33%',
                            textSize: 75,
                            figureSize: 35,
                            chartSize: 50,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 1,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
                {
                    startIndex: 1,
                    endIndex: 4,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                    ],
                },
            ],
            'e': [
                {
                    startIndex: 0,
                    endIndex: 3,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                    ],
                },
                {
                    startIndex: 3,
                    endIndex: 4,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
            ],
        },
        '5': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 5,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 5,
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 90,
                            figureSize: 65,
                            chartSize: 15,
                        },
                        {
                            width: '25%',
                            textSize: 50,
                            figureSize: 35,
                            chartSize: 50,
                        },
                        {
                            width: '25%',
                            textSize: 50,
                            figureSize: 35,
                            chartSize: 50,
                        },
                        {
                            width: '25%',
                            textSize: 50,
                            figureSize: 35,
                            chartSize: 50,
                        },
                        {
                            width: '25%',
                            textSize: 50,
                            figureSize: 35,
                            chartSize: 50,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 5,
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 90,
                            figureSize: 50,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 90,
                            figureSize: 50,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 75,
                            figureSize: 35,
                            chartSize: 40,
                        },
                        {
                            width: '33%',
                            textSize: 75,
                            figureSize: 35,
                            chartSize: 40,
                        },
                        {
                            width: '33%',
                            textSize: 75,
                            figureSize: 35,
                            chartSize: 40,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 1,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
                {
                    startIndex: 1,
                    endIndex: 5,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                    ],
                },
            ],
            'e': [
                {
                    startIndex: 0,
                    endIndex: 4,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                        {
                            width: '50%',
                            textSize: 70,
                            figureSize: 40,
                            chartSize: 40,
                        },
                    ],
                },
                {
                    startIndex: 4,
                    endIndex: 5,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
            ],
        },
        '6': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 6,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                        {
                            width: '33%',
                            textSize: 65,
                            figureSize: 65,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 6,
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 25,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 25,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 25,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 25,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 25,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 25,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 3,
                    width: '50%',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 50,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 50,
                        },
                    ],
                },
                {
                    startIndex: 3,
                    endIndex: 6,
                    width: '50%',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 80,
                            figureSize: 50,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 65,
                            figureSize: 30,
                            chartSize: 50,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 1,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
                {
                    startIndex: 1,
                    endIndex: 6,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 65,
                            figureSize: 40,
                            chartSize: 15,
                        },
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
            ],
        },
        '7': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 7,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 3,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                        {
                            width: '100%',
                            textSize: 70,
                            figureSize: 25,
                            chartSize: 20,
                        },
                    ],
                },
                {
                    startIndex: 3,
                    endIndex: 7,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 50,
                            figureSize: 50,
                            chartSize: 50,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 1,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 100,
                            figureSize: 120,
                            chartSize: 70,
                        },
                    ],
                },
                {
                    startIndex: 1,
                    endIndex: 7,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 7,
                    elementStyles: [
                        {
                            width: '33%',
                            textSize: 60,
                            figureSize: 50,
                            chartSize: 60,
                        },
                        {
                            width: '33%',
                            textSize: 60,
                            figureSize: 50,
                            chartSize: 60,
                        },
                        {
                            width: '33%',
                            textSize: 60,
                            figureSize: 50,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 60,
                            figureSize: 35,
                            chartSize: 70,
                        },
                        {
                            width: '25%',
                            textSize: 60,
                            figureSize: 35,
                            chartSize: 70,
                        },
                        {
                            width: '25%',
                            textSize: 60,
                            figureSize: 35,
                            chartSize: 70,
                        },
                        {
                            width: '25%',
                            textSize: 60,
                            figureSize: 35,
                            chartSize: 70,
                        },
                    ],
                },
            ],
        },
        '8': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 8,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 55,
                            figureSize: 55,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 8,
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 20,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 4,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                    ],
                },
                {
                    startIndex: 4,
                    endIndex: 8,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 4,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                        {
                            width: '50%',
                            textSize: 55,
                            figureSize: 50,
                            chartSize: 50,
                        },
                    ],
                },
                {
                    startIndex: 4,
                    endIndex: 8,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                    ],
                },
            ],
        },
        '9': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 9,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 9,
                    elementStyles: [
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 9,
                    elementStyles: [
                        {
                            width: '25%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 15,
                        },
                        {
                            width: '25%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '25%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 15,
                        },
                        {
                            width: '25%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '25%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 15,
                        },
                        {
                            width: '25%',
                            textSize: 45,
                            figureSize: 30,
                            chartSize: 30,
                        },
                    ],
                },
            ],
        },
        '10': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 10,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                        {
                            width: '20%',
                            textSize: 45,
                            figureSize: 45,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 5,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 20,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
                {
                    startIndex: 5,
                    endIndex: 10,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 15,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
            ],
            'c': [
                {
                    startIndex: 0,
                    endIndex: 6,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
                {
                    startIndex: 6,
                    endIndex: 10,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                    ],
                },
            ],
            'd': [
                {
                    startIndex: 0,
                    endIndex: 4,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                        {
                            width: '100%',
                            textSize: 55,
                            figureSize: 20,
                            chartSize: 15,
                        },
                    ],
                },
                {
                    startIndex: 4,
                    endIndex: 10,
                    width: '50%',
                    alignItems: 'center',
                    elementStyles: [
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '50%',
                            textSize: 45,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
            ],
        },
        '11': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 11,
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    elementStyles: [
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 11,
                    justifyContent: 'flex-start',
                    elementStyles: [
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
            ],
        },
        '12': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 12,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                        {
                            width: '25%',
                            textSize: 40,
                            figureSize: 40,
                            chartSize: 80,
                        },
                    ],
                },
            ],
            'b': [
                {
                    startIndex: 0,
                    endIndex: 12,
                    elementStyles: [
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                        {
                            width: '33%',
                            textSize: 40,
                            figureSize: 25,
                            chartSize: 30,
                        },
                    ],
                },
            ],
        },
        '13': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 13,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
        '14': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 14,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
        '15': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 15,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 30,
                            figureSize: 25,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
        '16': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 16,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                        {
                            width: '25%',
                            textSize: 25,
                            figureSize: 25,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
        '17': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 17,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
        '18': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 18,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
        '19': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 19,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
        '20': {
            'a': [
                {
                    startIndex: 0,
                    endIndex: 20,
                    alignItems: 'flex-start',
                    elementStyles: [
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                        {
                            width: '20%',
                            textSize: 15,
                            figureSize: 15,
                            chartSize: 60,
                        },
                    ],
                },
            ],
        },
    };
    return (data[elementCount] || { 'a': [] })[layoutType] || [];
}

const isSameElement = (left, right) => left === right ||
    (left.id === right.id &&
        isEqual(left.data, right.data) &&
        isEqual(left.options, right.options) &&
        isEqual(left.style, right.style));
const isDifferentArray = (a, b) => a.length !== b.length ||
    !a.every((element, i) => isSameElement(element, b[i]));
let SlideComponent$1 = class SlideComponent {
    get slideId() {
        return this.slide ? this.slide.id : void 0;
    }
    constructor(ngZone, changeDetector) {
        this.ngZone = ngZone;
        this.changeDetector = changeDetector;
        this.isCoverSlide = false;
        this.colorSetOffset = 0;
        this.currentStep = 0;
        this.isActive = false;
        this.hiRes = false;
        this.editMode = false;
        this.onSlideEnded = new EventEmitter();
        this.reloadElements$ = new Subject();
        this.reloadStyles$ = new Subject();
        this.reloadBodyElementGroups$ = new Subject();
        this.backgroundElements$ = new BehaviorSubject([]);
        this.headerElements$ = new BehaviorSubject([]);
        this.bodyElements$ = new BehaviorSubject([]);
        this.footerElements$ = new BehaviorSubject([]);
        this.elementIndexes = new Map();
        this.bodyElementGroups$ = new BehaviorSubject([]);
        this.bodyAlignItems$ = new BehaviorSubject(null);
        this.colorSet$ = new BehaviorSubject({});
        this.slideStyles$ = new BehaviorSubject({});
        this.backgroundStyles$ = new BehaviorSubject({});
        this.backgroundClasses$ = new BehaviorSubject({});
        this.logoPosition$ = new BehaviorSubject('off');
        this.logoUrl$ = new BehaviorSubject(null);
    }
    ngOnChanges(changes) {
        if (didChange(changes['layout']) ||
            didChange(changes['palette']) ||
            didChange(changes['colorSetOffset']) ||
            didChange(changes['logoLightBg']) ||
            didChange(changes['logoDarkBg']) ||
            didChange(changes['slideElements']) ||
            didChange(changes['slideOptions'])) {
            this.reloadStyles$.next();
        }
        if (didChange(changes['slideElements'])) {
            this.reloadElements$.next();
        }
        if (didChange(changes['slideOptions'])) {
            this.reloadBodyElementGroups$.next();
        }
    }
    ngOnInit() {
        this.reloadElements$
            .pipe(debounceTime(10))
            .subscribe(() => this.reloadElementArrays());
        this.reloadStyles$
            .pipe(debounceTime(10))
            .subscribe(() => this.reloadStyles());
        this.reloadBodyElementGroups$
            .pipe(debounceTime(10))
            .subscribe(() => this.reloadBodyElementGroups());
        // Trigger initial load
        this.reloadElementArrays();
        this.reloadBodyElementGroups();
        this.reloadStyles();
    }
    reloadElementArrays() {
        if (!this.slide) {
            return;
        }
        this.ngZone.runOutsideAngular(() => {
            const backgroundElements = [];
            const headerElements = [];
            const bodyElements = [];
            const footerElements = [];
            this.slide.elements.forEach((element) => {
                switch (element.type) {
                    case 'background':
                    case 'video':
                        backgroundElements.push(element);
                        break;
                    case 'cover':
                    case 'heading':
                        headerElements.push(element);
                        break;
                    case 'footer':
                        footerElements.push(element);
                        break;
                    default:
                        bodyElements.push(element);
                        break;
                }
            });
            let changes = false;
            if (isDifferentArray(this.backgroundElements$.getValue(), backgroundElements)) {
                this.backgroundElements$.next(backgroundElements);
                changes = true;
            }
            if (isDifferentArray(this.headerElements$.getValue(), headerElements)) {
                this.headerElements$.next(headerElements);
                changes = true;
            }
            if (isDifferentArray(this.bodyElements$.getValue(), bodyElements)) {
                this.bodyElements$.next(bodyElements);
                changes = true;
            }
            if (isDifferentArray(this.footerElements$.getValue(), footerElements)) {
                this.footerElements$.next(footerElements);
                changes = true;
            }
            if (changes) {
                this.elementIndexes.clear();
                [
                    ...this.backgroundElements$.getValue(),
                    ...this.headerElements$.getValue(),
                    ...this.bodyElements$.getValue(),
                    ...this.footerElements$.getValue(),
                ].forEach(element => {
                    this.elementIndexes.set(element, this.slide.elements.indexOf(element));
                });
                this.reloadBodyElementGroups();
                this.ngZone.run(() => this.changeDetector.detectChanges());
            }
        });
    }
    reloadColorSet() {
        // Calculate based on colorSetOffset and the slide's colorSet option
        let colorIndex = this.colorSetOffset;
        if (this.slide) {
            const colorSetOption = getSlideOptionValue(this.slide)('colorSet');
            if (/^[0-9]+$/.test(colorSetOption)) {
                colorIndex = +colorSetOption;
            }
        }
        if (this.palette) {
            this.colorSet$.next(this.palette.colorSets[Math.abs(colorIndex) % this.palette.colorSets.length]);
        }
        // Change the text and back colors to white and black
        if (this.slide &&
            hasBackgroundImage(this.slide) &&
            getSlideOptionValue(this.slide)('backgroundColorSet') !== true &&
            this.colorSet$.getValue()) {
            this.colorSet$.next({
                ...this.colorSet$.getValue(),
                back: '#000',
                text: '#FFF',
            });
        }
    }
    reloadStyles() {
        this.reloadColorSet();
        const colorSet = this.colorSet$.getValue();
        const set = new ColorSet(colorSet);
        this.slideStyles$.next({
            'color': set.textColor,
            'background-color': set.backColor,
        });
        const logoPosition = getSlideOptionValue(this.slide)('logoPosition');
        this.logoPosition$.next(logoPosition);
        if (logoPosition !== 'off') {
            this.logoUrl$.next(new Color(set.backColor).isLight()
                ? this.logoLightBg || this.logoDarkBg
                : this.logoDarkBg || this.logoLightBg);
        }
        else {
            this.logoUrl$.next(null);
        }
    }
    reloadBodyElementGroups() {
        const bodyElements = this.bodyElements$.getValue();
        if (this.slide && bodyElements && bodyElements.length) {
            const bodyElementCount = bodyElements.length;
            const layoutType = getSlideOptionValue(this.slide)(`body-layout_${bodyElementCount}`);
            this.bodyElementGroups$.next(getElementGroups(bodyElementCount, layoutType).map(group => Object.assign(group, {
                elements: bodyElements.slice(group.startIndex, group.endIndex),
            })));
            this.bodyAlignItems$.next({
                'top': 'flex-start',
                'center': 'center',
                'bottom': 'flex-end',
            }[getSlideOptionValue(this.slide)(`body-vertical-alignment_${bodyElementCount}`)] || null);
        }
        else {
            this.bodyElementGroups$.next([]);
            this.bodyAlignItems$.next(null);
        }
    }
    trackSlideElementById(index, slideElement) {
        return slideElement.id;
    }
    activate() {
        this.slideElementComponents.forEach(component => {
            if (component.isActive) {
                component.activate();
            }
        });
    }
    deactivate() {
        this.slideElementComponents.forEach(component => component.deactivate());
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onElementActivationEnded(element) {
        if (this.slide.elements.length <= 1) {
            this.onSlideEnded.emit(this);
        }
    }
    getSlideOptionValue(option) {
        return getSlideOptionValue(this.slide)(option);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideComponent, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideComponent, selector: "sb-slide", inputs: { slide: "slide", slideElements: "slideElements", slideOptions: "slideOptions", isCoverSlide: "isCoverSlide", layoutId: "layoutId", palette: "palette", colorSetOffset: "colorSetOffset", textFont: "textFont", headingFont: "headingFont", logoLightBg: "logoLightBg", logoDarkBg: "logoDarkBg", currentStep: "currentStep", isActive: "isActive", hiRes: "hiRes", editMode: "editMode" }, outputs: { onSlideEnded: "onSlideEnded" }, host: { properties: { "attr.data-sb-slide-id": "this.slideId" } }, viewQueries: [{ propertyName: "slideElementComponents", predicate: SlideElementHostComponent$1, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "component", type: SlideElementHostComponent$1, selector: "sb-slide-element-host", inputs: ["slide", "slideElements", "slideOptions", "element", "elementData", "elementOptions", "layoutId", "colorSet", "textFont", "headingFont", "isActive", "hiRes", "editMode"], outputs: ["onActivationEnded"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
};
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideComponent$1, decorators: [{
            type: Component,
            args: [{ selector: 'sb-slide', template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }], propDecorators: { slide: [{
                type: Input
            }], slideElements: [{
                type: Input
            }], slideOptions: [{
                type: Input
            }], isCoverSlide: [{
                type: Input
            }], layoutId: [{
                type: Input
            }], palette: [{
                type: Input
            }], colorSetOffset: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], logoLightBg: [{
                type: Input
            }], logoDarkBg: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], isActive: [{
                type: Input
            }], hiRes: [{
                type: Input
            }], editMode: [{
                type: Input
            }], onSlideEnded: [{
                type: Output
            }], slideElementComponents: [{
                type: ViewChildren,
                args: [SlideElementHostComponent$1]
            }], slideId: [{
                type: HostBinding,
                args: ['attr.data-sb-slide-id']
            }] } });

class RaptureSlideComponent extends SlideComponent$1 {
    reloadStyles() {
        super.reloadStyles();
        const colorSet = this.colorSet$.getValue();
        const set = new ColorSet(colorSet);
        let colorInner = new Color(set.backColor);
        let colorOuter = colorInner.darkenOrLighten(20);
        if (colorInner.isDarkerThan(colorOuter)) {
            const temp = colorInner;
            colorInner = colorOuter;
            colorOuter = temp;
        }
        this.slideStyles$.next(Object.assign({}, this.slideStyles$.getValue(), {
            'background-color': null,
        }));
        this.backgroundStyles$.next(Object.assign({}, this.backgroundStyles$.getValue(), {
            'background-color': colorOuter.val(),
            'background': `radial-gradient(${colorInner.val()},${colorOuter.val()})`,
        }));
        /*this.backgroundStyles['background'] = [
          "-webkit-radial-gradient(" + colorInner.val() + "," + colorOuter.val() + ")",
             "-moz-radial-gradient(" + colorInner.val() + "," + colorOuter.val() + ")",
              "-ms-radial-gradient(" + colorInner.val() + "," + colorOuter.val() + ")",
               "-o-radial-gradient(" + colorInner.val() + "," + colorOuter.val() + ")",
                  "radial-gradient(" + colorInner.val() + "," + colorOuter.val() + ")"
        ];*/
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureSlideComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: RaptureSlideComponent, selector: "sb-rapture-slide", usesInheritance: true, ngImport: i0, template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "component", type: SlideElementHostComponent$1, selector: "sb-slide-element-host", inputs: ["slide", "slideElements", "slideOptions", "element", "elementData", "elementOptions", "layoutId", "colorSet", "textFont", "headingFont", "isActive", "hiRes", "editMode"], outputs: ["onActivationEnded"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RaptureSlideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-rapture-slide', template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n" }]
        }] });

class BlueprintSlideComponent extends SlideComponent$1 {
    reloadStyles() {
        super.reloadStyles();
        const colorSet = this.colorSet$.getValue();
        const set = new ColorSet(colorSet);
        const backColor = new Color(set.backColor);
        let colorRight = new Color(set.backColor);
        let colorLeft = colorRight.darkenOrLighten(15);
        if (colorLeft.isDarkerThan(colorRight)) {
            const temp = colorRight;
            colorRight = colorLeft;
            colorLeft = temp;
        }
        this.slideStyles$.next(Object.assign({}, this.slideStyles$.getValue(), {
            'background-color': backColor.val(),
            'background': `linear-gradient(to bottom right, ${colorLeft.val()}, ${colorRight.val()})`,
        }));
        /*this.slideStyles["background"] = [
          "-webkit-linear-gradient(left top, "      + colorLeft.val() + "," + colorRight.val() + ")",
             "-moz-linear-gradient(bottom right, "  + colorLeft.val() + "," + colorRight.val() + ")",
               "-o-linear-gradient(bottom right"    + colorLeft.val() + "," + colorRight.val() + ")",
                  "linear-gradient(to bottom right" + colorLeft.val() + "," + colorRight.val() + ")"
        ];*/
        const isDarkBackground = backColor.isDark();
        this.backgroundClasses$.next(Object.assign({}, this.backgroundClasses$.getValue(), {
            dark: isDarkBackground,
            light: !isDarkBackground,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintSlideComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: BlueprintSlideComponent, selector: "sb-blueprint-slide", usesInheritance: true, ngImport: i0, template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "component", type: SlideElementHostComponent$1, selector: "sb-slide-element-host", inputs: ["slide", "slideElements", "slideOptions", "element", "elementData", "elementOptions", "layoutId", "colorSet", "textFont", "headingFont", "isActive", "hiRes", "editMode"], outputs: ["onActivationEnded"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: BlueprintSlideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-blueprint-slide', template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n" }]
        }] });

class PolygonSlideComponent extends SlideComponent$1 {
    reloadStyles() {
        super.reloadStyles();
        const colorSet = this.colorSet$.getValue();
        const set = new ColorSet(colorSet);
        this.slideStyles$.next(Object.assign({}, this.slideStyles$.getValue(), {
            'background-color': null,
        }));
        this.backgroundStyles$.next(Object.assign({}, this.backgroundStyles$.getValue(), {
            'background-color': set.backColor,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonSlideComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: PolygonSlideComponent, selector: "sb-polygon-slide", usesInheritance: true, ngImport: i0, template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "component", type: SlideElementHostComponent$1, selector: "sb-slide-element-host", inputs: ["slide", "slideElements", "slideOptions", "element", "elementData", "elementOptions", "layoutId", "colorSet", "textFont", "headingFont", "isActive", "hiRes", "editMode"], outputs: ["onActivationEnded"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: PolygonSlideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-polygon-slide', template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n" }]
        }] });

class GusSlideComponent extends SlideComponent$1 {
    reloadStyles() {
        super.reloadStyles();
        const colorSet = this.colorSet$.getValue();
        const set = new ColorSet(colorSet);
        let back = new Color(set.backColor);
        let back2 = colorSet['back2']
            ? new Color(colorSet['back2'])
            : back.darkenOrLighten(20);
        if (back.isLighterThan(back2)) {
            const temp = back;
            back = back2;
            back2 = temp;
        }
        this.slideStyles$.next(Object.assign({}, this.slideStyles$.getValue(), {
            'background-color': null,
        }));
        this.backgroundStyles$.next(Object.assign({}, this.backgroundStyles$.getValue(), {
            'background-color': back.val(),
            'background': `radial-gradient(circle at top right, ${back2.val()} 0%, ${back.val()} 85%, ${back.val()} 100%)`,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusSlideComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: GusSlideComponent, selector: "sb-gus-slide", usesInheritance: true, ngImport: i0, template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "component", type: SlideElementHostComponent$1, selector: "sb-slide-element-host", inputs: ["slide", "slideElements", "slideOptions", "element", "elementData", "elementOptions", "layoutId", "colorSet", "textFont", "headingFont", "isActive", "hiRes", "editMode"], outputs: ["onActivationEnded"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: GusSlideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-gus-slide', template: "<div class=\"slide-wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <div\n    class=\"slide-background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Background images, Videos -->\n  <div\n    *ngIf=\"(backgroundElements$ | async).length\"\n    class=\"slide-element-container slide-element-container-background\"\n  >\n    <sb-slide-element-host\n      *ngFor=\"let element of backgroundElements$ | async\"\n      id=\"sb_{{ element.id }}\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-element-index]=\"elementIndexes.get(element)\"\n      [slide]=\"slide\"\n      [slideElements]=\"slide.elements\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [layoutId]=\"layoutId\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [hiRes]=\"hiRes\"\n      [editMode]=\"editMode\"\n      [isActive]=\"isActive\"\n      [class.active]=\"isActive\"\n      (onActivationEnded)=\"onElementActivationEnded($event)\"\n    ></sb-slide-element-host>\n  </div>\n\n  <div\n    *ngIf=\"\n      (headerElements$ | async).length ||\n      (bodyElements$ | async).length ||\n      (footerElements$ | async).length\n    \"\n    class=\"slide-content-wrapper\"\n  >\n    <!-- Logo -->\n    <figure\n      *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n      class=\"logo {{ logoPosition$ | async }}\"\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n\n    <!-- Header -->\n    <div\n      *ngIf=\"(headerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-header\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of headerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </div>\n\n    <!-- Body -->\n    <div\n      *ngIf=\"(bodyElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-body\"\n      [attr.data-element-count]=\"(bodyElements$ | async).length\"\n      [class.animated]=\"!isCoverSlide && getSlideOptionValue('animate')\"\n      [class.multiple-elements]=\"(bodyElements$ | async).length > 1\"\n    >\n      <div\n        *ngFor=\"let group of bodyElementGroups$ | async; let groupIndex = index\"\n        class=\"slide-element-group\"\n        [style.width]=\"group.width || '100%'\"\n        [style.align-items]=\"\n          (bodyAlignItems$ | async) || group.alignItems || 'center'\n        \"\n        [style.justify-content]=\"group.justifyContent || 'center'\"\n      >\n        <sb-slide-element-host\n          *ngFor=\"let element of group.elements; let elementIndex = index\"\n          class=\"text-size--{{\n            group.elementStyles[elementIndex]?.textSize || '100'\n          }} figure-size--{{\n            group.elementStyles[elementIndex]?.figureSize || '100'\n          }} chart-size--{{\n            group.elementStyles[elementIndex]?.chartSize || '60'\n          }}\"\n          [style.width]=\"group.elementStyles[elementIndex]?.width || '100%'\"\n          id=\"sb_{{ element.id }}\"\n          [attr.data-element-type]=\"element.type\"\n          [attr.data-element-index]=\"elementIndexes.get(element)\"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [element]=\"element\"\n          [elementData]=\"element.data\"\n          [elementOptions]=\"element.options\"\n          [layoutId]=\"layoutId\"\n          [colorSet]=\"colorSet$ | async\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [isActive]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          [class.active]=\"\n            isActive &&\n            (isCoverSlide ||\n              currentStep >= elementIndex + group.startIndex ||\n              !getSlideOptionValue('animate'))\n          \"\n          (onActivationEnded)=\"onElementActivationEnded($event)\"\n        ></sb-slide-element-host>\n      </div>\n    </div>\n\n    <!-- Footer -->\n    <span\n      *ngIf=\"(footerElements$ | async).length\"\n      class=\"slide-element-container slide-element-container-footer\"\n    >\n      <sb-slide-element-host\n        *ngFor=\"let element of footerElements$ | async\"\n        id=\"sb_{{ element.id }}\"\n        [attr.data-element-type]=\"element.type\"\n        [attr.data-element-index]=\"elementIndexes.get(element)\"\n        [slide]=\"slide\"\n        [slideElements]=\"slide.elements\"\n        [slideOptions]=\"slide.options\"\n        [element]=\"element\"\n        [elementData]=\"element.data\"\n        [elementOptions]=\"element.options\"\n        [layoutId]=\"layoutId\"\n        [colorSet]=\"colorSet$ | async\"\n        [textFont]=\"textFont\"\n        [headingFont]=\"headingFont\"\n        [hiRes]=\"hiRes\"\n        [editMode]=\"editMode\"\n        [isActive]=\"isActive\"\n        [class.active]=\"isActive\"\n        (onActivationEnded)=\"onElementActivationEnded($event)\"\n      ></sb-slide-element-host>\n    </span>\n  </div>\n</div>\n" }]
        }] });

var SlideTypes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    BlueprintSlideComponent: BlueprintSlideComponent,
    GusSlideComponent: GusSlideComponent,
    PolygonSlideComponent: PolygonSlideComponent,
    RaptureSlideComponent: RaptureSlideComponent,
    SlideComponent: SlideComponent$1
});

class SlideHostComponent extends HostComponent {
    constructor(resolver, changeDetector) {
        super(resolver, changeDetector);
        this.isCoverSlide = false;
        this.isActive = false;
        this.currentStep = 0;
        this.hiRes = false;
        this.editMode = false;
        this.onSlideEnded = new EventEmitter();
        this.instanceId = `sb_${newId()}`;
    }
    getHostedComponentType() {
        const typeName = this.layoutId
            ? {
                'aurora': 'Aurora',
                'blueprint': 'Blueprint',
                'boulevard': 'Boulevard',
                'fiveoclock': 'FiveOclock',
                'polygon': 'Polygon',
                'rapture': 'Rapture',
                'scala': 'Scala',
                'stairway': 'Stairway',
                'ey': 'EY',
                'gus': 'Gus',
                'urban-collective': 'UrbanCollective',
            }[this.layoutId] + 'SlideComponent'
            : null;
        const defaultTypeName = 'SlideComponent';
        return (typeName && SlideTypes[typeName]) || SlideTypes[defaultTypeName];
    }
    wireHostedComponent(componentRef) {
        const component = componentRef.instance;
        component.slide = this.slide;
        component.slideElements = this.slideElements;
        component.slideOptions = this.slideOptions;
        component.isCoverSlide = this.isCoverSlide;
        component.layoutId = this.layoutId;
        component.palette = this.palette;
        component.colorSetOffset = this.colorSetOffset;
        component.textFont = this.textFont;
        component.headingFont = this.headingFont;
        component.logoLightBg = this.logoLightBg;
        component.logoDarkBg = this.logoDarkBg;
        component.isActive = this.isActive;
        component.currentStep = this.currentStep;
        component.hiRes = this.hiRes;
        component.editMode = this.editMode;
        if (this._onSlideEndedSubscription) {
            this._onSlideEndedSubscription.unsubscribe();
        }
        this._onSlideEndedSubscription = component.onSlideEnded.subscribe(event => {
            this.onSlideEnded.emit(event);
        });
    }
    ngOnChanges(changes) {
        if (this.componentRef) {
            if (didChange(changes['layout'])) {
                this.reload();
            }
            else {
                super.ngOnChanges(changes);
            }
        }
    }
    activate() {
        if (this.componentRef) {
            this.componentRef.instance.activate();
        }
    }
    deactivate() {
        if (this.componentRef) {
            this.componentRef.instance.deactivate();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideHostComponent, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideHostComponent, selector: "sb-slide-host", inputs: { slide: "slide", slideElements: "slideElements", slideOptions: "slideOptions", isCoverSlide: "isCoverSlide", layoutId: "layoutId", palette: "palette", colorSetOffset: "colorSetOffset", textFont: "textFont", headingFont: "headingFont", logoLightBg: "logoLightBg", logoDarkBg: "logoDarkBg", isActive: "isActive", currentStep: "currentStep", hiRes: "hiRes", editMode: "editMode" }, outputs: { onSlideEnded: "onSlideEnded" }, host: { properties: { "id": "this.instanceId" } }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: '<ng-container #host></ng-container>', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-slide-host',
                    template: '<ng-container #host></ng-container>',
                }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.ChangeDetectorRef }], propDecorators: { slide: [{
                type: Input
            }], slideElements: [{
                type: Input
            }], slideOptions: [{
                type: Input
            }], isCoverSlide: [{
                type: Input
            }], layoutId: [{
                type: Input
            }], palette: [{
                type: Input
            }], colorSetOffset: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], logoLightBg: [{
                type: Input
            }], logoDarkBg: [{
                type: Input
            }], isActive: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], hiRes: [{
                type: Input
            }], editMode: [{
                type: Input
            }], onSlideEnded: [{
                type: Output
            }], instanceId: [{
                type: HostBinding,
                args: ['id']
            }] } });

class SlideViewerComponent {
    get colorSetOffset() {
        return this._colorSetOffset;
    }
    set colorSetOffset(value) {
        this._colorSetOffset = Number.isInteger(+value) ? +value : 0;
    }
    static { this.scaleCache = new Map(); }
    constructor(fontLoader, ngZone, detector) {
        this.fontLoader = fontLoader;
        this.ngZone = ngZone;
        this.detector = detector;
        this.currentIndex = 0;
        this.currentStep = 999;
        this._colorSetOffset = 0;
        this.hiRes = false;
        this.editMode = false;
        this.fullScreen = false;
        this.lazyLoad = true;
        this.onSlideEnded = new EventEmitter();
        this.reloadSlides$ = new Subject();
        this.reloadFonts$ = new Subject();
        this.reloadSize$ = new Subject();
        this.isVisible$ = new BehaviorSubject(true);
        this.destroy$ = new Subject();
        this.viewerSlides = [];
        this.viewerScale$ = new Subject();
    }
    ngOnChanges(changes) {
        if (didChange(changes['slides']) || didChange(changes['slide'])) {
            this.reloadSlides$.next();
        }
        if (didChange(changes['textFont']) || didChange(changes['headingFont'])) {
            this.reloadFonts$.next();
        }
    }
    ngOnInit() {
        const isVisible = () => this.lazyLoader
            ? this.lazyLoader.isVisible$.pipe(filter(v => !!v), take(1))
            : empty();
        this.reloadSlides$
            .pipe(switchMap(isVisible), debounceTime(100), takeUntil(this.destroy$))
            .subscribe(this.reloadSlides.bind(this));
        this.reloadFonts$
            .pipe(switchMap(isVisible), debounceTime(100), takeUntil(this.destroy$))
            .subscribe(this.reloadFonts.bind(this));
        this.reloadSize$
            .pipe(switchMap(isVisible), takeUntil(this.destroy$))
            .subscribe(this.reloadSize.bind(this));
        this.viewerScale$
            .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(scale => requestAnimationFrame(() => {
            const transform = `translate(-50%, -50%) scale(${scale})`;
            this.mainContainer.nativeElement.style['transform'] = transform;
            ['webkit', 'ms'].forEach(prefix => {
                this.mainContainer.nativeElement.style[`${prefix}Transform`] =
                    transform;
            });
        }));
        // Boot
        this.reloadSlides$.next();
        this.reloadFonts$.next();
    }
    ngAfterViewInit() {
        this.reloadSize$.next();
        window.requestIdleCallback(() => {
            watchResize(this.wrapper.nativeElement)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.reloadSize$.next());
        });
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    reload() {
        this.reloadSlides();
        this.reloadFonts();
    }
    reloadSlides() {
        this.viewerSlides = this.slide
            ? [this.slide]
            : this.slides
                ? [...this.slides]
                : [];
        this.detector.detectChanges();
    }
    trackSlidesBy(index, slide) {
        return slide.id;
    }
    reloadFonts() {
        if (this.textFont) {
            this.fontLoader.load(this.textFont);
        }
        if (this.headingFont) {
            this.fontLoader.load(this.headingFont);
        }
    }
    reloadSize() {
        this.ngZone.runOutsideAngular(() => {
            const wrapperWidth = this.wrapper.nativeElement.offsetWidth;
            const wrapperHeight = this.wrapper.nativeElement.offsetHeight;
            const scaleCacheKey = `${wrapperWidth}x${wrapperHeight}`;
            let scale;
            if (SlideViewerComponent.scaleCache.has(scaleCacheKey)) {
                scale = SlideViewerComponent.scaleCache.get(scaleCacheKey);
            }
            else {
                const mainContainerWidth = this.mainContainer.nativeElement.offsetWidth;
                const mainContainerHeight = this.mainContainer.nativeElement.offsetHeight;
                if (!wrapperWidth ||
                    !wrapperHeight ||
                    !mainContainerWidth ||
                    !mainContainerHeight) {
                    return requestAnimationFrame(this.reloadSize.bind(this));
                }
                const WIDTH_TO_HEIGHT_RATIO = 1.6;
                if (wrapperWidth / wrapperHeight > WIDTH_TO_HEIGHT_RATIO) {
                    scale = wrapperHeight / mainContainerHeight;
                }
                else {
                    scale = wrapperWidth / mainContainerWidth;
                }
                scale = Math.ceil(scale * 1000) / 1000;
                SlideViewerComponent.scaleCache.set(scaleCacheKey, scale);
            }
            this.viewerScale$.next(scale);
        });
    }
    activate() {
        const slideComponent = this.slideComponents.find((component, index) => index === (this.currentIndex === 0 ? 0 : 1));
        if (slideComponent) {
            slideComponent.activate();
        }
    }
    deactivate() {
        const slideComponent = this.slideComponents.find((component, index) => index === (this.currentIndex === 0 ? 0 : 1));
        if (slideComponent) {
            slideComponent.deactivate();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideViewerComponent, deps: [{ token: i1$4.FontLoader }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideViewerComponent, selector: "sb-slide-viewer", inputs: { slides: "slides", slide: "slide", layoutId: "layoutId", palette: "palette", textFont: "textFont", headingFont: "headingFont", logoLightBg: "logoLightBg", logoDarkBg: "logoDarkBg", currentIndex: "currentIndex", currentStep: "currentStep", colorSetOffset: "colorSetOffset", hiRes: "hiRes", editMode: "editMode", fullScreen: "fullScreen", lazyLoad: "lazyLoad" }, outputs: { onSlideEnded: "onSlideEnded" }, viewQueries: [{ propertyName: "wrapper", first: true, predicate: ["wrapper"], descendants: true, static: true }, { propertyName: "mainContainer", first: true, predicate: ["main"], descendants: true, static: true }, { propertyName: "lazyLoader", first: true, predicate: LazyLoadComponent, descendants: true, static: true }, { propertyName: "slideComponents", predicate: SlideHostComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  #wrapper\n  class=\"viewer-wrapper\"\n  [attr.data-layout]=\"layoutId\"\n  [class.full-screen]=\"fullScreen\"\n>\n  <div\n    #main\n    class=\"viewer-main\"\n    [style.font-family]=\"textFont?.fontFamily\"\n    [class.edit-mode]=\"editMode\"\n  >\n    <sb-lazy-load [enabled]=\"lazyLoad\">\n      <ng-template\n        ngFor\n        [ngForOf]=\"viewerSlides\"\n        [ngForTrackBy]=\"trackSlidesBy\"\n        let-slide\n        let-slideIndex=\"index\"\n      >\n        <sb-slide-host\n          *ngIf=\"\n            currentIndex - 1 <= slideIndex && slideIndex <= currentIndex + 1\n          \"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [isCoverSlide]=\"slideIndex === 0\"\n          [layoutId]=\"layoutId\"\n          [palette]=\"palette\"\n          [colorSetOffset]=\"colorSetOffset + slideIndex\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [logoLightBg]=\"logoLightBg\"\n          [logoDarkBg]=\"logoDarkBg\"\n          [currentStep]=\"currentStep\"\n          [isActive]=\"slideIndex === currentIndex\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [class.previous]=\"slideIndex === currentIndex - 1\"\n          [class.active]=\"slideIndex === currentIndex\"\n          [class.next]=\"slideIndex === currentIndex + 1\"\n          (onSlideEnded)=\"onSlideEnded.emit($event)\"\n        ></sb-slide-host>\n      </ng-template>\n    </sb-lazy-load>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: LazyLoadComponent, selector: "sb-lazy-load", inputs: ["threshold", "enabled"] }, { kind: "component", type: SlideHostComponent, selector: "sb-slide-host", inputs: ["slide", "slideElements", "slideOptions", "isCoverSlide", "layoutId", "palette", "colorSetOffset", "textFont", "headingFont", "logoLightBg", "logoDarkBg", "isActive", "currentStep", "hiRes", "editMode"], outputs: ["onSlideEnded"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideViewerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-slide-viewer', template: "<div\n  #wrapper\n  class=\"viewer-wrapper\"\n  [attr.data-layout]=\"layoutId\"\n  [class.full-screen]=\"fullScreen\"\n>\n  <div\n    #main\n    class=\"viewer-main\"\n    [style.font-family]=\"textFont?.fontFamily\"\n    [class.edit-mode]=\"editMode\"\n  >\n    <sb-lazy-load [enabled]=\"lazyLoad\">\n      <ng-template\n        ngFor\n        [ngForOf]=\"viewerSlides\"\n        [ngForTrackBy]=\"trackSlidesBy\"\n        let-slide\n        let-slideIndex=\"index\"\n      >\n        <sb-slide-host\n          *ngIf=\"\n            currentIndex - 1 <= slideIndex && slideIndex <= currentIndex + 1\n          \"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [isCoverSlide]=\"slideIndex === 0\"\n          [layoutId]=\"layoutId\"\n          [palette]=\"palette\"\n          [colorSetOffset]=\"colorSetOffset + slideIndex\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [logoLightBg]=\"logoLightBg\"\n          [logoDarkBg]=\"logoDarkBg\"\n          [currentStep]=\"currentStep\"\n          [isActive]=\"slideIndex === currentIndex\"\n          [hiRes]=\"hiRes\"\n          [editMode]=\"editMode\"\n          [class.previous]=\"slideIndex === currentIndex - 1\"\n          [class.active]=\"slideIndex === currentIndex\"\n          [class.next]=\"slideIndex === currentIndex + 1\"\n          (onSlideEnded)=\"onSlideEnded.emit($event)\"\n        ></sb-slide-host>\n      </ng-template>\n    </sb-lazy-load>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i1$4.FontLoader }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }], propDecorators: { wrapper: [{
                type: ViewChild,
                args: ['wrapper', { static: true }]
            }], mainContainer: [{
                type: ViewChild,
                args: ['main', { static: true }]
            }], slideComponents: [{
                type: ViewChildren,
                args: [SlideHostComponent]
            }], lazyLoader: [{
                type: ViewChild,
                args: [LazyLoadComponent, { static: true }]
            }], slides: [{
                type: Input
            }], slide: [{
                type: Input
            }], layoutId: [{
                type: Input
            }], palette: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], logoLightBg: [{
                type: Input
            }], logoDarkBg: [{
                type: Input
            }], currentIndex: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], colorSetOffset: [{
                type: Input
            }], hiRes: [{
                type: Input
            }], editMode: [{
                type: Input
            }], fullScreen: [{
                type: Input
            }], lazyLoad: [{
                type: Input
            }], onSlideEnded: [{
                type: Output
            }] } });

class SlidePlayerComponent {
    constructor(changeDetector, ngZone, pusher) {
        this.changeDetector = changeDetector;
        this.ngZone = ngZone;
        this.pusher = pusher;
        this.hiRes = false;
        this.keyboardEnabled = false;
        this.toolbarEnabled = true;
        this.toolbarDark = true;
        this.autoRewindEnabled = false;
        this.autoPlayEnabled = true;
        this.embedded = false;
        this.liveEnabled = true;
        this.nonNativeFullScreenEnabled = true;
        this.onSlideIndexChanged = new EventEmitter();
        this.onPusherEvent = new EventEmitter();
        this.onEnterFullScreen = new EventEmitter();
        this.onExitFullScreen = new EventEmitter();
        this.slides$ = new BehaviorSubject([]);
        this.currentIndex = 0;
        this.currentStep = 0;
        this.hasReachedEnd = false;
        this.isToolbarVisible = false;
        this.isFullScreen = false;
        this.isAutoPlaying = false;
        this.autoPlaySeconds = 3;
        this.autoPlayTimeout = null;
        this.liveModeEnabled = true;
        this.liveModeManuallyToggled = false;
        this.destroy$ = new Subject();
        // PROGRESS BAR NAVIGATION
        this.progressHoverSlideIndex = 0;
        this.progressBarWidth = '0%';
    }
    ngOnInit() {
        this._onFullScreenChange = (event) => this.onFullScreenChange(event.target);
        if (this.isNativeFullScreenAvailable) {
            document.addEventListener(screenfull.raw.fullscreenchange, this._onFullScreenChange);
        }
        this.reloadProgressBarStyles();
        if (this.liveEnabled) {
            this.subscribePusher();
        }
    }
    ngOnChanges(changes) {
        // Reload slides
        if (changes['presentation'] || changes['slides']) {
            const slides = [];
            if (this.slides) {
                slides.push(...this.slides);
            }
            else if (this.presentation && this.presentation.slides) {
                slides.push(...this.presentation.slides);
            }
            this.slides$.next(slides);
        }
        if (didChange(changes['presentation'])) {
            this.reloadProgressBarStyles();
            if (this.liveEnabled) {
                this.unsubscribePusher();
                this.subscribePusher();
            }
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.isNativeFullScreenAvailable) {
            document.removeEventListener(screenfull.raw.fullscreenchange, this._onFullScreenChange);
        }
        this.unsubscribePusher();
    }
    reload() {
        this.viewer.reload();
        this.reloadProgressBarStyles();
    }
    get viewerSlides() {
        return this.slides$.getValue();
    }
    getCurrentSlide() {
        return this.viewerSlides[this.currentIndex];
    }
    getCurrentSlideSteps() {
        return this.getSlideSteps(this.currentIndex);
    }
    getSlideSteps(slideIndex) {
        if (0 < slideIndex && slideIndex < this.viewerSlides.length) {
            const slide = this.viewerSlides[slideIndex];
            if (slide && getSlideOptionValue(slide)('animate')) {
                const bodyCount = slide.elements.filter(isBodyElement).length;
                if (bodyCount > 0) {
                    return bodyCount;
                }
            }
        }
        // Default is 1
        return 1;
    }
    goTo(slideIndex, slideStep = 0, { triggerEvents = true, triggerPusher = true, } = {}) {
        this.hasReachedEnd = false;
        if (0 <= slideIndex &&
            slideIndex < this.viewerSlides.length &&
            (this.currentIndex !== slideIndex || this.currentStep !== slideStep)) {
            this.currentIndex = slideIndex;
            this.currentStep = slideStep;
            this.ngZone.run(() => this.changeDetector.detectChanges());
            if (triggerEvents) {
                this.onSlideIndexChanged.emit({
                    slideIndex: this.currentIndex,
                    slideStep: this.currentStep,
                });
            }
            if (triggerPusher) {
                if (this.liveModeEnabled && this.pusherChannelName) {
                    this.pusher.trigger('private-' + this.pusherChannelName, 'client-slide-changed', {
                        index: slideIndex,
                        step: slideStep,
                    });
                }
            }
        }
        this.reloadProgressBarSize();
    }
    goToEnd() {
        this.hasReachedEnd = true;
        this.reloadProgressBarSize();
    }
    play(seconds) {
        this.stop();
        if (this.hasReachedEnd) {
            this.rewind();
        }
        // Go to full-screen
        if (!this.isFullScreen && this.isNativeFullScreenAvailable) {
            this.toggleFullScreen();
        }
        this.isAutoPlaying = true;
        this.autoPlaySeconds = seconds || this.autoPlaySeconds || 3;
        this.autoPlayNext();
        // Start videos, etc
        this.viewer.activate();
    }
    playCustom() {
        // TODO: Localization
        // const seconds = prompt(Localization.getString('How much time (in seconds) between steps?'), '10');
        const result = prompt('How much time (in seconds) between steps?', '10');
        if (result) {
            const seconds = parseInt(result, 10);
            if (!isNaN(seconds) && seconds > 0) {
                this.play(seconds);
            }
        }
    }
    autoPlayNext() {
        if (this.isAutoPlaying && !this.hasReachedEnd) {
            this.autoPlayTimeout = setTimeout(() => {
                const slide = this.getCurrentSlide();
                if (slide && !hasVideo(slide)) {
                    // Videos advance when they finish by triggering 'onSlideEnded'
                    this.nextStep();
                    this.autoPlayNext();
                }
            }, this.autoPlaySeconds * 1000);
        }
        else {
            this.stop();
        }
        this.changeDetector.detectChanges();
    }
    onSlideEnded() {
        if (this.isAutoPlaying) {
            this.nextStep();
            this.autoPlayNext();
        }
    }
    stop() {
        this.isAutoPlaying = false;
        if (this.autoPlayTimeout) {
            clearTimeout(this.autoPlayTimeout);
            this.autoPlayTimeout = null;
        }
    }
    nextStep() {
        if (!this.hasReachedEnd) {
            if (this.currentStep + 1 < this.getCurrentSlideSteps()) {
                this.goTo(this.currentIndex, this.currentStep + 1);
            }
            else if (this.currentIndex + 1 < this.viewerSlides.length) {
                this.goTo(this.currentIndex + 1);
            }
            else {
                this.goToEnd();
            }
        }
    }
    next() {
        this.stop();
        if (this.hasReachedEnd) {
            if (this.isFullScreen) {
                this.toggleFullScreen();
            }
            this.rewind();
        }
        else {
            this.nextStep();
        }
    }
    back() {
        this.stop();
        if (this.hasReachedEnd) {
            this.goTo(this.currentIndex, this.currentStep);
        }
        else if (this.currentStep > 0) {
            this.goTo(this.currentIndex, this.currentStep - 1);
        }
        else if (this.currentIndex > 0) {
            this.goTo(this.currentIndex - 1, this.getSlideSteps(this.currentIndex - 1) - 1);
        }
    }
    rewind() {
        this.stop();
        this.goTo(0);
    }
    nextEnabled() {
        return this.viewerSlides.length && !this.hasReachedEnd;
    }
    backEnabled() {
        return this.currentStep > 0 || this.currentIndex > 0 || this.hasReachedEnd;
    }
    onKeyDown(event) {
        // Ignore if not fullscreen and not forced-enabled
        if (!this.isFullScreen && !this.keyboardEnabled)
            return;
        const targetIsInput = $(event.target).is('input, textarea, [contenteditable]');
        if (targetIsInput)
            return;
        // Hide the toolbar, because we're using the keyboard :)
        this.hideToolbar();
        switch (event.code) {
            case 'Space':
            case 'PageDown':
            case 'ArrowRight':
                this.next();
                event.preventDefault();
                break;
            case 'ArrowDown':
            case 'Enter':
                {
                    if (this.isFullScreen) {
                        this.next();
                        event.preventDefault();
                    }
                }
                break;
            case 'PageUp':
            case 'ArrowLeft':
                this.back();
                event.preventDefault();
                break;
            case 'ArrowUp':
                {
                    if (this.isFullScreen) {
                        this.back();
                        event.preventDefault();
                    }
                }
                break;
            case 'Escape':
                {
                    if (!this.isNativeFullScreenAvailable && this.isFullScreen) {
                        this.toggleFullScreen();
                        event.preventDefault();
                    }
                }
                break;
        }
    }
    // FULL-SCREEN
    get isNativeFullScreenAvailable() {
        return screenfull.isEnabled;
    }
    toggleToolbar(event) {
        if (!this.isFullScreen) {
            return;
        }
        if (event) {
            // Ignore when the event target is a link or input field
            const targetIsInput = $(event.target).is('a, input, textarea, [contenteditable]');
            if (targetIsInput)
                return;
        }
        this.isToolbarVisible = !this.isToolbarVisible;
    }
    hideToolbar() {
        this.isToolbarVisible = false;
    }
    onFullScreenChange(target) {
        if (target === this.mainContainer.nativeElement ||
            (screenfull && screenfull.element === this.mainContainer.nativeElement)) {
            this.isFullScreen = !this.isFullScreen;
        }
        else if (!target || (screenfull && !screenfull.element)) {
            this.isFullScreen = false;
        }
        if (this.isFullScreen) {
            this.viewer.activate();
            this.onEnterFullScreen.emit();
        }
        else {
            this.hideToolbar();
            this.viewer.deactivate();
            this.stop();
            this.onExitFullScreen.emit();
        }
        if (this.autoRewindEnabled) {
            this.rewind();
        }
    }
    toggleFullScreen() {
        if (this.isNativeFullScreenAvailable) {
            screenfull.toggle(this.mainContainer.nativeElement);
        }
        else {
            this.onFullScreenChange(this.mainContainer.nativeElement);
        }
    }
    onProgressBarMouseMove(event) {
        this.ngZone.runOutsideAngular(() => {
            if (this.viewerSlides && this.viewerSlides.length) {
                const barWidth = this.mainContainer.nativeElement.offsetWidth;
                let indicatorLeft = 0;
                let slideIndex = this.progressHoverSlideIndex;
                if (barWidth) {
                    const progressHoverRatioX = event.offsetX / barWidth;
                    indicatorLeft = progressHoverRatioX * 100;
                    slideIndex = Math.floor(progressHoverRatioX * this.viewerSlides.length);
                }
                else {
                    slideIndex = 0;
                }
                if (slideIndex !== this.progressHoverSlideIndex) {
                    this.progressHoverSlideIndex = slideIndex;
                    this.ngZone.run(() => this.changeDetector.detectChanges());
                }
                requestAnimationFrame(() => {
                    this.progressHoverIndicator.nativeElement.style.left =
                        indicatorLeft + '%';
                });
            }
        });
    }
    reloadProgressBarStyles() {
        const palette = this.palette || (this.presentation ? this.presentation.palette : null);
        if (palette && palette.colorSets?.length > 0) {
            const [colorSet] = palette.colorSets;
            const set = new ColorSet(colorSet);
            this.progressBarColor = set.highlightColor;
            this.progressBarEmptyColor = set.backColor;
        }
    }
    reloadProgressBarSize() {
        let width = 0;
        if (this.hasReachedEnd) {
            width = 100;
        }
        else if (this.viewerSlides && this.viewerSlides.length) {
            width =
                ((this.currentIndex + this.currentStep / this.getCurrentSlideSteps()) /
                    this.viewerSlides.length) *
                    100;
        }
        this.progressBarWidth = width + '%';
    }
    // Pusher & Live mode
    subscribePusher() {
        if (this.presentation) {
            this.pusherChannelName = `private-player-${this.presentation.id}`;
            // Player "live" events
            this.pusher
                .subscribe(this.pusherChannelName, 'client-live-started')
                .pipe(takeUntil(this.destroy$))
                .subscribe(event => this._onPusherEvent(event));
        }
    }
    unsubscribePusher() {
        if (this.pusherChannelName) {
            this.pusher.unsubscribe(this.pusherChannelName);
            this.pusherChannelName = null;
        }
    }
    _onPusherEvent(event) {
        switch (event.name) {
            case 'client-slide-changed':
                {
                    if (this.liveModeEnabled) {
                        const { index, step } = event.data;
                        this.goTo(index, step, { triggerPusher: false });
                    }
                }
                break;
        }
        this.onPusherEvent.emit(event);
    }
    toggleLiveMode() {
        this.liveModeEnabled = !this.liveModeEnabled;
        this.liveModeManuallyToggled = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlidePlayerComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i1$5.PusherService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlidePlayerComponent, selector: "sb-slide-player", inputs: { presentation: "presentation", slides: "slides", layoutId: "layoutId", palette: "palette", textFont: "textFont", headingFont: "headingFont", logoLightBg: "logoLightBg", logoDarkBg: "logoDarkBg", hiRes: "hiRes", keyboardEnabled: "keyboardEnabled", toolbarEnabled: "toolbarEnabled", toolbarDark: "toolbarDark", autoRewindEnabled: "autoRewindEnabled", autoPlayEnabled: "autoPlayEnabled", embedded: "embedded", liveEnabled: "liveEnabled", nonNativeFullScreenEnabled: "nonNativeFullScreenEnabled" }, outputs: { onSlideIndexChanged: "onSlideIndexChanged", onPusherEvent: "onPusherEvent", onEnterFullScreen: "onEnterFullScreen", onExitFullScreen: "onExitFullScreen" }, host: { properties: { "class.full-screen": "this.isFullScreen" } }, viewQueries: [{ propertyName: "viewer", first: true, predicate: SlideViewerComponent, descendants: true }, { propertyName: "mainContainer", first: true, predicate: ["mainContainer"], descendants: true }, { propertyName: "progressHoverIndicator", first: true, predicate: ["progressHoverIndicator"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  #mainContainer\n  class=\"player-main\"\n  [class.embedded]=\"embedded && !isFullScreen\"\n  (window:keydown)=\"onKeyDown($event)\"\n  (swipeleft)=\"nextEnabled() ? next() : null\"\n  (swiperight)=\"backEnabled() ? back() : null\"\n  (swipeup)=\"isFullScreen ? toggleFullScreen() : null\"\n  (swipedown)=\"isFullScreen ? toggleFullScreen() : null\"\n  (pinchin)=\"isFullScreen ? toggleFullScreen() : null\"\n  (pinchout)=\"isFullScreen ? toggleFullScreen() : null\"\n>\n  <div class=\"viewer__wrapper\">\n    <sb-slide-viewer\n      [slides]=\"slides$ | async\"\n      [layoutId]=\"layoutId || presentation?.layoutId\"\n      [palette]=\"palette || presentation?.palette\"\n      [textFont]=\"textFont || presentation?.textFont\"\n      [headingFont]=\"headingFont || presentation?.headingFont\"\n      [logoLightBg]=\"logoLightBg || presentation?.logoLightBg\"\n      [logoDarkBg]=\"logoDarkBg || presentation?.logoDarkBg\"\n      [currentIndex]=\"currentIndex\"\n      [currentStep]=\"currentStep\"\n      [hiRes]=\"hiRes || isFullScreen\"\n      [fullScreen]=\"embedded || isFullScreen\"\n      [lazyLoad]=\"false\"\n      (onSlideEnded)=\"onSlideEnded()\"\n      (click)=\"toggleToolbar($event)\"\n    ></sb-slide-viewer>\n\n    <!-- Ads -->\n    <aside class=\"outro\" [class.active]=\"hasReachedEnd\">\n      <div>\n        <a\n          class=\"btn btn-link text-uppercase\"\n          href=\"javascript:null\"\n          (click)=\"rewind()\"\n        >\n          <i class=\"far fa-backward\"></i> <span translate>Play Again</span>\n        </a>\n        <ng-container *ngIf=\"isFullScreen\">\n          &nbsp;&nbsp;\n          <a\n            class=\"btn btn-link text-uppercase\"\n            href=\"javascript:void(0)\"\n            (click)=\"next()\"\n          >\n            <i class=\"far fa-eject\"></i> <span translate>Finish</span>\n          </a>\n        </ng-container>\n      </div>\n    </aside>\n  </div>\n\n  <div\n    class=\"controls\"\n    [class.controls__visible]=\"isFullScreen && isToolbarVisible\"\n  >\n    <!-- PROGRESS BAR -->\n    <div\n      class=\"progress__wrapper\"\n      (click)=\"goTo(progressHoverSlideIndex)\"\n      (mousemove)=\"onProgressBarMouseMove($event)\"\n    >\n      <div class=\"progress__hover-indicator\" #progressHoverIndicator>{{\n        progressHoverSlideIndex + 1\n      }}</div>\n      <div class=\"progress\" [style.background-color]=\"progressBarEmptyColor\">\n        <div\n          class=\"progress-bar\"\n          [style.background-color]=\"progressBarColor\"\n          [style.width]=\"progressBarWidth\"\n        ></div>\n      </div>\n    </div>\n\n    <!-- TOOLBAR -->\n    <div\n      *ngIf=\"toolbarEnabled\"\n      class=\"player-toolbar\"\n      [class.bg-dark]=\"toolbarDark\"\n    >\n      <div class=\"player-toolbar__group player-toolbar__group--left\">\n        <!-- Live Mode -->\n        <button\n          *ngIf=\"liveEnabled\"\n          type=\"button\"\n          class=\"btn btn-xs text-uppercase ml-2\"\n          [class.btn-secondary]=\"!liveModeEnabled\"\n          [class.btn-outline-danger]=\"liveModeEnabled\"\n          kp-tooltip=\"{{ 'Sync slides with all viewers' | translate }}\"\n          (click)=\"toggleLiveMode()\"\n        >\n          {{ 'Live' | translate }}\n        </button>\n        <ng-content select=\"[sb-slide-player-toolbar-left]\"></ng-content>\n      </div>\n\n      <div class=\"player-toolbar__group player-toolbar__group--center\">\n        <!-- Back -->\n        <button\n          type=\"button\"\n          class=\"btn btn-link\"\n          [disabled]=\"!backEnabled()\"\n          (click)=\"back()\"\n        >\n          <i class=\"far fa-chevron-left\"></i>\n        </button>\n        <!-- Next -->\n        <button\n          type=\"button\"\n          class=\"btn btn-link\"\n          [disabled]=\"!nextEnabled()\"\n          (click)=\"next()\"\n        >\n          <i class=\"far fa-chevron-right\"></i>\n        </button>\n      </div>\n\n      <div class=\"player-toolbar__group player-toolbar__group--right\">\n        <ng-content select=\"[sb-slide-player-toolbar-right]\"></ng-content>\n        <!-- Auto-play -->\n        <div *ngIf=\"autoPlayEnabled && !isAutoPlaying\" class=\"btn-group dropup\">\n          <button\n            type=\"button\"\n            class=\"btn btn-link\"\n            data-toggle=\"dropdown\"\n            aria-haspopup=\"true\"\n            aria-expanded=\"false\"\n          >\n            <i class=\"far fa-clock\"></i>\n          </button>\n          <div class=\"dropdown-menu dropdown-menu-right\">\n            <h6 class=\"dropdown-header\" translate>Auto-play</h6>\n            <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(3)\"\n              ><span translate>3 seconds</span></a\n            >\n            <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(5)\"\n              ><span translate>5 seconds</span></a\n            >\n            <a\n              class=\"dropdown-item\"\n              href=\"javascript:void(0)\"\n              (click)=\"play(10)\"\n              ><span translate>10 seconds</span></a\n            >\n            <a\n              class=\"dropdown-item\"\n              href=\"javascript:void(0)\"\n              (click)=\"playCustom()\"\n              ><span translate>Specify time</span>\u2026</a\n            >\n          </div>\n        </div>\n        <!-- Stop auto-play -->\n        <button\n          *ngIf=\"autoPlayEnabled && isAutoPlaying\"\n          type=\"button\"\n          class=\"btn btn-link\"\n          (click)=\"stop()\"\n        >\n          <i class=\"far fa-pause\"></i>\n        </button>\n        <!-- Full screen -->\n        <button\n          *ngIf=\"nonNativeFullScreenEnabled || isNativeFullScreenAvailable\"\n          type=\"button\"\n          class=\"btn btn-link\"\n          (click)=\"toggleFullScreen()\"\n        >\n          <i class=\"far fa-expand-wide\"></i>\n        </button>\n      </div>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$1.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "directive", type: i4.TooltipDirective, selector: "[kp-tooltip]", exportAs: ["tooltip"] }, { kind: "directive", type: i4.TooltipBehavior, selector: "[kp-tooltip]", inputs: ["kp-tooltip", "kp-tooltip-trigger", "kp-tooltip-placement"], exportAs: ["tooltip"] }, { kind: "component", type: SlideViewerComponent, selector: "sb-slide-viewer", inputs: ["slides", "slide", "layoutId", "palette", "textFont", "headingFont", "logoLightBg", "logoDarkBg", "currentIndex", "currentStep", "colorSetOffset", "hiRes", "editMode", "fullScreen", "lazyLoad"], outputs: ["onSlideEnded"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "pipe", type: i3$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlidePlayerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-slide-player', template: "<div\n  #mainContainer\n  class=\"player-main\"\n  [class.embedded]=\"embedded && !isFullScreen\"\n  (window:keydown)=\"onKeyDown($event)\"\n  (swipeleft)=\"nextEnabled() ? next() : null\"\n  (swiperight)=\"backEnabled() ? back() : null\"\n  (swipeup)=\"isFullScreen ? toggleFullScreen() : null\"\n  (swipedown)=\"isFullScreen ? toggleFullScreen() : null\"\n  (pinchin)=\"isFullScreen ? toggleFullScreen() : null\"\n  (pinchout)=\"isFullScreen ? toggleFullScreen() : null\"\n>\n  <div class=\"viewer__wrapper\">\n    <sb-slide-viewer\n      [slides]=\"slides$ | async\"\n      [layoutId]=\"layoutId || presentation?.layoutId\"\n      [palette]=\"palette || presentation?.palette\"\n      [textFont]=\"textFont || presentation?.textFont\"\n      [headingFont]=\"headingFont || presentation?.headingFont\"\n      [logoLightBg]=\"logoLightBg || presentation?.logoLightBg\"\n      [logoDarkBg]=\"logoDarkBg || presentation?.logoDarkBg\"\n      [currentIndex]=\"currentIndex\"\n      [currentStep]=\"currentStep\"\n      [hiRes]=\"hiRes || isFullScreen\"\n      [fullScreen]=\"embedded || isFullScreen\"\n      [lazyLoad]=\"false\"\n      (onSlideEnded)=\"onSlideEnded()\"\n      (click)=\"toggleToolbar($event)\"\n    ></sb-slide-viewer>\n\n    <!-- Ads -->\n    <aside class=\"outro\" [class.active]=\"hasReachedEnd\">\n      <div>\n        <a\n          class=\"btn btn-link text-uppercase\"\n          href=\"javascript:null\"\n          (click)=\"rewind()\"\n        >\n          <i class=\"far fa-backward\"></i> <span translate>Play Again</span>\n        </a>\n        <ng-container *ngIf=\"isFullScreen\">\n          &nbsp;&nbsp;\n          <a\n            class=\"btn btn-link text-uppercase\"\n            href=\"javascript:void(0)\"\n            (click)=\"next()\"\n          >\n            <i class=\"far fa-eject\"></i> <span translate>Finish</span>\n          </a>\n        </ng-container>\n      </div>\n    </aside>\n  </div>\n\n  <div\n    class=\"controls\"\n    [class.controls__visible]=\"isFullScreen && isToolbarVisible\"\n  >\n    <!-- PROGRESS BAR -->\n    <div\n      class=\"progress__wrapper\"\n      (click)=\"goTo(progressHoverSlideIndex)\"\n      (mousemove)=\"onProgressBarMouseMove($event)\"\n    >\n      <div class=\"progress__hover-indicator\" #progressHoverIndicator>{{\n        progressHoverSlideIndex + 1\n      }}</div>\n      <div class=\"progress\" [style.background-color]=\"progressBarEmptyColor\">\n        <div\n          class=\"progress-bar\"\n          [style.background-color]=\"progressBarColor\"\n          [style.width]=\"progressBarWidth\"\n        ></div>\n      </div>\n    </div>\n\n    <!-- TOOLBAR -->\n    <div\n      *ngIf=\"toolbarEnabled\"\n      class=\"player-toolbar\"\n      [class.bg-dark]=\"toolbarDark\"\n    >\n      <div class=\"player-toolbar__group player-toolbar__group--left\">\n        <!-- Live Mode -->\n        <button\n          *ngIf=\"liveEnabled\"\n          type=\"button\"\n          class=\"btn btn-xs text-uppercase ml-2\"\n          [class.btn-secondary]=\"!liveModeEnabled\"\n          [class.btn-outline-danger]=\"liveModeEnabled\"\n          kp-tooltip=\"{{ 'Sync slides with all viewers' | translate }}\"\n          (click)=\"toggleLiveMode()\"\n        >\n          {{ 'Live' | translate }}\n        </button>\n        <ng-content select=\"[sb-slide-player-toolbar-left]\"></ng-content>\n      </div>\n\n      <div class=\"player-toolbar__group player-toolbar__group--center\">\n        <!-- Back -->\n        <button\n          type=\"button\"\n          class=\"btn btn-link\"\n          [disabled]=\"!backEnabled()\"\n          (click)=\"back()\"\n        >\n          <i class=\"far fa-chevron-left\"></i>\n        </button>\n        <!-- Next -->\n        <button\n          type=\"button\"\n          class=\"btn btn-link\"\n          [disabled]=\"!nextEnabled()\"\n          (click)=\"next()\"\n        >\n          <i class=\"far fa-chevron-right\"></i>\n        </button>\n      </div>\n\n      <div class=\"player-toolbar__group player-toolbar__group--right\">\n        <ng-content select=\"[sb-slide-player-toolbar-right]\"></ng-content>\n        <!-- Auto-play -->\n        <div *ngIf=\"autoPlayEnabled && !isAutoPlaying\" class=\"btn-group dropup\">\n          <button\n            type=\"button\"\n            class=\"btn btn-link\"\n            data-toggle=\"dropdown\"\n            aria-haspopup=\"true\"\n            aria-expanded=\"false\"\n          >\n            <i class=\"far fa-clock\"></i>\n          </button>\n          <div class=\"dropdown-menu dropdown-menu-right\">\n            <h6 class=\"dropdown-header\" translate>Auto-play</h6>\n            <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(3)\"\n              ><span translate>3 seconds</span></a\n            >\n            <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(5)\"\n              ><span translate>5 seconds</span></a\n            >\n            <a\n              class=\"dropdown-item\"\n              href=\"javascript:void(0)\"\n              (click)=\"play(10)\"\n              ><span translate>10 seconds</span></a\n            >\n            <a\n              class=\"dropdown-item\"\n              href=\"javascript:void(0)\"\n              (click)=\"playCustom()\"\n              ><span translate>Specify time</span>\u2026</a\n            >\n          </div>\n        </div>\n        <!-- Stop auto-play -->\n        <button\n          *ngIf=\"autoPlayEnabled && isAutoPlaying\"\n          type=\"button\"\n          class=\"btn btn-link\"\n          (click)=\"stop()\"\n        >\n          <i class=\"far fa-pause\"></i>\n        </button>\n        <!-- Full screen -->\n        <button\n          *ngIf=\"nonNativeFullScreenEnabled || isNativeFullScreenAvailable\"\n          type=\"button\"\n          class=\"btn btn-link\"\n          (click)=\"toggleFullScreen()\"\n        >\n          <i class=\"far fa-expand-wide\"></i>\n        </button>\n      </div>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i1$5.PusherService }], propDecorators: { presentation: [{
                type: Input
            }], slides: [{
                type: Input
            }], layoutId: [{
                type: Input
            }], palette: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], logoLightBg: [{
                type: Input
            }], logoDarkBg: [{
                type: Input
            }], hiRes: [{
                type: Input
            }], keyboardEnabled: [{
                type: Input
            }], toolbarEnabled: [{
                type: Input
            }], toolbarDark: [{
                type: Input
            }], autoRewindEnabled: [{
                type: Input
            }], autoPlayEnabled: [{
                type: Input
            }], embedded: [{
                type: Input
            }], liveEnabled: [{
                type: Input
            }], nonNativeFullScreenEnabled: [{
                type: Input
            }], onSlideIndexChanged: [{
                type: Output
            }], onPusherEvent: [{
                type: Output
            }], onEnterFullScreen: [{
                type: Output
            }], onExitFullScreen: [{
                type: Output
            }], viewer: [{
                type: ViewChild,
                args: [SlideViewerComponent, { static: false }]
            }], mainContainer: [{
                type: ViewChild,
                args: ['mainContainer', { static: false }]
            }], progressHoverIndicator: [{
                type: ViewChild,
                args: ['progressHoverIndicator', { static: false }]
            }], isFullScreen: [{
                type: HostBinding,
                args: ['class.full-screen']
            }] } });

const EXPORTED_DIRECTIVES$1 = [SlidePlayerComponent, SlideViewerComponent];
const INNER_DIRECTIVES$1 = [SlideHostComponent, SlideElementHostComponent$1];
const HOSTED_DIRECTIVES = [
    // Slides
    SlideComponent$1,
    RaptureSlideComponent,
    BlueprintSlideComponent,
    PolygonSlideComponent,
    GusSlideComponent,
    // Slide Elements
    SlideElementComponent,
    BaseBackgroundComponent,
    BaseCoverComponent,
    BaseHeadingComponent,
    BaseParagraphComponent,
    BaseBulletsComponent,
    BaseChartComponent,
    BaseBarChartComponent,
    BaseLineChartComponent,
    BasePieChartComponent,
    BaseCodeComponent,
    BaseFooterComponent,
    BaseImageComponent,
    BaseNumberComponent,
    BaseQuoteComponent,
    BaseTableComponent,
    BaseVideoComponent,
    AuroraCoverComponent,
    AuroraHeadingComponent,
    AuroraBulletsComponent,
    AuroraImageComponent,
    AuroraNumberComponent,
    AuroraQuoteComponent,
    BlueprintBulletsComponent,
    BlueprintImageComponent,
    BlueprintNumberComponent,
    BlueprintQuoteComponent,
    BoulevardCoverComponent,
    BoulevardHeadingComponent,
    BoulevardBulletsComponent,
    BoulevardImageComponent,
    BoulevardQuoteComponent,
    FiveOclockCoverComponent,
    FiveOclockHeadingComponent,
    FiveOclockParagraphComponent,
    FiveOclockBulletsComponent,
    FiveOclockImageComponent,
    FiveOclockNumberComponent,
    FiveOclockQuoteComponent,
    FiveOclockTableComponent,
    PolygonCoverComponent,
    PolygonHeadingComponent,
    PolygonParagraphComponent,
    PolygonBulletsComponent,
    PolygonImageComponent,
    PolygonNumberComponent,
    PolygonQuoteComponent,
    RaptureCoverComponent,
    RaptureImageComponent,
    RaptureQuoteComponent,
    ScalaCoverComponent,
    ScalaHeadingComponent,
    ScalaBulletsComponent,
    ScalaFooterComponent,
    ScalaImageComponent,
    ScalaNumberComponent,
    ScalaQuoteComponent,
    StairwayCoverComponent,
    StairwayHeadingComponent,
    StairwayBulletsComponent,
    StairwayImageComponent,
    StairwayNumberComponent,
    StairwayQuoteComponent,
    // Custom layouts
    EYCoverComponent,
    EYHeadingComponent,
    EYBulletsComponent,
    EYImageComponent,
    EYQuoteComponent,
    GusCoverComponent,
    GusImageComponent,
    GusQuoteComponent,
    UrbanCollectiveCoverComponent,
    UrbanCollectiveHeadingComponent,
    UrbanCollectiveBulletsComponent,
    UrbanCollectiveImageComponent,
    UrbanCollectiveQuoteComponent,
];
class RenderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.11", ngImport: i0, type: RenderModule, declarations: [SlidePlayerComponent, SlideViewerComponent, SlideHostComponent, SlideElementHostComponent$1, 
            // Slides
            SlideComponent$1,
            RaptureSlideComponent,
            BlueprintSlideComponent,
            PolygonSlideComponent,
            GusSlideComponent,
            // Slide Elements
            SlideElementComponent,
            BaseBackgroundComponent,
            BaseCoverComponent,
            BaseHeadingComponent,
            BaseParagraphComponent,
            BaseBulletsComponent,
            BaseChartComponent,
            BaseBarChartComponent,
            BaseLineChartComponent,
            BasePieChartComponent,
            BaseCodeComponent,
            BaseFooterComponent,
            BaseImageComponent,
            BaseNumberComponent,
            BaseQuoteComponent,
            BaseTableComponent,
            BaseVideoComponent,
            AuroraCoverComponent,
            AuroraHeadingComponent,
            AuroraBulletsComponent,
            AuroraImageComponent,
            AuroraNumberComponent,
            AuroraQuoteComponent,
            BlueprintBulletsComponent,
            BlueprintImageComponent,
            BlueprintNumberComponent,
            BlueprintQuoteComponent,
            BoulevardCoverComponent,
            BoulevardHeadingComponent,
            BoulevardBulletsComponent,
            BoulevardImageComponent,
            BoulevardQuoteComponent,
            FiveOclockCoverComponent,
            FiveOclockHeadingComponent,
            FiveOclockParagraphComponent,
            FiveOclockBulletsComponent,
            FiveOclockImageComponent,
            FiveOclockNumberComponent,
            FiveOclockQuoteComponent,
            FiveOclockTableComponent,
            PolygonCoverComponent,
            PolygonHeadingComponent,
            PolygonParagraphComponent,
            PolygonBulletsComponent,
            PolygonImageComponent,
            PolygonNumberComponent,
            PolygonQuoteComponent,
            RaptureCoverComponent,
            RaptureImageComponent,
            RaptureQuoteComponent,
            ScalaCoverComponent,
            ScalaHeadingComponent,
            ScalaBulletsComponent,
            ScalaFooterComponent,
            ScalaImageComponent,
            ScalaNumberComponent,
            ScalaQuoteComponent,
            StairwayCoverComponent,
            StairwayHeadingComponent,
            StairwayBulletsComponent,
            StairwayImageComponent,
            StairwayNumberComponent,
            StairwayQuoteComponent,
            // Custom layouts
            EYCoverComponent,
            EYHeadingComponent,
            EYBulletsComponent,
            EYImageComponent,
            EYQuoteComponent,
            GusCoverComponent,
            GusImageComponent,
            GusQuoteComponent,
            UrbanCollectiveCoverComponent,
            UrbanCollectiveHeadingComponent,
            UrbanCollectiveBulletsComponent,
            UrbanCollectiveImageComponent,
            UrbanCollectiveQuoteComponent], imports: [CommonModule,
            RenderCommonModule,
            NounProjectIconModule,
            TextModule,
            ImageModule,
            PusherModule,
            TranslateModule,
            VideoModule,
            ChartModule,
            TooltipModule], exports: [SlidePlayerComponent, SlideViewerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderModule, imports: [CommonModule,
            RenderCommonModule,
            NounProjectIconModule,
            TextModule,
            ImageModule,
            PusherModule,
            TranslateModule,
            VideoModule,
            ChartModule,
            TooltipModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RenderCommonModule,
                        NounProjectIconModule,
                        TextModule,
                        ImageModule,
                        PusherModule,
                        TranslateModule,
                        VideoModule,
                        ChartModule,
                        TooltipModule,
                    ],
                    declarations: [
                        ...EXPORTED_DIRECTIVES$1,
                        ...INNER_DIRECTIVES$1,
                        ...HOSTED_DIRECTIVES,
                    ],
                    exports: EXPORTED_DIRECTIVES$1,
                }]
        }] });

class SlideElementHostComponent {
    get slideElementId() {
        return this.element ? 'sb_' + this.element.id : null;
    }
    get slideElementLeft() {
        const hasX = this.element &&
            this.element.style &&
            typeof this.element.style.x === 'number';
        return hasX ? this.element.style.x * 100 : null;
    }
    get slideElementTop() {
        const hasY = this.element &&
            this.element.style &&
            typeof this.element.style.y === 'number';
        return hasY ? this.element.style.y * 100 : null;
    }
    get slideElementZIndex() {
        const hasZ = this.element &&
            this.element.style &&
            typeof this.element.style.z === 'number';
        return hasZ ? this.element.style.z : null;
    }
    get slideElementWidth() {
        const hasWidth = this.element &&
            this.element.style &&
            typeof this.element.style.w === 'number';
        return hasWidth ? this.element.style.w * 100 : null;
    }
    get slideElementHeight() {
        const hasHeight = this.element &&
            this.element.style &&
            typeof this.element.style.h === 'number';
        return hasHeight ? this.element.style.h * 100 : null;
    }
    constructor(resolver, changeDetector, pluginCatalog) {
        this.resolver = resolver;
        this.changeDetector = changeDetector;
        this.pluginCatalog = pluginCatalog;
        this.defaultVariations = {};
        this.isActive = false;
        this.onActivationEnded = new EventEmitter();
        this.variation$ = new Subject();
        this.image$ = new Subject();
        this.destroy$ = new Subject();
    }
    wireHostedComponent(componentRef) {
        const component = componentRef
            .instance;
        component.slide = this.slide;
        component.slideOptions = this.slideOptions;
        component.element = this.element;
        component.elementData = this.elementData;
        component.elementOptions = this.elementOptions;
        component.colorSet = this.colorSet;
        component.textFont = this.textFont;
        component.headingFont = this.headingFont;
        component.isActive = this.isActive;
        if (this.onActivationEndedSubscription) {
            this.onActivationEndedSubscription.unsubscribe();
        }
        this.onActivationEndedSubscription = component.onActivationEnded.subscribe(event => {
            this.onActivationEnded.emit(event);
        });
    }
    ngOnInit() {
        this.reload();
        merge(this.variation$.pipe(distinctUntilChanged()), this.image$.pipe(distinctUntilChanged()))
            .pipe(takeUntil(this.destroy$))
            .subscribe(this.reload.bind(this));
    }
    ngOnChanges(changes) {
        if (didChange(changes['elementOptions'])) {
            this.variation$.next(this.elementOptions['variation']);
        }
        if (didChange(changes['elementData'])) {
            if (this.element && this.element.type === 'image') {
                this.image$.next(this.elementData['image'] || this.elementData['icon']);
            }
        }
        if (didChange(changes['defaultVariations'])) {
            this.reload();
        }
        else {
            if (this.componentRef) {
                this.wireHostedComponent(this.componentRef);
                if (typeof this.componentRef.instance['ngOnChanges'] === 'function') {
                    this.componentRef.instance.ngOnChanges(changes);
                }
            }
        }
    }
    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }
    activate() {
        if (this.componentRef) {
            this.componentRef.instance.activate();
        }
    }
    deactivate() {
        if (this.componentRef) {
            this.componentRef.instance.deactivate();
        }
    }
    reload() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
        this.host.clear();
        const elementType = this.element.type === 'image' && !!this.element.data.icon
            ? 'icon'
            : this.element.type === 'bullets'
                ? 'paragraph'
                : this.element.type;
        const variationName = getElementOptionValue(this.element)('variation') ||
            (this.defaultVariations ? this.defaultVariations[elementType] : null);
        const plugin = this.pluginCatalog.get(elementType);
        if (plugin) {
            const hostedComponentType = plugin.getComponent(variationName);
            if (hostedComponentType) {
                const hostedComponent = this.resolver.resolveComponentFactory(hostedComponentType);
                this.componentRef = this.host.createComponent(hostedComponent);
                this.wireHostedComponent(this.componentRef);
            }
        }
        this.changeDetector.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementHostComponent, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ChangeDetectorRef }, { token: i1$6.RenderPluginCatalog }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideElementHostComponent, selector: "sb-evo-slide-element-host", inputs: { slide: "slide", slideOptions: "slideOptions", element: "element", elementData: "elementData", elementOptions: "elementOptions", defaultVariations: "defaultVariations", colorSet: "colorSet", textFont: "textFont", headingFont: "headingFont", isActive: "isActive" }, outputs: { onActivationEnded: "onActivationEnded" }, host: { properties: { "id": "this.slideElementId", "style.left.%": "this.slideElementLeft", "style.top.%": "this.slideElementTop", "style.zIndex": "this.slideElementZIndex", "style.width.%": "this.slideElementWidth", "style.height.%": "this.slideElementHeight" } }, viewQueries: [{ propertyName: "host", first: true, predicate: ["host"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: '<ng-container #host></ng-container>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideElementHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-evo-slide-element-host',
                    template: '<ng-container #host></ng-container>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.ChangeDetectorRef }, { type: i1$6.RenderPluginCatalog }], propDecorators: { slide: [{
                type: Input
            }], slideOptions: [{
                type: Input
            }], element: [{
                type: Input
            }], elementData: [{
                type: Input
            }], elementOptions: [{
                type: Input
            }], defaultVariations: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], isActive: [{
                type: Input
            }], onActivationEnded: [{
                type: Output
            }], host: [{
                type: ViewChild,
                args: ['host', { static: true, read: ViewContainerRef }]
            }], slideElementId: [{
                type: HostBinding,
                args: ['id']
            }], slideElementLeft: [{
                type: HostBinding,
                args: ['style.left.%']
            }], slideElementTop: [{
                type: HostBinding,
                args: ['style.top.%']
            }], slideElementZIndex: [{
                type: HostBinding,
                args: ['style.zIndex']
            }], slideElementWidth: [{
                type: HostBinding,
                args: ['style.width.%']
            }], slideElementHeight: [{
                type: HostBinding,
                args: ['style.height.%']
            }] } });

class SlideComponent {
    constructor() {
        this.defaultElementVariations = {};
        this.defaultSlideOptions = {};
        this.colorSetOffset = 0;
        this.isPrevious = false;
        this.isActive = true;
        this.isNext = false;
        this.currentStep = 0;
        this.onElementActivationEnded = new EventEmitter();
        this.reloadElements$ = new Subject();
        this.reloadStyles$ = new Subject();
        this.destroy$ = new Subject();
        this.elements$ = new BehaviorSubject([]);
        this.colorSet$ = new BehaviorSubject({});
        this.slideStyles$ = new BehaviorSubject({});
        this.backgroundStyles$ = new BehaviorSubject({});
        this.backgroundClasses$ = new BehaviorSubject({});
        this.headingFontFamily$ = new Subject();
        this.logoPosition$ = new BehaviorSubject('off');
        this.logoUrl$ = new BehaviorSubject(null);
        this.instanceId = `sb_${newId()}`;
    }
    get slideId() {
        return this.slide ? this.slide.id : void 0;
    }
    ngOnChanges(changes) {
        if (didChange(changes['defaultElementVariations']) ||
            didChange(changes['defaultSlideOptions']) ||
            didChange(changes['palette']) ||
            didChange(changes['colorSetOffset']) ||
            didChange(changes['textFont']) ||
            didChange(changes['headingFont']) ||
            didChange(changes['logoLightBg']) ||
            didChange(changes['logoDarkBg']) ||
            didChange(changes['slideElements']) ||
            didChange(changes['slideOptions'])) {
            this.reloadStyles$.next();
        }
        if (didChange(changes['slideElements'])) {
            this.reloadElements$.next();
        }
    }
    ngOnInit() {
        this.reloadElements$
            .pipe(
        // debounceTime(10),
        takeUntil(this.destroy$))
            .subscribe(this.reloadElements.bind(this));
        this.reloadStyles$
            .pipe(
        // debounceTime(10),
        takeUntil(this.destroy$))
            .subscribe(this.reloadStyles.bind(this));
        this.headingFontFamily$
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(this.loadHeadingFontStylesheet.bind(this));
        this.colorSet$
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(this.loadTextColorStylesheet.bind(this));
        // Trigger initial load
        this.reloadElements();
        this.reloadStyles();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        // Clear stylesheets
        this.headingFontStyleSheet?.clear();
        this.textColorsStyleSheet?.clear();
    }
    reloadElements() {
        this.elements$.next(this.slideElements || []);
    }
    reloadStyles() {
        this.reloadColorSet();
        this.reloadSlideStyles();
        this.reloadBackground();
        this.reloadHeadingFont();
        this.reloadLogo();
    }
    reloadColorSet() {
        if (this.palette) {
            let colorSet = ColorSet.getColorSet(this.palette.colorSets, [
                +getSlideOptionValue(this.slide)('colorSet'),
                this.defaultSlideOptions ? +this.defaultSlideOptions['colorSet'] : null,
                this.colorSetOffset,
            ]);
            // Change the text and back colors to white and black
            if (this.slide &&
                hasBackgroundImage(this.slide) &&
                getSlideOptionValue(this.slide)('backgroundColorSet') !== true &&
                colorSet) {
                colorSet = {
                    ...colorSet,
                    back: '#000',
                    text: '#FFF',
                };
            }
            this.colorSet$.next(colorSet);
        }
    }
    reloadSlideStyles() {
        const colorSet = this.colorSet$.getValue();
        const set = new ColorSet(colorSet);
        this.slideStyles$.next({
            'color': set.textColor,
            'background-color': set.backColor,
            'font-family': this.textFont ? this.textFont.fontFamily : null,
        });
    }
    reloadBackground() {
        const colorSet = this.colorSet$.getValue();
        const set = new ColorSet(colorSet);
        let colorLight = new Color(set.backColor);
        let colorDark = colorLight.darkenOrLighten(20);
        if (colorLight.isDarkerThan(colorDark)) {
            const temp = colorLight;
            colorLight = colorDark;
            colorDark = temp;
        }
        const fill = getSlideOptionValue(this.slide)('backgroundFill') ||
            (this.defaultSlideOptions
                ? this.defaultSlideOptions['backgroundFill']
                : null);
        switch (fill) {
            case 'radial':
                {
                    this.backgroundStyles$.next({
                        'background': `radial-gradient(${colorLight.val()}, ${colorDark.val()})`,
                    });
                }
                break;
            case 'horizontal':
                {
                    this.backgroundStyles$.next({
                        'background': `linear-gradient(to left, ${colorLight.val()}, ${colorDark.val()})`,
                    });
                }
                break;
            case 'vertical':
                {
                    this.backgroundStyles$.next({
                        'background': `linear-gradient(to top, ${colorLight.val()}, ${colorDark.val()})`,
                    });
                }
                break;
            case 'diagonal':
                {
                    this.backgroundStyles$.next({
                        'background': `linear-gradient(to top left, ${colorLight.val()}, ${colorDark.val()})`,
                    });
                }
                break;
            default:
                {
                    this.backgroundStyles$.next({});
                }
                break;
        }
    }
    reloadHeadingFont() {
        this.headingFontFamily$.next((this.headingFont && this.headingFont.fontFamily) || null);
    }
    loadHeadingFontStylesheet(headingFontFamily) {
        if (this.headingFontStyleSheet) {
            this.headingFontStyleSheet.clear();
            delete this.headingFontStyleSheet;
        }
        if (headingFontFamily) {
            const sheet = (this.headingFontStyleSheet = cssx(`heading-font-${this.instanceId}`));
            sheet.scope(`#${this.instanceId}`);
            sheet.add(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].reduce((rules, tag) => {
                rules[tag] = { 'font-family': headingFontFamily };
                return rules;
            }, {}));
        }
    }
    loadTextColorStylesheet(colorSet) {
        if (this.textColorsStyleSheet) {
            this.textColorsStyleSheet.clear();
            delete this.textColorsStyleSheet;
        }
        if (colorSet) {
            const sheet = (this.textColorsStyleSheet = cssx(`text-colors-${this.instanceId}`));
            sheet.scope(`#${this.instanceId}`);
            sheet.add(Object.keys(colorSet).reduce((acc, colorKey) => ({
                ...acc,
                [`.textcolor-${colorKey}`]: {
                    'color': `${colorSet[colorKey]} !important`,
                },
                [`.textbackground-${colorKey}`]: {
                    'background-color': `${colorSet[colorKey]} !important`,
                },
            }), {}));
        }
    }
    reloadLogo() {
        const colorSet = this.colorSet$.getValue();
        const logoPosition = getSlideOptionValue(this.slide)('logoPosition') ||
            (this.defaultSlideOptions
                ? this.defaultSlideOptions['logoPosition']
                : null);
        this.logoPosition$.next(logoPosition || 'top-right');
        if (logoPosition !== 'off') {
            const set = new ColorSet(colorSet);
            this.logoUrl$.next(new Color(set.backColor).isLight()
                ? this.logoLightBg || this.logoDarkBg
                : this.logoDarkBg || this.logoLightBg);
        }
        else {
            this.logoUrl$.next(null);
        }
    }
    trackSlideElementById(index, slideElement) {
        return slideElement.id;
    }
    activate() {
        this.slideElementComponents.forEach(component => component.isActive ? component.activate() : component.deactivate());
    }
    deactivate() {
        this.slideElementComponents.forEach(component => component.deactivate());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlideComponent, selector: "sb-evo-slide", inputs: { slide: "slide", slideElements: "slideElements", slideOptions: "slideOptions", defaultElementVariations: "defaultElementVariations", defaultSlideOptions: "defaultSlideOptions", palette: "palette", colorSetOffset: "colorSetOffset", textFont: "textFont", headingFont: "headingFont", logoLightBg: "logoLightBg", logoDarkBg: "logoDarkBg", isPrevious: "isPrevious", isActive: "isActive", isNext: "isNext", currentStep: "currentStep" }, outputs: { onElementActivationEnded: "onElementActivationEnded" }, host: { properties: { "id": "this.instanceId", "attr.data-sb-slide-id": "this.slideId" } }, viewQueries: [{ propertyName: "slideElementComponents", predicate: SlideElementHostComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"slide__wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <!-- Background -->\n  <div\n    class=\"slide__background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Logo -->\n  <div\n    *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n    class=\"logo logo--{{ logoPosition$ | async }}\"\n  >\n    <figure\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n  </div>\n\n  <ng-template\n    ngFor\n    [ngForOf]=\"elements$ | async\"\n    [ngForTrackBy]=\"trackSlideElementById\"\n    let-element\n  >\n    <sb-evo-slide-element-host\n      class=\"slide__element\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-animation-step]=\"\n        (element.options && element.options.animationStep) || 0\n      \"\n      [slide]=\"slide\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [defaultVariations]=\"defaultElementVariations\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [isActive]=\"\n        isActive &&\n        currentStep ===\n          ((element.options && element.options.animationStep) || 0)\n      \"\n      [class.slide__element--visible]=\"\n        isPrevious ||\n        (isActive &&\n          currentStep >=\n            ((element.options && element.options.animationStep) || 0))\n      \"\n      [class.slide__element--animated]=\"\n        ((element.options && element.options.animationStep) || 0) > 0\n      \"\n      (onActivationEnded)=\"onElementActivationEnded.emit($event)\"\n    ></sb-evo-slide-element-host>\n  </ng-template>\n</div>\n", styles: [".slide__wrapper{position:relative;width:100%;height:100%;overflow:hidden;contain:content}.slide__background{position:absolute;left:0;top:0;width:100%;height:100%}.slide__element{display:block;position:absolute;z-index:1;left:0;top:0;width:auto;height:auto;pointer-events:none}.slide__element[data-element-type=background]{z-index:0;width:100%;height:100%}.slide__element[data-element-type=footer]{top:auto;bottom:0;width:100%}.slide__element[data-element-type=chart-bar],.slide__element[data-element-type=chart-line],.slide__element[data-element-type=google-doc],.slide__element[data-element-type=video]{width:50%;height:50%}.slide__element[data-element-type=chart-pie]{width:40%;height:64%}.slide__element[data-element-type=line]{top:0;left:0;width:1px;height:1px;pointer-events:none}.slide__element--animated{transition:opacity .3s;opacity:0}.slide__element--visible{opacity:1;pointer-events:initial}.logo{position:absolute;z-index:1000;width:8em;height:6em;padding:1em;transition:.3s}.logo>figure{width:100%;height:100%;margin:0;background:no-repeat;background-size:contain}.logo--top-left{left:0;top:0}.logo--top-left>figure{background-position:top left}.logo--top-center{left:50%;top:0;transform:translate(-50%)}.logo--top-center>figure{background-position:top center}.logo--top-right{left:100%;top:0;transform:translate(-100%)}.logo--top-right>figure{background-position:top right}.logo--bottom-left{left:0;top:100%;transform:translateY(-100%)}.logo--bottom-left>figure{background-position:bottom left}.logo--bottom-center{left:50%;top:100%;transform:translate(-50%,-100%)}.logo--bottom-center>figure{background-position:bottom center}.logo--bottom-right{left:100%;top:100%;transform:translate(-100%,-100%)}.logo--bottom-right>figure{background-position:bottom right}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i3.PreloadImageDirective, selector: "[sbPreloadImage]", inputs: ["sbPreloadImage"] }, { kind: "component", type: SlideElementHostComponent, selector: "sb-evo-slide-element-host", inputs: ["slide", "slideOptions", "element", "elementData", "elementOptions", "defaultVariations", "colorSet", "textFont", "headingFont", "isActive"], outputs: ["onActivationEnded"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-slide', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"slide__wrapper\" [ngStyle]=\"slideStyles$ | async\">\n  <!-- Background -->\n  <div\n    class=\"slide__background\"\n    [ngStyle]=\"backgroundStyles$ | async\"\n    [ngClass]=\"backgroundClasses$ | async\"\n  ></div>\n\n  <!-- Logo -->\n  <div\n    *ngIf=\"(logoPosition$ | async) !== 'off' && (logoUrl$ | async)\"\n    class=\"logo logo--{{ logoPosition$ | async }}\"\n  >\n    <figure\n      [style.background-image]=\"'url(' + (logoUrl$ | async) + ')'\"\n      [sbPreloadImage]=\"logoUrl$ | async\"\n    ></figure>\n  </div>\n\n  <ng-template\n    ngFor\n    [ngForOf]=\"elements$ | async\"\n    [ngForTrackBy]=\"trackSlideElementById\"\n    let-element\n  >\n    <sb-evo-slide-element-host\n      class=\"slide__element\"\n      [attr.data-element-type]=\"element.type\"\n      [attr.data-animation-step]=\"\n        (element.options && element.options.animationStep) || 0\n      \"\n      [slide]=\"slide\"\n      [slideOptions]=\"slide.options\"\n      [element]=\"element\"\n      [elementData]=\"element.data\"\n      [elementOptions]=\"element.options\"\n      [defaultVariations]=\"defaultElementVariations\"\n      [colorSet]=\"colorSet$ | async\"\n      [textFont]=\"textFont\"\n      [headingFont]=\"headingFont\"\n      [isActive]=\"\n        isActive &&\n        currentStep ===\n          ((element.options && element.options.animationStep) || 0)\n      \"\n      [class.slide__element--visible]=\"\n        isPrevious ||\n        (isActive &&\n          currentStep >=\n            ((element.options && element.options.animationStep) || 0))\n      \"\n      [class.slide__element--animated]=\"\n        ((element.options && element.options.animationStep) || 0) > 0\n      \"\n      (onActivationEnded)=\"onElementActivationEnded.emit($event)\"\n    ></sb-evo-slide-element-host>\n  </ng-template>\n</div>\n", styles: [".slide__wrapper{position:relative;width:100%;height:100%;overflow:hidden;contain:content}.slide__background{position:absolute;left:0;top:0;width:100%;height:100%}.slide__element{display:block;position:absolute;z-index:1;left:0;top:0;width:auto;height:auto;pointer-events:none}.slide__element[data-element-type=background]{z-index:0;width:100%;height:100%}.slide__element[data-element-type=footer]{top:auto;bottom:0;width:100%}.slide__element[data-element-type=chart-bar],.slide__element[data-element-type=chart-line],.slide__element[data-element-type=google-doc],.slide__element[data-element-type=video]{width:50%;height:50%}.slide__element[data-element-type=chart-pie]{width:40%;height:64%}.slide__element[data-element-type=line]{top:0;left:0;width:1px;height:1px;pointer-events:none}.slide__element--animated{transition:opacity .3s;opacity:0}.slide__element--visible{opacity:1;pointer-events:initial}.logo{position:absolute;z-index:1000;width:8em;height:6em;padding:1em;transition:.3s}.logo>figure{width:100%;height:100%;margin:0;background:no-repeat;background-size:contain}.logo--top-left{left:0;top:0}.logo--top-left>figure{background-position:top left}.logo--top-center{left:50%;top:0;transform:translate(-50%)}.logo--top-center>figure{background-position:top center}.logo--top-right{left:100%;top:0;transform:translate(-100%)}.logo--top-right>figure{background-position:top right}.logo--bottom-left{left:0;top:100%;transform:translateY(-100%)}.logo--bottom-left>figure{background-position:bottom left}.logo--bottom-center{left:50%;top:100%;transform:translate(-50%,-100%)}.logo--bottom-center>figure{background-position:bottom center}.logo--bottom-right{left:100%;top:100%;transform:translate(-100%,-100%)}.logo--bottom-right>figure{background-position:bottom right}\n"] }]
        }], propDecorators: { slide: [{
                type: Input
            }], slideElements: [{
                type: Input
            }], slideOptions: [{
                type: Input
            }], defaultElementVariations: [{
                type: Input
            }], defaultSlideOptions: [{
                type: Input
            }], palette: [{
                type: Input
            }], colorSetOffset: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], logoLightBg: [{
                type: Input
            }], logoDarkBg: [{
                type: Input
            }], isPrevious: [{
                type: Input
            }], isActive: [{
                type: Input
            }], isNext: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], onElementActivationEnded: [{
                type: Output
            }], slideElementComponents: [{
                type: ViewChildren,
                args: [SlideElementHostComponent]
            }], instanceId: [{
                type: HostBinding,
                args: ['id']
            }], slideId: [{
                type: HostBinding,
                args: ['attr.data-sb-slide-id']
            }] } });

const DEFAULT_CONFIG = {
    slideHeight: 1000,
    slideWidth: 1600,
};
const RENDER_EVO_CONFIG = new InjectionToken('__RENDER_EVO_CONFIG__');

class EvoSlideViewerComponent {
    get colorSetOffset() {
        return this._colorSetOffset;
    }
    set colorSetOffset(value) {
        this._colorSetOffset = Number.isInteger(+value) ? +value : 0;
    }
    static { this.scaleCache = new Map(); }
    constructor(fontLoader, ngZone, detector, config) {
        this.fontLoader = fontLoader;
        this.ngZone = ngZone;
        this.detector = detector;
        this.config = config;
        this.currentIndex = 0;
        this.currentStep = 999;
        this._colorSetOffset = 0;
        this.isFullScreen = false;
        this.lazyLoad = true;
        this.onElementActivationEnded = new EventEmitter();
        this.reloadSlides$ = new Subject();
        this.reloadFonts$ = new Subject();
        this.reloadSize$ = new Subject();
        this.destroy$ = new Subject();
        this.viewerSlides = [];
        this.viewerScale$ = new Subject();
    }
    ngOnChanges(changes) {
        if (didChange(changes['slides']) || didChange(changes['slide'])) {
            this.reloadSlides$.next();
        }
        if (didChange(changes['textFont']) || didChange(changes['headingFont'])) {
            this.reloadFonts$.next();
        }
    }
    ngOnInit() {
        const isVisible = () => this.lazyLoader
            ? this.lazyLoader.isVisible$.pipe(filter(v => !!v), take(1))
            : empty();
        this.reloadSlides$
            .pipe(switchMap(isVisible), debounceTime(100), takeUntil(this.destroy$))
            .subscribe(this.reloadSlides.bind(this));
        this.reloadFonts$
            .pipe(switchMap(isVisible), debounceTime(100), takeUntil(this.destroy$))
            .subscribe(this.reloadFonts.bind(this));
        this.reloadSize$
            .pipe(switchMap(isVisible), takeUntil(this.destroy$))
            .subscribe(this.reloadSize.bind(this));
        this.viewerScale$
            .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(scale => requestAnimationFrame(() => {
            const transform = `translate(-50%, -50%) scale(${scale})`;
            this.sizer.nativeElement.style['transform'] = transform;
            ['webkit', 'ms'].forEach(prefix => {
                this.sizer.nativeElement.style[`${prefix}Transform`] = transform;
            });
        }));
        // Boot
        this.reloadSlides$.next();
        this.reloadFonts$.next();
    }
    ngAfterViewInit() {
        this.reloadSize$.next();
        window.requestIdleCallback(() => {
            watchResize(this.wrapper.nativeElement)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.reloadSize$.next());
        });
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    trackSlidesBy(index, slide) {
        return slide.id;
    }
    reloadSlides() {
        this.viewerSlides = this.slide
            ? [this.slide]
            : this.slides
                ? [...this.slides]
                : [];
        this.detector.detectChanges();
    }
    reloadFonts() {
        if (this.textFont) {
            this.fontLoader.load(this.textFont);
        }
        if (this.headingFont) {
            this.fontLoader.load(this.headingFont);
        }
    }
    reloadSize() {
        this.ngZone.runOutsideAngular(() => {
            const wrapperWidth = this.wrapper.nativeElement.offsetWidth;
            const wrapperHeight = this.wrapper.nativeElement.offsetHeight;
            if (!wrapperWidth || !wrapperHeight) {
                return requestAnimationFrame(this.reloadSize.bind(this));
            }
            const scaleCacheKey = `${wrapperWidth}x${wrapperHeight}`;
            let scale;
            if (EvoSlideViewerComponent.scaleCache.has(scaleCacheKey)) {
                scale = EvoSlideViewerComponent.scaleCache.get(scaleCacheKey);
            }
            else {
                const slideWidthToHeightRatio = this.config.slideWidth / this.config.slideHeight;
                if (wrapperWidth > slideWidthToHeightRatio * wrapperHeight) {
                    scale = wrapperHeight / this.config.slideHeight;
                }
                else {
                    scale = wrapperWidth / this.config.slideWidth;
                }
                // Ceil to 3 decimals
                // XXX: More than 3 decimals causes undesired, random outlines
                // around the slide wrapper.
                scale = Math.ceil(scale * 1000) / 1000;
                EvoSlideViewerComponent.scaleCache.set(scaleCacheKey, scale);
            }
            this.viewerScale$.next(scale);
        });
    }
    activate() {
        const slideComponent = this.slideComponents.find((_, index) => index === (this.currentIndex === 0 ? 0 : 1));
        if (slideComponent) {
            slideComponent.activate();
        }
    }
    deactivate() {
        const slideComponent = this.slideComponents.find((_, index) => index === (this.currentIndex === 0 ? 0 : 1));
        if (slideComponent) {
            slideComponent.deactivate();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EvoSlideViewerComponent, deps: [{ token: i1$4.FontLoader }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: RENDER_EVO_CONFIG }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: EvoSlideViewerComponent, selector: "sb-evo-slide-viewer", inputs: { slides: "slides", slide: "slide", formula: "formula", palette: "palette", textFont: "textFont", headingFont: "headingFont", logoLightBg: "logoLightBg", logoDarkBg: "logoDarkBg", currentIndex: "currentIndex", currentStep: "currentStep", colorSetOffset: "colorSetOffset", isFullScreen: "isFullScreen", lazyLoad: "lazyLoad" }, outputs: { onElementActivationEnded: "onElementActivationEnded" }, viewQueries: [{ propertyName: "wrapper", first: true, predicate: ["wrapper"], descendants: true, static: true }, { propertyName: "sizer", first: true, predicate: ["sizer"], descendants: true, static: true }, { propertyName: "lazyLoader", first: true, predicate: LazyLoadComponent, descendants: true, static: true }, { propertyName: "slideComponents", predicate: SlideComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  #wrapper\n  class=\"viewer__wrapper\"\n  [class.viewer__wrapper--full-screen]=\"isFullScreen\"\n>\n  <div #sizer class=\"viewer__sizer\">\n    <sb-lazy-load [enabled]=\"lazyLoad\">\n      <ng-template\n        ngFor\n        [ngForOf]=\"viewerSlides\"\n        [ngForTrackBy]=\"trackSlidesBy\"\n        let-slide\n        let-slideIndex=\"index\"\n      >\n        <sb-evo-slide\n          *ngIf=\"\n            currentIndex - 1 <= slideIndex && slideIndex <= currentIndex + 1\n          \"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [defaultElementVariations]=\"formula?.defaultElementVariations\"\n          [defaultSlideOptions]=\"formula?.defaultSlideOptions\"\n          [palette]=\"palette\"\n          [colorSetOffset]=\"colorSetOffset + slideIndex\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [logoLightBg]=\"logoLightBg\"\n          [logoDarkBg]=\"logoDarkBg\"\n          [currentStep]=\"currentStep\"\n          [isPrevious]=\"slideIndex === currentIndex - 1\"\n          [isActive]=\"slideIndex === currentIndex\"\n          [isNext]=\"slideIndex === currentIndex + 1\"\n          [class.previous]=\"slideIndex === currentIndex - 1\"\n          [class.active]=\"slideIndex === currentIndex\"\n          [class.next]=\"slideIndex === currentIndex + 1\"\n          (onElementActivationEnded)=\"onElementActivationEnded.emit($event)\"\n        ></sb-evo-slide>\n      </ng-template>\n    </sb-lazy-load>\n  </div>\n</div>\n", styles: ["@keyframes next{0%{z-index:1}to{z-index:0}}:host{display:block;width:100%}.viewer__wrapper{position:relative;width:100%;height:0;padding-bottom:62.5%;overflow:hidden;contain:strict;-webkit-user-select:none;user-select:none;font-size:36px;overflow-wrap:initial}.viewer__wrapper.viewer__wrapper--full-screen{position:absolute;left:0;top:0;height:100%;padding:0;background:#111}.viewer__sizer{display:block;position:absolute;left:50%;top:50%;transform-origin:center;transform:translate(-50%,-50%) scale(0);overflow:hidden;width:1600px;height:1000px}sb-evo-slide{display:block;position:absolute;left:0;top:0;width:100%;height:100%;transition:opacity .3s}sb-evo-slide.previous{opacity:1;z-index:1}sb-evo-slide.active{opacity:1;z-index:1}sb-evo-slide.next{opacity:0;animation:next ease .3s}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: LazyLoadComponent, selector: "sb-lazy-load", inputs: ["threshold", "enabled"] }, { kind: "component", type: SlideComponent, selector: "sb-evo-slide", inputs: ["slide", "slideElements", "slideOptions", "defaultElementVariations", "defaultSlideOptions", "palette", "colorSetOffset", "textFont", "headingFont", "logoLightBg", "logoDarkBg", "isPrevious", "isActive", "isNext", "currentStep"], outputs: ["onElementActivationEnded"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EvoSlideViewerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-slide-viewer', template: "<div\n  #wrapper\n  class=\"viewer__wrapper\"\n  [class.viewer__wrapper--full-screen]=\"isFullScreen\"\n>\n  <div #sizer class=\"viewer__sizer\">\n    <sb-lazy-load [enabled]=\"lazyLoad\">\n      <ng-template\n        ngFor\n        [ngForOf]=\"viewerSlides\"\n        [ngForTrackBy]=\"trackSlidesBy\"\n        let-slide\n        let-slideIndex=\"index\"\n      >\n        <sb-evo-slide\n          *ngIf=\"\n            currentIndex - 1 <= slideIndex && slideIndex <= currentIndex + 1\n          \"\n          [slide]=\"slide\"\n          [slideElements]=\"slide.elements\"\n          [slideOptions]=\"slide.options\"\n          [defaultElementVariations]=\"formula?.defaultElementVariations\"\n          [defaultSlideOptions]=\"formula?.defaultSlideOptions\"\n          [palette]=\"palette\"\n          [colorSetOffset]=\"colorSetOffset + slideIndex\"\n          [textFont]=\"textFont\"\n          [headingFont]=\"headingFont\"\n          [logoLightBg]=\"logoLightBg\"\n          [logoDarkBg]=\"logoDarkBg\"\n          [currentStep]=\"currentStep\"\n          [isPrevious]=\"slideIndex === currentIndex - 1\"\n          [isActive]=\"slideIndex === currentIndex\"\n          [isNext]=\"slideIndex === currentIndex + 1\"\n          [class.previous]=\"slideIndex === currentIndex - 1\"\n          [class.active]=\"slideIndex === currentIndex\"\n          [class.next]=\"slideIndex === currentIndex + 1\"\n          (onElementActivationEnded)=\"onElementActivationEnded.emit($event)\"\n        ></sb-evo-slide>\n      </ng-template>\n    </sb-lazy-load>\n  </div>\n</div>\n", styles: ["@keyframes next{0%{z-index:1}to{z-index:0}}:host{display:block;width:100%}.viewer__wrapper{position:relative;width:100%;height:0;padding-bottom:62.5%;overflow:hidden;contain:strict;-webkit-user-select:none;user-select:none;font-size:36px;overflow-wrap:initial}.viewer__wrapper.viewer__wrapper--full-screen{position:absolute;left:0;top:0;height:100%;padding:0;background:#111}.viewer__sizer{display:block;position:absolute;left:50%;top:50%;transform-origin:center;transform:translate(-50%,-50%) scale(0);overflow:hidden;width:1600px;height:1000px}sb-evo-slide{display:block;position:absolute;left:0;top:0;width:100%;height:100%;transition:opacity .3s}sb-evo-slide.previous{opacity:1;z-index:1}sb-evo-slide.active{opacity:1;z-index:1}sb-evo-slide.next{opacity:0;animation:next ease .3s}\n"] }]
        }], ctorParameters: () => [{ type: i1$4.FontLoader }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [RENDER_EVO_CONFIG]
                }] }], propDecorators: { wrapper: [{
                type: ViewChild,
                args: ['wrapper', { static: true }]
            }], sizer: [{
                type: ViewChild,
                args: ['sizer', { static: true }]
            }], slideComponents: [{
                type: ViewChildren,
                args: [SlideComponent]
            }], lazyLoader: [{
                type: ViewChild,
                args: [LazyLoadComponent, { static: true }]
            }], slides: [{
                type: Input
            }], slide: [{
                type: Input
            }], formula: [{
                type: Input
            }], palette: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], logoLightBg: [{
                type: Input
            }], logoDarkBg: [{
                type: Input
            }], currentIndex: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], colorSetOffset: [{
                type: Input
            }], isFullScreen: [{
                type: Input
            }], lazyLoad: [{
                type: Input
            }], onElementActivationEnded: [{
                type: Output
            }] } });

const SLIDE_CHANGED_EVENT = 'client-slide-changed';
var LiveMode;
(function (LiveMode) {
    LiveMode["Off"] = "off";
    LiveMode["Sync"] = "sync";
    LiveMode["Broadcast"] = "broadcast";
})(LiveMode || (LiveMode = {}));
class LiveButtonComponent {
    get mode() {
        return this._mode ?? this.initialMode;
    }
    constructor(pusher) {
        this.pusher = pusher;
        this.isBroadcastEnabled = false;
        this.LiveMode = LiveMode;
        this.initialMode = LiveMode.Sync;
        this.onGoToSlide = new EventEmitter();
        this.onPusherEvent = new EventEmitter();
        this.destroy$ = new Subject();
    }
    ngOnChanges(changes) {
        if (changes['presentationId']) {
            this.subscribe({ presentationId: this.presentationId });
        }
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.unsubscribe();
    }
    subscribe({ presentationId }) {
        this.unsubscribe();
        if (presentationId) {
            this.pusherChannelName = `private-player-${presentationId}`;
            this.pusher
                .subscribe(this.pusherChannelName, SLIDE_CHANGED_EVENT)
                .pipe(takeUntil(this.destroy$))
                .subscribe(event => this._onPusherEvent(event));
        }
    }
    unsubscribe() {
        if (this.pusherChannelName) {
            this.pusher.unsubscribe(this.pusherChannelName);
            this.pusherChannelName = null;
        }
    }
    setLiveMode(mode) {
        if (mode === LiveMode.Broadcast && !this.isBroadcastEnabled)
            return;
        this._mode = mode;
    }
    notifySlideChange({ slideIndex, slideStep }) {
        if (this.mode === LiveMode.Broadcast && this.pusherChannelName) {
            this.pusher.trigger(this.pusherChannelName, SLIDE_CHANGED_EVENT, {
                index: slideIndex,
                step: slideStep,
            });
        }
    }
    _onPusherEvent(event) {
        if (this.mode === LiveMode.Off)
            return;
        switch (event.name) {
            case SLIDE_CHANGED_EVENT:
                this.onGoToSlide.emit({
                    slideIndex: event.data.index,
                    slideStep: event.data.step,
                });
                break;
        }
        this.onPusherEvent.emit(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: LiveButtonComponent, deps: [{ token: i1$5.PusherService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: LiveButtonComponent, selector: "sb-evo-live-button", inputs: { presentationId: "presentationId", isBroadcastEnabled: "isBroadcastEnabled", initialMode: "initialMode" }, outputs: { onGoToSlide: "onGoToSlide", onPusherEvent: "onPusherEvent" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"btn-group dropup\">\n  <button\n    type=\"button\"\n    class=\"btn btn-xs text-uppercase ml-2\"\n    [class.btn-outline-secondary]=\"mode === LiveMode.Off\"\n    [class.btn-outline-danger]=\"mode === LiveMode.Sync\"\n    [class.btn-danger]=\"mode === LiveMode.Broadcast\"\n    data-toggle=\"dropdown\"\n    aria-haspopup=\"true\"\n    aria-expanded=\"false\"\n  >\n    <i class=\"fal fa-signal\"></i>\n    {{ 'Live' | translate }}\n  </button>\n  <div class=\"dropdown-menu dropdown-menu-right\">\n    <a\n      *ngIf=\"isBroadcastEnabled\"\n      class=\"dropdown-item pl-2\"\n      href=\"javascript:void(0)\"\n      (click)=\"setLiveMode(LiveMode.Broadcast)\"\n    >\n      <i class=\"fal fa-fw\" [class.fa-check]=\"mode === LiveMode.Broadcast\"></i>\n      {{ 'Make all viewers follow my slides' | translate }}\n    </a>\n    <a\n      class=\"dropdown-item pl-2\"\n      href=\"javascript:void(0)\"\n      (click)=\"setLiveMode(LiveMode.Sync)\"\n    >\n      <i class=\"fal fa-fw\" [class.fa-check]=\"mode === LiveMode.Sync\"></i>\n      {{ 'Keep in sync with the presenter' | translate }}\n    </a>\n    <a\n      class=\"dropdown-item pl-2\"\n      href=\"javascript:void(0)\"\n      (click)=\"setLiveMode(LiveMode.Off)\"\n    >\n      <i class=\"fal fa-fw\" [class.fa-check]=\"mode === LiveMode.Off\"></i>\n      {{ 'Turn off' | translate }}\n    </a>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i3$1.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: LiveButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-live-button', template: "<div class=\"btn-group dropup\">\n  <button\n    type=\"button\"\n    class=\"btn btn-xs text-uppercase ml-2\"\n    [class.btn-outline-secondary]=\"mode === LiveMode.Off\"\n    [class.btn-outline-danger]=\"mode === LiveMode.Sync\"\n    [class.btn-danger]=\"mode === LiveMode.Broadcast\"\n    data-toggle=\"dropdown\"\n    aria-haspopup=\"true\"\n    aria-expanded=\"false\"\n  >\n    <i class=\"fal fa-signal\"></i>\n    {{ 'Live' | translate }}\n  </button>\n  <div class=\"dropdown-menu dropdown-menu-right\">\n    <a\n      *ngIf=\"isBroadcastEnabled\"\n      class=\"dropdown-item pl-2\"\n      href=\"javascript:void(0)\"\n      (click)=\"setLiveMode(LiveMode.Broadcast)\"\n    >\n      <i class=\"fal fa-fw\" [class.fa-check]=\"mode === LiveMode.Broadcast\"></i>\n      {{ 'Make all viewers follow my slides' | translate }}\n    </a>\n    <a\n      class=\"dropdown-item pl-2\"\n      href=\"javascript:void(0)\"\n      (click)=\"setLiveMode(LiveMode.Sync)\"\n    >\n      <i class=\"fal fa-fw\" [class.fa-check]=\"mode === LiveMode.Sync\"></i>\n      {{ 'Keep in sync with the presenter' | translate }}\n    </a>\n    <a\n      class=\"dropdown-item pl-2\"\n      href=\"javascript:void(0)\"\n      (click)=\"setLiveMode(LiveMode.Off)\"\n    >\n      <i class=\"fal fa-fw\" [class.fa-check]=\"mode === LiveMode.Off\"></i>\n      {{ 'Turn off' | translate }}\n    </a>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i1$5.PusherService }], propDecorators: { presentationId: [{
                type: Input
            }], isBroadcastEnabled: [{
                type: Input
            }], initialMode: [{
                type: Input
            }], onGoToSlide: [{
                type: Output
            }], onPusherEvent: [{
                type: Output
            }] } });

class AutoPlayButtonComponent {
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        this.arePlayableElementsActive = false;
        this.hasReachedEnd = false;
        this.onStart = new EventEmitter();
        this.onNextStep = new EventEmitter();
        this.isAutoPlaying = false;
        this.autoPlaySeconds = 3;
        this.autoPlayTimeout = null;
    }
    stop() {
        this.isAutoPlaying = false;
        if (this.autoPlayTimeout) {
            clearTimeout(this.autoPlayTimeout);
            this.autoPlayTimeout = null;
        }
    }
    play(seconds) {
        this.stop();
        this.onStart.emit();
        this.isAutoPlaying = true;
        this.autoPlaySeconds = seconds || this.autoPlaySeconds || 3;
        setTimeout(() => this.scheduleNext());
    }
    playCustom() {
        // TODO: Localization
        // const seconds = prompt(Localization.getString('How much time (in seconds) between steps?'), '10');
        const result = prompt('How much time (in seconds) between steps?', '10');
        if (result) {
            const seconds = parseInt(result, 10);
            if (!isNaN(seconds) && seconds > 0) {
                this.play(seconds);
            }
        }
    }
    scheduleNext() {
        if (this.isAutoPlaying && !this.hasReachedEnd) {
            this.autoPlayTimeout = setTimeout(() => this.doNext(), this.autoPlaySeconds * 1000);
        }
        else {
            this.stop();
        }
        this.changeDetector.detectChanges();
    }
    doNext() {
        if (!this.arePlayableElementsActive) {
            // XXX: Slides with videos or audio advance when they finish
            // by triggering 'onElementActivationEnded'
            this.onNextStep.emit();
            setTimeout(() => this.scheduleNext());
        }
    }
    resume() {
        if (this.isAutoPlaying) {
            if (this.autoPlayTimeout) {
                clearTimeout(this.autoPlayTimeout);
            }
            this.onNextStep.emit();
            setTimeout(() => this.scheduleNext());
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AutoPlayButtonComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: AutoPlayButtonComponent, selector: "sb-evo-auto-play-button", inputs: { arePlayableElementsActive: "arePlayableElementsActive", hasReachedEnd: "hasReachedEnd" }, outputs: { onStart: "onStart", onNextStep: "onNextStep" }, ngImport: i0, template: "<div *ngIf=\"!isAutoPlaying\" class=\"btn-group dropup\">\n  <button\n    type=\"button\"\n    class=\"btn btn-link\"\n    data-toggle=\"dropdown\"\n    aria-haspopup=\"true\"\n    aria-expanded=\"false\"\n  >\n    <i class=\"far fa-clock\"></i>\n  </button>\n  <div class=\"dropdown-menu dropdown-menu-right\">\n    <h6 class=\"dropdown-header\" translate>Auto-play</h6>\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(3)\"\n      ><span translate>3 seconds</span></a\n    >\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(5)\"\n      ><span translate>5 seconds</span></a\n    >\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(10)\"\n      ><span translate>10 seconds</span></a\n    >\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"playCustom()\"\n      ><span translate>Specify time</span>\u2026</a\n    >\n  </div>\n</div>\n<!-- Stop auto-play -->\n<button\n  *ngIf=\"isAutoPlaying\"\n  type=\"button\"\n  class=\"btn btn-link\"\n  (click)=\"stop()\"\n>\n  <i class=\"far fa-pause\"></i>\n</button>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$1.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: AutoPlayButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-auto-play-button', template: "<div *ngIf=\"!isAutoPlaying\" class=\"btn-group dropup\">\n  <button\n    type=\"button\"\n    class=\"btn btn-link\"\n    data-toggle=\"dropdown\"\n    aria-haspopup=\"true\"\n    aria-expanded=\"false\"\n  >\n    <i class=\"far fa-clock\"></i>\n  </button>\n  <div class=\"dropdown-menu dropdown-menu-right\">\n    <h6 class=\"dropdown-header\" translate>Auto-play</h6>\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(3)\"\n      ><span translate>3 seconds</span></a\n    >\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(5)\"\n      ><span translate>5 seconds</span></a\n    >\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"play(10)\"\n      ><span translate>10 seconds</span></a\n    >\n    <a class=\"dropdown-item\" href=\"javascript:void(0)\" (click)=\"playCustom()\"\n      ><span translate>Specify time</span>\u2026</a\n    >\n  </div>\n</div>\n<!-- Stop auto-play -->\n<button\n  *ngIf=\"isAutoPlaying\"\n  type=\"button\"\n  class=\"btn btn-link\"\n  (click)=\"stop()\"\n>\n  <i class=\"far fa-pause\"></i>\n</button>\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { arePlayableElementsActive: [{
                type: Input
            }], hasReachedEnd: [{
                type: Input
            }], onStart: [{
                type: Output
            }], onNextStep: [{
                type: Output
            }] } });

class SlidePlayerProgressBarComponent {
    constructor(ngZone) {
        this.ngZone = ngZone;
        this.currentIndex = 0;
        this.currentStep = 0;
        this.currentSlideSteps = 1;
        this.hasReachedEnd = false;
        this.onGoToSlide = new EventEmitter();
        this.barWidth$ = new BehaviorSubject(0);
        this.barColor$ = new BehaviorSubject(null);
        this.barEmptyColor$ = new BehaviorSubject(null);
        this.hoverSlideIndex = 0;
    }
    ngOnChanges(changes) {
        const changed = (key) => changes[key] && changes[key].previousValue !== changes[key].currentValue;
        // Colors
        if (changed('colorSet')) {
            if (this.colorSet) {
                const set = new ColorSet(this.colorSet);
                this.barColor$.next(set.highlightColor);
                this.barEmptyColor$.next(set.backColor);
            }
        }
        // Bar width
        if (changed('currentIndex') ||
            changed('currentStep') ||
            changed('currentSlideSteps') ||
            changed('totalSlides') ||
            changed('hasReachedEnd')) {
            if (this.hasReachedEnd) {
                this.barWidth$.next(1);
            }
            else if (this.totalSlides) {
                const w = Math.max(0, Math.min(1, ((this.currentIndex || 0) +
                    (this.currentStep || 0) / (this.currentSlideSteps || 1)) /
                    this.totalSlides));
                this.barWidth$.next(w);
            }
            else {
                this.barWidth$.next(0);
            }
        }
    }
    onProgressBarMouseMove(event) {
        this.ngZone.runOutsideAngular(() => {
            if (this.totalSlides) {
                let ratioX = 0;
                const barWidth = event.currentTarget.offsetWidth;
                if (barWidth) {
                    ratioX = event.offsetX / barWidth;
                }
                this.hoverSlideIndex = Math.floor(ratioX * this.totalSlides);
                requestAnimationFrame(() => {
                    this.hoverIndicator.nativeElement.style.left = `${ratioX * 100}%`;
                    this.hoverIndicator.nativeElement.textContent = `${this.hoverSlideIndex + 1}`;
                });
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlidePlayerProgressBarComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlidePlayerProgressBarComponent, selector: "sb-evo-slide-player-progress-bar", inputs: { totalSlides: "totalSlides", currentIndex: "currentIndex", currentStep: "currentStep", currentSlideSteps: "currentSlideSteps", hasReachedEnd: "hasReachedEnd", colorSet: "colorSet" }, outputs: { onGoToSlide: "onGoToSlide" }, viewQueries: [{ propertyName: "hoverIndicator", first: true, predicate: ["hoverIndicator"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"progress__wrapper\"\n  (click)=\"onGoToSlide.emit({ slideIndex: hoverSlideIndex })\"\n  (mousemove)=\"onProgressBarMouseMove($event)\"\n>\n  <div #hoverIndicator class=\"progress__hover-indicator\"></div>\n  <div class=\"progress\" [style.background-color]=\"barEmptyColor$ | async\">\n    <div\n      class=\"progress-bar\"\n      [style.background-color]=\"barColor$ | async\"\n      [style.width.%]=\"(barWidth$ | async) * 100\"\n    ></div>\n  </div>\n</div>\n", styles: [".progress__wrapper{position:relative;z-index:1;width:100%;cursor:pointer;padding-top:30px;margin-top:-30px}.progress__wrapper .progress__hover-indicator{position:absolute;bottom:1rem;pointer-events:none;background:#000000b3;color:#fff;border-radius:.3rem;padding:.3rem .8rem;font-size:.8rem;transition:transform .3s,opacity .3s;transform:translate(-50%,100%) scale(0);opacity:0}.progress__wrapper .progress__hover-indicator:after{content:\"\";display:block;position:absolute;top:100%;left:50%;transform:translate(-50%);width:0;height:0;border-style:solid;border-width:.5rem .5rem 0 .5rem;border-color:rgba(0,0,0,.7) transparent transparent transparent}.progress__wrapper:hover .progress__hover-indicator{opacity:1;transform:translate(-50%) scale(1)}.progress__wrapper .progress{margin:0;border-radius:0;box-shadow:none;height:5px}.progress__wrapper .progress .progress-bar{height:100%;box-shadow:none;transition:width .3s}\n"], dependencies: [{ kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlidePlayerProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-slide-player-progress-bar', template: "<div\n  class=\"progress__wrapper\"\n  (click)=\"onGoToSlide.emit({ slideIndex: hoverSlideIndex })\"\n  (mousemove)=\"onProgressBarMouseMove($event)\"\n>\n  <div #hoverIndicator class=\"progress__hover-indicator\"></div>\n  <div class=\"progress\" [style.background-color]=\"barEmptyColor$ | async\">\n    <div\n      class=\"progress-bar\"\n      [style.background-color]=\"barColor$ | async\"\n      [style.width.%]=\"(barWidth$ | async) * 100\"\n    ></div>\n  </div>\n</div>\n", styles: [".progress__wrapper{position:relative;z-index:1;width:100%;cursor:pointer;padding-top:30px;margin-top:-30px}.progress__wrapper .progress__hover-indicator{position:absolute;bottom:1rem;pointer-events:none;background:#000000b3;color:#fff;border-radius:.3rem;padding:.3rem .8rem;font-size:.8rem;transition:transform .3s,opacity .3s;transform:translate(-50%,100%) scale(0);opacity:0}.progress__wrapper .progress__hover-indicator:after{content:\"\";display:block;position:absolute;top:100%;left:50%;transform:translate(-50%);width:0;height:0;border-style:solid;border-width:.5rem .5rem 0 .5rem;border-color:rgba(0,0,0,.7) transparent transparent transparent}.progress__wrapper:hover .progress__hover-indicator{opacity:1;transform:translate(-50%) scale(1)}.progress__wrapper .progress{margin:0;border-radius:0;box-shadow:none;height:5px}.progress__wrapper .progress .progress-bar{height:100%;box-shadow:none;transition:width .3s}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }], propDecorators: { totalSlides: [{
                type: Input
            }], currentIndex: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], currentSlideSteps: [{
                type: Input
            }], hasReachedEnd: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], onGoToSlide: [{
                type: Output
            }], hoverIndicator: [{
                type: ViewChild,
                args: ['hoverIndicator', { static: false }]
            }] } });

class SlidePlayerToolbarComponent {
    constructor() {
        this.isBackEnabled = true;
        this.isNextEnabled = true;
        this.isFullScreenVisible = true;
        this.onBack = new EventEmitter();
        this.onNext = new EventEmitter();
        this.onFullScreen = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlidePlayerToolbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: SlidePlayerToolbarComponent, selector: "sb-evo-slide-player-toolbar", inputs: { isBackEnabled: "isBackEnabled", isNextEnabled: "isNextEnabled", isFullScreenVisible: "isFullScreenVisible" }, outputs: { onBack: "onBack", onNext: "onNext", onFullScreen: "onFullScreen" }, ngImport: i0, template: "<div class=\"player-toolbar\">\n  <div class=\"player-toolbar__group player-toolbar__group--left\">\n    <ng-content select=\"[sb-evo-slide-player-toolbar-left]\"></ng-content>\n  </div>\n\n  <div class=\"player-toolbar__group player-toolbar__group--center\">\n    <!-- Back -->\n    <button\n      type=\"button\"\n      class=\"btn btn-link\"\n      [disabled]=\"!isBackEnabled\"\n      (click)=\"onBack.emit()\"\n    >\n      <i class=\"far fa-chevron-left\"></i>\n    </button>\n    <!-- Next -->\n    <button\n      type=\"button\"\n      class=\"btn btn-link\"\n      [disabled]=\"!isNextEnabled\"\n      (click)=\"onNext.emit()\"\n    >\n      <i class=\"far fa-chevron-right\"></i>\n    </button>\n  </div>\n\n  <div class=\"player-toolbar__group player-toolbar__group--right\">\n    <ng-content select=\"[sb-evo-slide-player-toolbar-right]\"></ng-content>\n    <!-- Full screen -->\n    <button\n      *ngIf=\"isFullScreenVisible\"\n      type=\"button\"\n      class=\"btn btn-link\"\n      (click)=\"onFullScreen.emit()\"\n    >\n      <i class=\"far fa-expand-wide\"></i>\n    </button>\n  </div>\n</div>\n", styles: [".player-toolbar{position:relative;width:100%;display:flex;flex-flow:row nowrap;align-items:center;padding:.25rem 0}.player-toolbar__group--center{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)}.player-toolbar__group--right{margin-left:auto}.btn{cursor:pointer}.btn [disabled]{cursor:default;opacity:.5}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: SlidePlayerToolbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-slide-player-toolbar', template: "<div class=\"player-toolbar\">\n  <div class=\"player-toolbar__group player-toolbar__group--left\">\n    <ng-content select=\"[sb-evo-slide-player-toolbar-left]\"></ng-content>\n  </div>\n\n  <div class=\"player-toolbar__group player-toolbar__group--center\">\n    <!-- Back -->\n    <button\n      type=\"button\"\n      class=\"btn btn-link\"\n      [disabled]=\"!isBackEnabled\"\n      (click)=\"onBack.emit()\"\n    >\n      <i class=\"far fa-chevron-left\"></i>\n    </button>\n    <!-- Next -->\n    <button\n      type=\"button\"\n      class=\"btn btn-link\"\n      [disabled]=\"!isNextEnabled\"\n      (click)=\"onNext.emit()\"\n    >\n      <i class=\"far fa-chevron-right\"></i>\n    </button>\n  </div>\n\n  <div class=\"player-toolbar__group player-toolbar__group--right\">\n    <ng-content select=\"[sb-evo-slide-player-toolbar-right]\"></ng-content>\n    <!-- Full screen -->\n    <button\n      *ngIf=\"isFullScreenVisible\"\n      type=\"button\"\n      class=\"btn btn-link\"\n      (click)=\"onFullScreen.emit()\"\n    >\n      <i class=\"far fa-expand-wide\"></i>\n    </button>\n  </div>\n</div>\n", styles: [".player-toolbar{position:relative;width:100%;display:flex;flex-flow:row nowrap;align-items:center;padding:.25rem 0}.player-toolbar__group--center{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)}.player-toolbar__group--right{margin-left:auto}.btn{cursor:pointer}.btn [disabled]{cursor:default;opacity:.5}\n"] }]
        }], propDecorators: { isBackEnabled: [{
                type: Input
            }], isNextEnabled: [{
                type: Input
            }], isFullScreenVisible: [{
                type: Input
            }], onBack: [{
                type: Output
            }], onNext: [{
                type: Output
            }], onFullScreen: [{
                type: Output
            }] } });

class OutroComponent {
    constructor() {
        this.onPlayAgain = new EventEmitter();
        this.onExit = new EventEmitter();
        this.isExitVisible = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: OutroComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: OutroComponent, selector: "sb-evo-outro", inputs: { isExitVisible: "isExitVisible" }, outputs: { onPlayAgain: "onPlayAgain", onExit: "onExit" }, ngImport: i0, template: "<div>\n  <a\n    class=\"btn btn-link text-uppercase\"\n    href=\"javascript:null\"\n    (click)=\"onPlayAgain.emit()\"\n  >\n    <i class=\"far fa-backward\"></i>\n    <span translate>Play Again</span>\n  </a>\n  <ng-container *ngIf=\"isExitVisible\">\n    &nbsp;&nbsp;\n    <a\n      class=\"btn btn-link text-uppercase\"\n      href=\"javascript:void(0)\"\n      (click)=\"onExit.emit()\"\n    >\n      <i class=\"far fa-eject\"></i>\n      <span translate>Finish</span>\n    </a>\n  </ng-container>\n</div>\n", styles: [":host{display:block;width:100%;height:100%;background:#000000b3;text-align:center;display:flex;justify-content:center;align-items:center}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$1.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: OutroComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-outro', template: "<div>\n  <a\n    class=\"btn btn-link text-uppercase\"\n    href=\"javascript:null\"\n    (click)=\"onPlayAgain.emit()\"\n  >\n    <i class=\"far fa-backward\"></i>\n    <span translate>Play Again</span>\n  </a>\n  <ng-container *ngIf=\"isExitVisible\">\n    &nbsp;&nbsp;\n    <a\n      class=\"btn btn-link text-uppercase\"\n      href=\"javascript:void(0)\"\n      (click)=\"onExit.emit()\"\n    >\n      <i class=\"far fa-eject\"></i>\n      <span translate>Finish</span>\n    </a>\n  </ng-container>\n</div>\n", styles: [":host{display:block;width:100%;height:100%;background:#000000b3;text-align:center;display:flex;justify-content:center;align-items:center}\n"] }]
        }], propDecorators: { onPlayAgain: [{
                type: Output
            }], onExit: [{
                type: Output
            }], isExitVisible: [{
                type: Input
            }] } });

class EvoSlidePlayerComponent {
    get fullScreenClass() {
        return this.isFullScreen$.getValue();
    }
    constructor(changeDetector, ngZone) {
        this.changeDetector = changeDetector;
        this.ngZone = ngZone;
        this.keyboardEnabled = false;
        this.toolbarDark = true;
        this.autoRewindEnabled = false;
        this.autoPlayEnabled = true;
        this.isLiveEnabled = true;
        this.isLiveBroadcastEnabled = false;
        this.embedded = false;
        this.nonNativeFullScreenEnabled = true;
        this.onSlideIndexChanged = new EventEmitter();
        this.onEnterFullScreen = new EventEmitter();
        this.onExitFullScreen = new EventEmitter();
        this.onPusherEvent = new EventEmitter();
        this.slides$ = new BehaviorSubject([]);
        this.currentIndex$ = new BehaviorSubject(0);
        this.currentStep$ = new BehaviorSubject(0);
        this.hasReachedEnd$ = new BehaviorSubject(false);
        this.isToolbarVisible$ = new BehaviorSubject(false);
        this.isFullScreen$ = new BehaviorSubject(false);
        this.isNativeFullScreenAvailable = screenfull.isEnabled;
        this.LiveMode = LiveMode;
        this.onFullScreenChange = (target) => {
            if (target === this.mainContainer.nativeElement ||
                (screenfull && screenfull.element === this.mainContainer.nativeElement)) {
                this.isFullScreen$.next(!this.isFullScreen$.getValue());
            }
            else if (!target || (screenfull && !screenfull.element)) {
                this.isFullScreen$.next(false);
            }
            if (this.isFullScreen$.getValue()) {
                this.viewer.activate();
                this.onEnterFullScreen.emit();
            }
            else {
                this.hideToolbar();
                this.viewer.deactivate();
                this.stopAutoPlay();
                this.onExitFullScreen.emit();
            }
            if (this.autoRewindEnabled) {
                this.rewind();
            }
        };
        this.totalSlides$ = this.slides$.pipe(map(slides => slides.length));
        this.isNextEnabled$ = combineLatest(this.hasReachedEnd$, this.totalSlides$).pipe(map(([hasReachedEnd, totalSlides]) => !hasReachedEnd && !!totalSlides));
        this.isBackEnabled$ = combineLatest(this.hasReachedEnd$, this.currentIndex$, this.currentStep$).pipe(map(([hasReachedEnd, currentIndex, currentStep]) => hasReachedEnd || currentIndex > 0 || currentStep > 0));
        this.currentSlideSteps$ = this.currentIndex$.pipe(map(slideIndex => this.getSlideSteps(slideIndex)));
        this.arePlayableElementsActive$ = combineLatest(this.hasReachedEnd$, this.slides$, this.currentIndex$, this.currentStep$).pipe(map(([hasReachedEnd, slides, currentIndex, currentStep]) => {
            if (!hasReachedEnd) {
                const currentSlide = slides[currentIndex];
                if (currentSlide) {
                    return currentSlide.elements
                        .filter(element => (getElementOptionValue(element)('animationStep') || 0) ===
                        currentStep)
                        .some(({ type }) => /^(audio|video)$/i.test(type));
                }
            }
            return false;
        }));
    }
    ngOnInit() {
        if (this.isNativeFullScreenAvailable) {
            document.addEventListener(screenfull.raw.fullscreenchange, this.onFullScreenChange);
        }
    }
    ngOnChanges(changes) {
        // Reload slides
        if (changes['presentation'] || changes['slides']) {
            const slides = [];
            if (this.slides) {
                slides.push(...this.slides);
            }
            else if (this.presentation && this.presentation.slides) {
                slides.push(...this.presentation.slides);
            }
            this.slides$.next(slides);
        }
    }
    ngOnDestroy() {
        if (this.isNativeFullScreenAvailable) {
            document.removeEventListener(screenfull.raw.fullscreenchange, this.onFullScreenChange);
        }
    }
    getSlideSteps(slideIndex) {
        let steps = 0;
        const slide = this.slides$.getValue()[slideIndex];
        if (slide) {
            steps = slide.elements.reduce((highestStep, element) => Math.max(highestStep, getElementOptionValue(element)('animationStep') || 0), 0);
        }
        return steps + 1;
    }
    // Navigation
    goToNextStep() {
        if (!this.hasReachedEnd$.getValue()) {
            combineLatest(this.currentIndex$, this.currentStep$, this.currentSlideSteps$, this.totalSlides$)
                .pipe(take(1))
                .subscribe(([currentIndex, currentStep, currentSlideSteps, totalSlides]) => {
                if (currentStep + 1 < currentSlideSteps) {
                    this.goTo({
                        slideIndex: currentIndex,
                        slideStep: currentStep + 1,
                    });
                }
                else if (currentIndex + 1 < totalSlides) {
                    this.goTo({ slideIndex: currentIndex + 1 });
                }
                else {
                    this.goToEnd();
                }
            });
        }
    }
    goTo({ slideIndex = 0, slideStep = 0, triggerEvents = true, triggerPusher = true, } = {}) {
        this.hasReachedEnd$.next(false);
        combineLatest(this.totalSlides$, this.currentIndex$, this.currentStep$)
            .pipe(take(1))
            .subscribe(([totalSlides, currentIndex, currentStep]) => {
            if (0 <= slideIndex &&
                slideIndex < totalSlides &&
                (currentIndex !== slideIndex || currentStep !== slideStep)) {
                this.currentIndex$.next(slideIndex);
                this.currentStep$.next(slideStep);
                if (triggerEvents) {
                    this.onSlideIndexChanged.emit({ slideIndex, slideStep });
                }
                if (triggerPusher && this.liveButton) {
                    this.liveButton.notifySlideChange({ slideIndex, slideStep });
                }
                this.ngZone.run(() => this.changeDetector.detectChanges());
            }
        });
    }
    goToEnd() {
        this.hasReachedEnd$.next(true);
    }
    next() {
        this.stopAutoPlay();
        if (this.hasReachedEnd$.getValue()) {
            if (this.isFullScreen$.getValue()) {
                this.toggleFullScreen();
            }
            this.rewind();
        }
        else {
            this.goToNextStep();
        }
    }
    back() {
        this.stopAutoPlay();
        combineLatest(this.currentIndex$, this.currentStep$, this.hasReachedEnd$)
            .pipe(take(1))
            .subscribe(([currentIndex, currentStep, hasReachedEnd]) => {
            if (hasReachedEnd) {
                this.goTo({ slideIndex: currentIndex, slideStep: currentStep });
            }
            else if (currentStep > 0) {
                this.goTo({ slideIndex: currentIndex, slideStep: currentStep - 1 });
            }
            else if (currentIndex > 0) {
                const previousSlideIndex = currentIndex - 1;
                const previousSlideSteps = this.getSlideSteps(previousSlideIndex);
                this.goTo({
                    slideIndex: previousSlideIndex,
                    slideStep: previousSlideSteps - 1,
                });
            }
        });
    }
    rewind() {
        this.stopAutoPlay();
        this.goTo({ slideIndex: 0 });
    }
    // Keyboard
    onKeyDown(event) {
        const isFullScreen = this.isFullScreen$.getValue();
        // Ignore if not fullscreen and not forced-enabled
        if (!isFullScreen && !this.keyboardEnabled)
            return;
        const targetIsInput = $(event.target).is('input, textarea, [contenteditable]');
        if (targetIsInput)
            return;
        // Hide the toolbar, because we're using the keyboard :)
        this.hideToolbar();
        switch (event.code) {
            case 'Space':
            case 'PageDown':
            case 'ArrowRight':
                this.next();
                event.preventDefault();
                break;
            case 'ArrowDown':
            case 'Enter':
                {
                    if (isFullScreen) {
                        this.next();
                        event.preventDefault();
                    }
                }
                break;
            case 'PageUp':
            case 'ArrowLeft':
                this.back();
                event.preventDefault();
                break;
            case 'ArrowUp':
                {
                    if (isFullScreen) {
                        this.back();
                        event.preventDefault();
                    }
                }
                break;
            case 'Escape':
                {
                    if (!this.isNativeFullScreenAvailable && isFullScreen) {
                        this.toggleFullScreen();
                        event.preventDefault();
                    }
                }
                break;
        }
    }
    // Toolbar
    toggleToolbar(event) {
        if (!this.isFullScreen$.getValue()) {
            return;
        }
        if (event) {
            // Ignore when the event target is a link or input field
            const targetIsInput = $(event.target).is('a, input, textarea, [contenteditable]');
            if (targetIsInput)
                return;
        }
        this.isToolbarVisible$.next(!this.isToolbarVisible$.getValue());
    }
    hideToolbar() {
        this.isToolbarVisible$.next(false);
    }
    // Auto-player
    onAutoPlayStart() {
        if (this.hasReachedEnd$.getValue()) {
            this.rewind();
        }
        // Go to full-screen
        if (!this.isFullScreen$.getValue() && this.isNativeFullScreenAvailable) {
            this.toggleFullScreen();
        }
        // Start videos, etc
        this.viewer.activate();
    }
    onElementActivationEnded() {
        if (this.autoPlayButton) {
            this.autoPlayButton.resume();
        }
    }
    stopAutoPlay() {
        if (this.autoPlayButton) {
            this.autoPlayButton.stop();
        }
    }
    // Full screen
    exitFullScreen() {
        if (this.isFullScreen$.getValue()) {
            this.toggleFullScreen();
        }
    }
    toggleFullScreen() {
        if (this.isNativeFullScreenAvailable) {
            screenfull.toggle(this.mainContainer.nativeElement);
        }
        else {
            this.onFullScreenChange(this.mainContainer.nativeElement);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EvoSlidePlayerComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.11", type: EvoSlidePlayerComponent, selector: "sb-evo-slide-player", inputs: { presentation: "presentation", slides: "slides", formula: "formula", palette: "palette", textFont: "textFont", headingFont: "headingFont", logoLightBg: "logoLightBg", logoDarkBg: "logoDarkBg", keyboardEnabled: "keyboardEnabled", toolbarDark: "toolbarDark", autoRewindEnabled: "autoRewindEnabled", autoPlayEnabled: "autoPlayEnabled", isLiveEnabled: "isLiveEnabled", isLiveBroadcastEnabled: "isLiveBroadcastEnabled", embedded: "embedded", nonNativeFullScreenEnabled: "nonNativeFullScreenEnabled" }, outputs: { onSlideIndexChanged: "onSlideIndexChanged", onEnterFullScreen: "onEnterFullScreen", onExitFullScreen: "onExitFullScreen", onPusherEvent: "onPusherEvent" }, host: { properties: { "class.full-screen": "this.fullScreenClass" } }, viewQueries: [{ propertyName: "mainContainer", first: true, predicate: ["mainContainer"], descendants: true }, { propertyName: "viewer", first: true, predicate: EvoSlideViewerComponent, descendants: true }, { propertyName: "liveButton", first: true, predicate: LiveButtonComponent, descendants: true }, { propertyName: "autoPlayButton", first: true, predicate: AutoPlayButtonComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  #mainContainer\n  class=\"player__wrapper\"\n  [class.player__wrapper--embedded]=\"embedded && !(isFullScreen$ | async)\"\n  (window:keydown)=\"onKeyDown($event)\"\n  (swipeleft)=\"next()\"\n  (swiperight)=\"back()\"\n  (swipeup)=\"exitFullScreen()\"\n  (swipedown)=\"exitFullScreen()\"\n  (pinchin)=\"exitFullScreen()\"\n  (pinchout)=\"exitFullScreen()\"\n>\n  <div class=\"viewer__wrapper\">\n    <sb-evo-slide-viewer\n      [slides]=\"slides$ | async\"\n      [formula]=\"formula || presentation?.formula\"\n      [palette]=\"palette || presentation?.palette\"\n      [textFont]=\"textFont || presentation?.textFont\"\n      [headingFont]=\"headingFont || presentation?.headingFont\"\n      [logoLightBg]=\"logoLightBg || presentation?.logoLightBg\"\n      [logoDarkBg]=\"logoDarkBg || presentation?.logoDarkBg\"\n      [currentIndex]=\"currentIndex$ | async\"\n      [currentStep]=\"currentStep$ | async\"\n      [isFullScreen]=\"embedded || (isFullScreen$ | async)\"\n      [lazyLoad]=\"false\"\n      (onElementActivationEnded)=\"onElementActivationEnded()\"\n      (click)=\"toggleToolbar($event)\"\n    ></sb-evo-slide-viewer>\n    <sb-evo-outro\n      [class.in]=\"hasReachedEnd$ | async\"\n      (onPlayAgain)=\"rewind()\"\n      [isExitVisible]=\"isFullScreen$ | async\"\n      (onExit)=\"next()\"\n    ></sb-evo-outro>\n  </div>\n\n  <div\n    class=\"alert-bar__wrapper\"\n    [style.visibility]=\"(isFullScreen$ | async) ? 'visible' : 'hidden'\"\n  >\n    <ng-content select=\"[sb-player-alert-bar]\"></ng-content>\n  </div>\n\n  <div\n    class=\"controls\"\n    [class.controls--visible]=\"\n      (isFullScreen$ | async) && (isToolbarVisible$ | async)\n    \"\n  >\n    <sb-evo-slide-player-progress-bar\n      [colorSet]=\"((palette || presentation?.palette)?.colorSets)[0]\"\n      [hasReachedEnd]=\"hasReachedEnd$ | async\"\n      [totalSlides]=\"totalSlides$ | async\"\n      [currentIndex]=\"currentIndex$ | async\"\n      [currentStep]=\"currentStep$ | async\"\n      [currentSlideSteps]=\"currentSlideSteps$ | async\"\n      (onGoToSlide)=\"goTo($event)\"\n    ></sb-evo-slide-player-progress-bar>\n\n    <sb-evo-slide-player-toolbar\n      class=\"d-block\"\n      [class.bg-dark]=\"toolbarDark || (isFullScreen$ | async)\"\n      [isBackEnabled]=\"isBackEnabled$ | async\"\n      [isNextEnabled]=\"isNextEnabled$ | async\"\n      [isFullScreenVisible]=\"\n        nonNativeFullScreenEnabled || isNativeFullScreenAvailable\n      \"\n      (onBack)=\"back()\"\n      (onNext)=\"next()\"\n      (onFullScreen)=\"toggleFullScreen()\"\n    >\n      <sb-evo-live-button\n        *ngIf=\"isLiveEnabled\"\n        [presentationId]=\"presentation?.id\"\n        [isBroadcastEnabled]=\"isLiveBroadcastEnabled\"\n        [initialMode]=\"\n          isLiveBroadcastEnabled ? LiveMode.Broadcast : LiveMode.Sync\n        \"\n        sb-evo-slide-player-toolbar-left\n        (onGoToSlide)=\"\n          goTo({\n            slideIndex: $event.slideIndex,\n            slideStep: $event.slideStep,\n            triggerPusher: false,\n          })\n        \"\n        (onPusherEvent)=\"onPusherEvent.emit($event)\"\n      ></sb-evo-live-button>\n\n      <sb-evo-auto-play-button\n        *ngIf=\"autoPlayEnabled\"\n        sb-evo-slide-player-toolbar-right\n        [arePlayableElementsActive]=\"arePlayableElementsActive$ | async\"\n        [hasReachedEnd]=\"hasReachedEnd$ | async\"\n        (onStart)=\"onAutoPlayStart()\"\n        (onNextStep)=\"goToNextStep()\"\n      ></sb-evo-auto-play-button>\n    </sb-evo-slide-player-toolbar>\n  </div>\n</div>\n", styles: [":host{display:block}.player__wrapper{display:block;position:relative;z-index:0;width:100%;margin:0;padding:0}.player__wrapper--embedded{position:absolute;left:0;top:0;height:100%;padding-bottom:50px}.player__wrapper--embedded .controls{position:absolute;left:0;bottom:0;width:100%}.viewer__wrapper{position:relative;width:100%;height:100%}.alert-bar__wrapper{position:absolute;left:0;top:0;width:100%}sb-evo-outro{position:absolute;z-index:2;top:0;left:0;transition:opacity .3s;opacity:0;pointer-events:none}sb-evo-outro.in{opacity:1;pointer-events:auto}:host.full-screen{position:static!important}:host.full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host.full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host.full-screen .controls:hover,:host.full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-webkit-full-screen{position:static!important}:host:-webkit-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-webkit-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-webkit-full-screen .controls:hover,:host:-webkit-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-moz-full-screen{position:static!important}:host:-moz-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-moz-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-moz-full-screen .controls:hover,:host:-moz-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-ms-full-screen{position:static!important}:host:-ms-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-ms-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-ms-full-screen .controls:hover,:host:-ms-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-o-full-screen{position:static!important}:host:-o-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-o-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-o-full-screen .controls:hover,:host:-o-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:full-screen{position:static!important}:host:full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:full-screen .controls:hover,:host:full-screen .controls.controls--visible{transform:translateY(-100%)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: EvoSlideViewerComponent, selector: "sb-evo-slide-viewer", inputs: ["slides", "slide", "formula", "palette", "textFont", "headingFont", "logoLightBg", "logoDarkBg", "currentIndex", "currentStep", "colorSetOffset", "isFullScreen", "lazyLoad"], outputs: ["onElementActivationEnded"] }, { kind: "component", type: SlidePlayerProgressBarComponent, selector: "sb-evo-slide-player-progress-bar", inputs: ["totalSlides", "currentIndex", "currentStep", "currentSlideSteps", "hasReachedEnd", "colorSet"], outputs: ["onGoToSlide"] }, { kind: "component", type: SlidePlayerToolbarComponent, selector: "sb-evo-slide-player-toolbar", inputs: ["isBackEnabled", "isNextEnabled", "isFullScreenVisible"], outputs: ["onBack", "onNext", "onFullScreen"] }, { kind: "component", type: LiveButtonComponent, selector: "sb-evo-live-button", inputs: ["presentationId", "isBroadcastEnabled", "initialMode"], outputs: ["onGoToSlide", "onPusherEvent"] }, { kind: "component", type: AutoPlayButtonComponent, selector: "sb-evo-auto-play-button", inputs: ["arePlayableElementsActive", "hasReachedEnd"], outputs: ["onStart", "onNextStep"] }, { kind: "component", type: OutroComponent, selector: "sb-evo-outro", inputs: ["isExitVisible"], outputs: ["onPlayAgain", "onExit"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: EvoSlidePlayerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-evo-slide-player', template: "<div\n  #mainContainer\n  class=\"player__wrapper\"\n  [class.player__wrapper--embedded]=\"embedded && !(isFullScreen$ | async)\"\n  (window:keydown)=\"onKeyDown($event)\"\n  (swipeleft)=\"next()\"\n  (swiperight)=\"back()\"\n  (swipeup)=\"exitFullScreen()\"\n  (swipedown)=\"exitFullScreen()\"\n  (pinchin)=\"exitFullScreen()\"\n  (pinchout)=\"exitFullScreen()\"\n>\n  <div class=\"viewer__wrapper\">\n    <sb-evo-slide-viewer\n      [slides]=\"slides$ | async\"\n      [formula]=\"formula || presentation?.formula\"\n      [palette]=\"palette || presentation?.palette\"\n      [textFont]=\"textFont || presentation?.textFont\"\n      [headingFont]=\"headingFont || presentation?.headingFont\"\n      [logoLightBg]=\"logoLightBg || presentation?.logoLightBg\"\n      [logoDarkBg]=\"logoDarkBg || presentation?.logoDarkBg\"\n      [currentIndex]=\"currentIndex$ | async\"\n      [currentStep]=\"currentStep$ | async\"\n      [isFullScreen]=\"embedded || (isFullScreen$ | async)\"\n      [lazyLoad]=\"false\"\n      (onElementActivationEnded)=\"onElementActivationEnded()\"\n      (click)=\"toggleToolbar($event)\"\n    ></sb-evo-slide-viewer>\n    <sb-evo-outro\n      [class.in]=\"hasReachedEnd$ | async\"\n      (onPlayAgain)=\"rewind()\"\n      [isExitVisible]=\"isFullScreen$ | async\"\n      (onExit)=\"next()\"\n    ></sb-evo-outro>\n  </div>\n\n  <div\n    class=\"alert-bar__wrapper\"\n    [style.visibility]=\"(isFullScreen$ | async) ? 'visible' : 'hidden'\"\n  >\n    <ng-content select=\"[sb-player-alert-bar]\"></ng-content>\n  </div>\n\n  <div\n    class=\"controls\"\n    [class.controls--visible]=\"\n      (isFullScreen$ | async) && (isToolbarVisible$ | async)\n    \"\n  >\n    <sb-evo-slide-player-progress-bar\n      [colorSet]=\"((palette || presentation?.palette)?.colorSets)[0]\"\n      [hasReachedEnd]=\"hasReachedEnd$ | async\"\n      [totalSlides]=\"totalSlides$ | async\"\n      [currentIndex]=\"currentIndex$ | async\"\n      [currentStep]=\"currentStep$ | async\"\n      [currentSlideSteps]=\"currentSlideSteps$ | async\"\n      (onGoToSlide)=\"goTo($event)\"\n    ></sb-evo-slide-player-progress-bar>\n\n    <sb-evo-slide-player-toolbar\n      class=\"d-block\"\n      [class.bg-dark]=\"toolbarDark || (isFullScreen$ | async)\"\n      [isBackEnabled]=\"isBackEnabled$ | async\"\n      [isNextEnabled]=\"isNextEnabled$ | async\"\n      [isFullScreenVisible]=\"\n        nonNativeFullScreenEnabled || isNativeFullScreenAvailable\n      \"\n      (onBack)=\"back()\"\n      (onNext)=\"next()\"\n      (onFullScreen)=\"toggleFullScreen()\"\n    >\n      <sb-evo-live-button\n        *ngIf=\"isLiveEnabled\"\n        [presentationId]=\"presentation?.id\"\n        [isBroadcastEnabled]=\"isLiveBroadcastEnabled\"\n        [initialMode]=\"\n          isLiveBroadcastEnabled ? LiveMode.Broadcast : LiveMode.Sync\n        \"\n        sb-evo-slide-player-toolbar-left\n        (onGoToSlide)=\"\n          goTo({\n            slideIndex: $event.slideIndex,\n            slideStep: $event.slideStep,\n            triggerPusher: false,\n          })\n        \"\n        (onPusherEvent)=\"onPusherEvent.emit($event)\"\n      ></sb-evo-live-button>\n\n      <sb-evo-auto-play-button\n        *ngIf=\"autoPlayEnabled\"\n        sb-evo-slide-player-toolbar-right\n        [arePlayableElementsActive]=\"arePlayableElementsActive$ | async\"\n        [hasReachedEnd]=\"hasReachedEnd$ | async\"\n        (onStart)=\"onAutoPlayStart()\"\n        (onNextStep)=\"goToNextStep()\"\n      ></sb-evo-auto-play-button>\n    </sb-evo-slide-player-toolbar>\n  </div>\n</div>\n", styles: [":host{display:block}.player__wrapper{display:block;position:relative;z-index:0;width:100%;margin:0;padding:0}.player__wrapper--embedded{position:absolute;left:0;top:0;height:100%;padding-bottom:50px}.player__wrapper--embedded .controls{position:absolute;left:0;bottom:0;width:100%}.viewer__wrapper{position:relative;width:100%;height:100%}.alert-bar__wrapper{position:absolute;left:0;top:0;width:100%}sb-evo-outro{position:absolute;z-index:2;top:0;left:0;transition:opacity .3s;opacity:0;pointer-events:none}sb-evo-outro.in{opacity:1;pointer-events:auto}:host.full-screen{position:static!important}:host.full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host.full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host.full-screen .controls:hover,:host.full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-webkit-full-screen{position:static!important}:host:-webkit-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-webkit-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-webkit-full-screen .controls:hover,:host:-webkit-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-moz-full-screen{position:static!important}:host:-moz-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-moz-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-moz-full-screen .controls:hover,:host:-moz-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-ms-full-screen{position:static!important}:host:-ms-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-ms-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-ms-full-screen .controls:hover,:host:-ms-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:-o-full-screen{position:static!important}:host:-o-full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:-o-full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:-o-full-screen .controls:hover,:host:-o-full-screen .controls.controls--visible{transform:translateY(-100%)}:host:full-screen{position:static!important}:host:full-screen .player__wrapper{position:fixed!important;top:0!important;left:0!important;width:100%!important;height:100%;max-width:100%!important;z-index:9999!important;display:block!important;padding:0!important}:host:full-screen .controls{position:absolute;top:100%;left:0;width:100%;z-index:200;display:block!important;padding-top:100px;transition:transform .3s;transform:translateY(-105px)}:host:full-screen .controls:hover,:host:full-screen .controls.controls--visible{transform:translateY(-100%)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }], propDecorators: { presentation: [{
                type: Input
            }], slides: [{
                type: Input
            }], formula: [{
                type: Input
            }], palette: [{
                type: Input
            }], textFont: [{
                type: Input
            }], headingFont: [{
                type: Input
            }], logoLightBg: [{
                type: Input
            }], logoDarkBg: [{
                type: Input
            }], keyboardEnabled: [{
                type: Input
            }], toolbarDark: [{
                type: Input
            }], autoRewindEnabled: [{
                type: Input
            }], autoPlayEnabled: [{
                type: Input
            }], isLiveEnabled: [{
                type: Input
            }], isLiveBroadcastEnabled: [{
                type: Input
            }], embedded: [{
                type: Input
            }], nonNativeFullScreenEnabled: [{
                type: Input
            }], onSlideIndexChanged: [{
                type: Output
            }], onEnterFullScreen: [{
                type: Output
            }], onExitFullScreen: [{
                type: Output
            }], onPusherEvent: [{
                type: Output
            }], mainContainer: [{
                type: ViewChild,
                args: ['mainContainer', { static: false }]
            }], viewer: [{
                type: ViewChild,
                args: [EvoSlideViewerComponent, { static: false }]
            }], liveButton: [{
                type: ViewChild,
                args: [LiveButtonComponent, { static: false }]
            }], autoPlayButton: [{
                type: ViewChild,
                args: [AutoPlayButtonComponent, { static: false }]
            }], fullScreenClass: [{
                type: HostBinding,
                args: ['class.full-screen']
            }] } });

const EXPORTED_DIRECTIVES = [EvoSlidePlayerComponent, EvoSlideViewerComponent];
const INNER_DIRECTIVES = [
    SlidePlayerProgressBarComponent,
    SlidePlayerToolbarComponent,
    LiveButtonComponent,
    AutoPlayButtonComponent,
    OutroComponent,
    SlideComponent,
    SlideElementHostComponent,
];
class RenderEvoModule {
    static forRoot(config) {
        return {
            ngModule: RenderEvoModule,
            providers: [
                {
                    provide: RENDER_EVO_CONFIG,
                    useFactory() {
                        return config || DEFAULT_CONFIG;
                    },
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderEvoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.11", ngImport: i0, type: RenderEvoModule, declarations: [EvoSlidePlayerComponent, EvoSlideViewerComponent, SlidePlayerProgressBarComponent,
            SlidePlayerToolbarComponent,
            LiveButtonComponent,
            AutoPlayButtonComponent,
            OutroComponent,
            SlideComponent,
            SlideElementHostComponent], imports: [CommonModule,
            RenderCommonModule,
            NounProjectIconModule,
            TextModule,
            ImageModule,
            PusherModule,
            TranslateModule], exports: [EvoSlidePlayerComponent, EvoSlideViewerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderEvoModule, imports: [CommonModule,
            RenderCommonModule,
            NounProjectIconModule,
            TextModule,
            ImageModule,
            PusherModule,
            TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.11", ngImport: i0, type: RenderEvoModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RenderCommonModule,
                        NounProjectIconModule,
                        TextModule,
                        ImageModule,
                        PusherModule,
                        TranslateModule,
                    ],
                    declarations: [...EXPORTED_DIRECTIVES, ...INNER_DIRECTIVES],
                    exports: EXPORTED_DIRECTIVES,
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EvoSlidePlayerComponent, EvoSlideViewerComponent, RenderEvoModule, RenderModule, SlidePlayerComponent, SlideViewerComponent };
